import React, { useEffect, useState, Fragment, Suspense, lazy, memo, Component } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, Shield, Sparkles, Gift, BookOpen, Repeat, Calendar, Video, Users, User, ArrowRight, Lock, MapPin, Sword, ChevronLeft, ChevronRight, Info, ExternalLink, Menu, X, Share2, MessageCircle, Copy, Check, Star, Award, Gem, Heart, Plus, Layers, Moon, ArrowDown, ArrowUp, Trophy, Zap, Coins, UserPlus, Map as MapIcon } from 'lucide-react';
import { KnightingCeremony } from '../components/KnightingCeremony';
import { RewardsTabContent } from '../components/treasury/RewardsTabContent';
import { CreateMislPanel } from '../components/treasury/CreateMislPanel';
import { FoundingMislStatus } from '../components/treasury/FoundingMislStatus';
import { downloadText, CLAUDE_DESIGN_MD, PACKET_OPENING_TSX } from '../components/treasury/claudeDesignDownloads';
// Lazy-loaded heavy components — they only ship JS to the client when actually opened.
// TreasuryAdminDashboard (~3.3k lines) + TreasuryOnboarding are by far the biggest
// modules on this page; loading them on demand cuts initial bundle dramatically.
const TreasuryOnboarding = lazy(() => import('../components/TreasuryOnboarding').then(m => ({
  default: m.TreasuryOnboarding
})));
const TreasuryAdminDashboard = lazy(() => import('../components/TreasuryAdminDashboard').then(m => ({
  default: m.TreasuryAdminDashboard
})));
// --- Types & Interfaces ---
// ---------- Visual Atoms ----------
const OrnateDivider = ({
  className = ''
}: {
  className?: string;
}) => <div className={`flex items-center justify-center gap-3 ${className}`} data-id="element-5041" data-mp="19id6eikbl900">
    <span className="h-px w-12 sm:w-20 bg-gradient-to-r from-transparent to-[#D4AF37]/60" data-id="element-5042" data-mp="lsraq535pbwr" />
    <svg width="28" height="14" viewBox="0 0 28 14" fill="none" data-id="element-5043" data-mp="gtxva74b7aty">
      <path d="M14 0 L17 4 L24 5 L19 9 L20 14 L14 11 L8 14 L9 9 L4 5 L11 4 Z" fill="#D4AF37" opacity="0.6" data-id="element-5044" data-mp="gto4wifcr1nq" />
    </svg>
    <span className="h-px w-12 sm:w-20 bg-gradient-to-l from-transparent to-[#D4AF37]/60" data-id="element-5045" data-mp="2luqtk1tb918f" />
  </div>;
const CornerOrnaments = () => <>
    <span className="absolute top-0 left-0 w-3 h-3 border-t border-l border-[#D4AF37]/50 pointer-events-none" data-id="element-5046" data-mp="2qmioeoji1t4d" />
    <span className="absolute top-0 right-0 w-3 h-3 border-t border-r border-[#D4AF37]/50 pointer-events-none" data-id="element-5047" data-mp="2vkabn5978r28" />
    <span className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-[#D4AF37]/50 pointer-events-none" data-id="element-5048" data-mp="1xzo53uyf4zsh" />
    <span className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-[#D4AF37]/50 pointer-events-none" data-id="element-5049" data-mp="27vist4hi1l24" />
  </>;
const SectionHeader = ({
  eyebrow,
  title,
  description
}: {
  eyebrow: string;
  title: React.ReactNode;
  description?: string;
}) => <div className="text-center mb-12" data-id="element-5050" data-mp="3ja4srz0jx1sa">
    <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-4 font-light" data-id="element-5051" data-mp="3nc9k2r3j1mbr">
      {eyebrow}
    </p>
    <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#FDFBF7] mb-6 leading-tight" data-id="element-5052" data-mp="1bpfoov4q6kly">
      {title}
    </h2>
    <OrnateDivider className="mb-6" data-id="element-5053" data-mp="17om1y530rbun" />
    {description && <p className="text-[#FDFBF7]/70 text-sm sm:text-base font-light max-w-2xl mx-auto leading-relaxed" data-id="element-5054" data-mp="12wcfx050yvxn">
        {description}
      </p>}
  </div>;
const RarityBadge = ({
  rarity
}: {
  rarity: string;
}) => {
  let colorClass = 'border-[#FDFBF7]/20 text-[#FDFBF7]/60 bg-[#FDFBF7]/5';
  if (rarity.includes('Founders')) colorClass = 'border-[#D4AF37]/70 text-[#D4AF37] bg-[#D4AF37]/10';else if (rarity.includes('Treasury Rare')) colorClass = 'border-[#D4AF37]/50 text-[#D4AF37]/90 bg-[#D4AF37]/5';else if (rarity.includes('Epic')) colorClass = 'border-[#8B0000]/70 text-[#8B0000] bg-[#8B0000]/10';else if (rarity.includes('Live Night') || rarity.includes('Treasury Night')) colorClass = 'border-violet-400/50 text-violet-300 bg-violet-400/10';
  return <span className={`text-[9px] tracking-[0.25em] uppercase font-medium px-2 py-1 border whitespace-nowrap ${colorClass}`} data-id="element-5055" data-mp="29atv3sg4yvrs">
      {rarity}
    </span>;
};
// ---------- Honour Glyph ----------
const HonourGlyph = ({
  type,
  className = ''
}: {
  type?: string;
  className?: string;
}) => {
  // Map honour type to an icon; default to Crown
  switch (type) {
    case 'order-of-rs':
      return <Star className={className} data-id="element-5056" data-mp="122048c69mrie" />;
    case 'gold-medallion':
      return <Award className={className} data-id="element-5057" data-mp="n9pvrcf3vfcz" />;
    case 'silver-medallion':
      return <Shield className={className} data-id="element-5058" data-mp="s3179quv4kb8" />;
    case 'sangat-seal':
      return <Users className={className} data-id="element-5059" data-mp="eaur44rpmdbs" />;
    case 'royal-sash':
      return <Crown className={className} data-id="element-5060" data-mp="e8i0z4bsswnn" />;
    default:
      return <Crown className={className} data-id="element-5061" data-mp="ibelx1ot94ur" />;
  }
};
// ---------- 1. Hero Section ----------
const HeroSection = ({
  onJoin
}: {
  onJoin: () => void;
}) => <section className="relative min-h-[90vh] flex flex-col items-center justify-center pt-20 pb-16 overflow-hidden" data-id="element-5062" data-mp="4ehzdcfq69jo">
    {/* Background Elements */}
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.15),_transparent_70%)] pointer-events-none" data-id="element-5063" data-mp="3luatlxtvcc4m" />
    <div className="absolute -left-24 -top-24 w-96 h-96 bg-[#8B0000]/10 blur-[120px] rounded-full pointer-events-none" data-id="element-5064" data-mp="3gxu695uhu1sl" />
    <div className="absolute -right-24 -bottom-24 w-96 h-96 bg-[#D4AF37]/10 blur-[120px] rounded-full pointer-events-none" data-id="element-5065" data-mp="3vqxe2lia9164" />

    <div className="container mx-auto px-4 relative z-10 flex flex-col items-center text-center" data-id="element-5066" data-mp="8o2cdtgq8ev6">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8
    }} className="mb-6" data-id="element-5067" data-mp="3q0uvcyy8nlt">
        <span className="text-[#D4AF37] text-[10px] sm:text-xs tracking-[0.4em] uppercase font-bold border border-[#D4AF37]/30 px-4 py-2 bg-[#D4AF37]/5" data-id="element-5068" data-mp="dfsp2634zepr">
          Enter the Lahore Court
        </span>
      </motion.div>

      <motion.h1 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      delay: 0.2
    }} className="font-serif text-5xl sm:text-7xl lg:text-8xl text-[#FDFBF7] mb-8 leading-[0.9] tracking-tight" data-id="element-5069" data-mp="nbnebk1v881i">
        TREASURES OF THE <span className="text-[#D4AF37]" data-id="element-5070" data-mp="19744f6cd1we8">SIKH EMPIRE</span>
      </motion.h1>

      <motion.p initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      delay: 0.4
    }} className="text-[#FDFBF7]/60 text-lg sm:text-xl font-light tracking-[0.15em] uppercase mb-12 max-w-2xl leading-relaxed" data-id="element-5071" data-mp="1d64munqxex0p">
        Recovering the lost stories of the Sikh Kingdoms.
      </motion.p>

      {/* Interactive Card Fan - Desktop Only */}
      <div className="hidden lg:block relative w-full max-w-4xl h-[300px] mb-12" data-id="element-5072" data-mp="3tv0eyapxba23">
        {[{
        img: 'https://cdn.magicpatterns.com/uploads/ekyaLxoHxw9b6NSM5wgnik/Hari_Singh_Nalwa.png',
        rotate: -15,
        x: -220,
        y: 20,
        z: 10
      }, {
        img: 'https://cdn.magicpatterns.com/uploads/nVFG4hPLbQSgSkuEzm99Kr/Jind_Kaur.png',
        rotate: -5,
        x: -110,
        y: 0,
        z: 20
      }, {
        img: 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png',
        rotate: 0,
        x: 0,
        y: -20,
        z: 30
      }, {
        img: 'https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png',
        rotate: 5,
        x: 110,
        y: 0,
        z: 20
      }, {
        img: 'https://cdn.magicpatterns.com/uploads/ekyaLxoHxw9b6NSM5wgnik/Hari_Singh_Nalwa.png',
        rotate: 15,
        x: 220,
        y: 20,
        z: 10
      }].map((card, i) => <motion.div key={i} initial={{
        opacity: 0,
        x: 0,
        rotate: 0
      }} animate={{
        opacity: 1,
        x: card.x,
        rotate: card.rotate
      }} transition={{
        duration: 1,
        delay: 0.6 + i * 0.1
      }} whileHover={{
        y: card.y - 20,
        scale: 1.05,
        zIndex: 40
      }} className="absolute left-1/2 top-10 sm:top-0 w-[140px] sm:w-[220px] aspect-[4/5] -ml-[70px] sm:-ml-[110px] shadow-2xl cursor-pointer" style={{
        zIndex: card.z
      }} data-id="element-5073" data-mp="34p7ifvmcx6ei" data-mpi={i}>
            <div className="w-full h-full border border-[#D4AF37]/40 bg-[#0B0F19] p-1" data-id="element-5074" data-mp="a6aqfbko90b6">
              <img src={card.img} alt="Court Card" className="w-full h-full object-cover" data-id="element-5075" data-mp="ex98ogp808h2" />
            </div>
          </motion.div>)}
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      delay: 1.2
    }} className="flex flex-col items-center gap-6 w-full" data-id="element-5076" data-mp="1upd1o50k25ye">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto" data-id="element-5077" data-mp="1goah5wp7l3pl">
          <button type="button" onClick={onJoin} className="w-full sm:w-auto px-10 py-5 bg-[#D4AF37] text-[#0B0F19] text-xs uppercase tracking-[0.3em] font-bold hover:bg-[#E5C158] transition-all shadow-[0_0_30px_rgba(212,175,55,0.3)] flex items-center justify-center gap-3 group" data-id="element-5078" data-mp="1bqsdoltyngoz">
            Join the Treasury
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-5079" data-mp="316rt2ko6a2d2" />
          </button>
          <button type="button" onClick={onJoin} className="w-full sm:w-auto px-10 py-5 border border-[#D4AF37]/40 hover:border-[#D4AF37] text-[#D4AF37] text-xs tracking-[0.3em] uppercase font-medium transition-colors text-center" data-id="element-5080" data-mp="3fjrlmt0i74xg">
            I Have an Invite
          </button>
        </div>

        <a href="#how-it-works" className="text-[#FDFBF7]/40 hover:text-[#D4AF37] text-[10px] tracking-[0.4em] uppercase transition-colors" data-id="element-5081" data-mp="31rticyth4397">
          See How It Works
        </a>
      </motion.div>
    </div>
  </section>;
// ---------- 2. What It Is Section ----------
const WhatItIsSection = () => {
  return <section className="py-24 bg-[#0B0F19] relative border-t border-[#D4AF37]/10 overflow-hidden" data-id="element-5082" data-mp="2nuoff7p23ki6">
      <div className="max-w-4xl mx-auto text-center px-4 mb-16" data-id="element-5083" data-mp="2iw3grcpgd8vu">
        <SectionHeader eyebrow="The Concept" title={<>
              A COLLECTIBLE <span className="text-[#D4AF37]" data-id="element-5085" data-mp="h35hay167i1x">PRESTIGE</span>{' '}
              EXPERIENCE
            </>} data-id="element-5084" data-mp="m0ympoung815" />
        <p className="text-[#FDFBF7]/80 text-lg sm:text-xl font-light leading-relaxed max-w-2xl mx-auto mb-6" data-id="element-5086" data-mp="ok29igbqtaez">
          Treasures of the Sikh Empire is not a game. It is a digital extension
          of the Lahore Court. By collecting beautifully crafted artifacts,
          trading with the community, and attending the live event, you signal
          your status and unlock real-world rewards.
        </p>
        <p className="text-[#D4AF37] text-base sm:text-lg font-light leading-relaxed max-w-2xl mx-auto" data-id="element-5087" data-mp="am3vxd58xi0j">
          Collectibles are not just collected. They help restore the digital
          memory map of Punjab. Contribute your cards to illuminate lost
          heritage and unlock exclusive access.
        </p>
      </div>

      {/* Centerpiece Collage */}
      <div className="relative w-full max-w-6xl mx-auto px-4" data-id="element-5088" data-mp="1r03w8p8079ob">
        <div className="grid grid-cols-12 grid-rows-6 gap-3 sm:gap-4 h-[400px] sm:h-[560px]" data-id="element-5089" data-mp="1v17aj7aekxnf">
          {/* Large left — Lahore Fort */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="col-span-7 row-span-4 relative border border-[#D4AF37]/30 bg-[#131B2B] p-1.5 overflow-hidden shadow-2xl group" data-id="element-5090" data-mp="3s0s152jzlyp8">
            <CornerOrnaments data-id="element-5091" data-mp="34ebqnt8c0jcf" />
            <img src="https://cdn.magicpatterns.com/uploads/7Y6dgrsg2zL5S5gqJcmvvX/Lahore_Fort_and_Hazuri_Bagh.png" alt="Lahore Fort and Hazuri Bagh" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" data-id="element-5092" data-mp="34axpgobemg6k" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19]/80 via-transparent to-transparent" data-id="element-5093" data-mp="2kpb1q51gg9l2" />
            <div className="absolute bottom-4 left-4 right-4" data-id="element-5094" data-mp="106qtqs6niif9">
              <p className="text-[#D4AF37] text-[9px] tracking-[0.3em] uppercase mb-1" data-id="element-5095" data-mp="1055wuvwv6ne8">
                Places of Power
              </p>
              <h4 className="font-serif text-xl sm:text-2xl text-[#FDFBF7] leading-tight" data-id="element-5096" data-mp="r73sc6dx9cm7">
                Lahore Fort & Hazuri Bagh
              </h4>
            </div>
          </motion.div>

          {/* Top right — Maharaja portrait card */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.15
        }} className="col-span-5 row-span-3 relative border border-[#D4AF37]/40 bg-[#131B2B] p-1.5 overflow-hidden shadow-2xl group" data-id="element-5097" data-mp="1jsvj6nm17vg2">
            <CornerOrnaments data-id="element-5098" data-mp="3en9htmd1glu" />
            <img src="https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png" alt="Maharaja Ranjit Singh" className="w-full h-full object-cover object-top opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" data-id="element-5099" data-mp="iq4tt3iyi7g5" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19]/80 via-transparent to-transparent" data-id="element-5100" data-mp="4zrmn3dw2wrv" />
            <div className="absolute bottom-4 left-4 right-4" data-id="element-5101" data-mp="11nxvy3lradch">
              <p className="text-[#D4AF37] text-[9px] tracking-[0.3em] uppercase mb-1" data-id="element-5102" data-mp="xnx85scropsr">
                Founders' Edition
              </p>
              <h4 className="font-serif text-lg text-[#FDFBF7] leading-tight" data-id="element-5103" data-mp="sn9jhdc0gl1p">
                Maharaja Ranjit Singh
              </h4>
            </div>
          </motion.div>

          {/* Mid right — Harmandir Sahib */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.3
        }} className="col-span-3 row-span-3 relative border border-[#D4AF37]/30 bg-[#131B2B] p-1.5 overflow-hidden shadow-2xl group" data-id="element-5104" data-mp="3dcp1bit94zf7">
            <img src="https://cdn.magicpatterns.com/uploads/st6W3BtxYpFsMW4u23CAxv/Harmandir_Sahib_Amritsar.png" alt="Harmandir Sahib" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" data-id="element-5105" data-mp="3d2r6b7dbluif" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19]/80 via-transparent to-transparent" data-id="element-5106" data-mp="3vzik51a3fhf3" />
            <div className="absolute bottom-3 left-3 right-3" data-id="element-5107" data-mp="1wmo8kq72z7fw">
              <p className="font-serif text-xs sm:text-sm text-[#FDFBF7] leading-tight" data-id="element-5108" data-mp="1wohhkc5623r2">
                Harmandir Sahib
              </p>
            </div>
          </motion.div>

          {/* Bottom-mid — Valley of Kashmir */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.45
        }} className="col-span-2 row-span-2 relative border border-[#D4AF37]/30 bg-[#131B2B] p-1.5 overflow-hidden shadow-2xl group" data-id="element-5109" data-mp="1olyp6iul6jl2">
            <img src="https://cdn.magicpatterns.com/uploads/ac4oQu8QYLHUDnoQhYPvFC/Valley_of_Kashmir.png" alt="Valley of Kashmir" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" data-id="element-5110" data-mp="2m97gwvl6kovi" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19]/70 to-transparent" data-id="element-5111" data-mp="2r93kitpyueaj" />
          </motion.div>

          {/* Bottom-left of right column — Jind Kaur */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.6
        }} className="col-span-3 row-span-2 relative border border-[#D4AF37]/40 bg-[#131B2B] p-1.5 overflow-hidden shadow-2xl group" data-id="element-5112" data-mp="3evyqvyleut74">
            <img src="https://cdn.magicpatterns.com/uploads/nVFG4hPLbQSgSkuEzm99Kr/Jind_Kaur.png" alt="Maharani Jind Kaur" className="w-full h-full object-cover object-top opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" data-id="element-5113" data-mp="2i5y7yg7q36xq" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19]/80 via-transparent to-transparent" data-id="element-5114" data-mp="2d74sii8vl5ux" />
            <div className="absolute bottom-3 left-3 right-3" data-id="element-5115" data-mp="2snjw8t7m8kzi">
              <p className="font-serif text-xs sm:text-sm text-[#FDFBF7] leading-tight" data-id="element-5116" data-mp="2npic04ygefmg">
                Maharani Jind Kaur
              </p>
            </div>
          </motion.div>

          {/* Bottom-right — Jamrud Fort */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.75
        }} className="col-span-2 row-span-2 relative border border-[#D4AF37]/30 bg-[#131B2B] p-1.5 overflow-hidden shadow-2xl group" data-id="element-5117" data-mp="2epmw5mtydwg3">
            <img src="https://cdn.magicpatterns.com/uploads/qPy5nBUtnmTQ5b2tqAUf9h/Jamrud_Fort.png" alt="Jamrud Fort" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" data-id="element-5118" data-mp="1qzixzuzjaowh" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19]/70 to-transparent" data-id="element-5119" data-mp="6gfat96gsngc" />
            <div className="absolute bottom-2 left-2 right-2" data-id="element-5120" data-mp="bg1rjsli4ltw">
              <p className="font-serif text-[10px] sm:text-xs text-[#FDFBF7] leading-tight" data-id="element-5121" data-mp="fhwq4pc1sl3z">
                Jamrud Fort
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
};
// ---------- 3. How It Works Section ----------
const HowItWorksSection = () => {
  const steps = [{
    num: '01',
    title: 'Sign Up',
    desc: 'Join the Lahore Court.',
    icon: Users
  }, {
    num: '02',
    title: 'Daily Packet',
    desc: 'Open one sealed envelope daily.',
    icon: Gift
  }, {
    num: '03',
    title: 'Collect',
    desc: 'Complete artifact sets.',
    icon: Crown
  }, {
    num: '04',
    title: 'Trade',
    desc: 'Exchange duplicates with others.',
    icon: Repeat
  }, {
    num: '05',
    title: 'Rank Up',
    desc: 'Increase your court influence.',
    icon: Trophy
  }, {
    num: '06',
    title: 'Live Unlocks',
    desc: 'Scan codes at the immersive event.',
    icon: Zap
  }, {
    num: '07',
    title: 'Treasury Nights',
    desc: 'Redeem progress for real rewards.',
    icon: Star
  }, {
    num: '08',
    title: 'Court Sessions',
    desc: 'Access private creator previews.',
    icon: Video
  }];
  return <section id="how-it-works" className="px-4 py-24 bg-[#131B2B] relative border-t border-[#D4AF37]/10" data-id="element-5122" data-mp="1xx4q44opjcqv">
      <div className="max-w-5xl mx-auto" data-id="element-5123" data-mp="22nutjcdkp5ix">
        <SectionHeader eyebrow="The Journey" title={<>
              HOW IT <span className="text-[#D4AF37]" data-id="element-5125" data-mp="1564k5o6tfywk">WORKS</span>
            </>} data-id="element-5124" data-mp="22owa1wdcyk81" />

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8" data-id="element-5126" data-mp="1a5h7ea1ln7zo">
          {steps.map(step => {
          const Icon = step.icon;
          return <div key={step.num} className="flex items-start gap-6 p-4 border-b border-[#D4AF37]/10 hover:bg-[#0B0F19] transition-colors group" data-id="element-5127" data-mp="xpzmdde3ty87">
                <span className="font-serif text-4xl text-[#D4AF37]/40 group-hover:text-[#D4AF37] transition-colors" data-id="element-5128" data-mp="omyhth4w0f6n">
                  {step.num}
                </span>
                <div data-id="element-5129" data-mp="eygjr7nzsm6p">
                  <h4 className="font-serif text-xl text-[#FDFBF7] mb-1 flex items-center gap-2" data-id="element-5130" data-mp="ex5h5b6a9kh6">
                    {step.title}
                    <Icon className="w-4 h-4 text-[#D4AF37]/60" data-id="element-5131" data-mp="9zdf3obxchz9" />
                  </h4>
                  <p className="text-[#FDFBF7]/60 text-sm font-light" data-id="element-5132" data-mp="51lmes3ggru0">
                    {step.desc}
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
};
// ---------- 4. Registration Flow Section ----------
const RegistrationFlowSection = () => <section id="signup" className="px-4 py-24 bg-[#0B0F19] relative border-t border-[#D4AF37]/10" data-id="element-5133" data-mp="3ij7cdi4bnlkn">
    <div className="max-w-7xl mx-auto text-center" data-id="element-5134" data-mp="3mgd75dg8efzc">
      <SectionHeader eyebrow="Sign-Up Journey" title={<>
            THE REGISTRATION <span className="text-[#D4AF37]" data-id="element-5136" data-mp="3dit4q16mudn5">FLOW</span>
          </>} description="Five simple steps from invitation to your first artifact." data-id="element-5135" data-mp="38k8bilpssu87" />

      <div className="flex flex-col lg:flex-row items-center lg:items-center justify-start lg:justify-start gap-4 mt-16 overflow-x-auto pb-8 snap-x scrollbar-hide px-4 lg:px-0" data-id="element-5137" data-mp="a1sxt3ogw90z">
        {/* Screen 01 */}
        <div className="w-full max-w-[260px] h-[460px] border border-[#D4AF37]/30 bg-[#131B2B] rounded-2xl p-6 flex flex-col relative shadow-2xl shrink-0 snap-center" data-id="element-5138" data-mp="apyo7f3m44c">
          <div className="text-center mb-8 mt-4" data-id="element-5139" data-mp="srsk5isxba0p">
            <p className="text-[#D4AF37] text-[8px] tracking-[0.3em] uppercase mb-2" data-id="element-5140" data-mp="sskhoz62zhtm">
              Treasures of the Sikh Empire
            </p>
            <h4 className="font-serif text-2xl text-[#FDFBF7] leading-tight" data-id="element-5141" data-mp="2dfv2awxeutji">
              Join the Lahore Court
            </h4>
          </div>
          <div className="mt-auto mb-auto w-full space-y-4" data-id="element-5142" data-mp="2m9gf4aq0m0qa">
            <div className="w-full border border-[#D4AF37]/20 bg-[#0B0F19] p-3 text-left" data-id="element-5143" data-mp="2i8wkpewttb7u">
              <span className="text-[#FDFBF7]/30 text-sm font-light" data-id="element-5144" data-mp="1ujbx2ipq9ps8">
                your.name@email.com
              </span>
            </div>
            <div className="w-full bg-[#D4AF37] py-3 text-[#0B0F19] text-[10px] tracking-[0.2em] uppercase font-medium text-center" data-id="element-5145" data-mp="1plaedyw7rsha">
              Request Invitation
            </div>
          </div>
          <p className="text-[#FDFBF7]/40 text-[10px] mt-auto" data-id="element-5146" data-mp="1yjvrheix2fao">
            Existing members → Sign In
          </p>
        </div>

        <ArrowDown className="lg:hidden w-6 h-6 text-[#D4AF37]/30 my-2 shrink-0" data-id="element-5147" data-mp="3excb1d7rfom0" />
        <ArrowRight className="hidden lg:block w-6 h-6 text-[#D4AF37]/30 mx-2 shrink-0" data-id="element-5148" data-mp="39zkgebfqdoe4" />

        {/* Screen 02 */}
        <div className="w-full max-w-[260px] h-[460px] border border-[#D4AF37]/30 bg-[#131B2B] rounded-2xl p-6 flex flex-col relative shadow-2xl shrink-0 snap-center" data-id="element-5149" data-mp="96z7rlpldxa0">
          <div className="text-center mb-8 mt-4" data-id="element-5150" data-mp="j29a6ajwk6ob">
            <h4 className="font-serif text-2xl text-[#FDFBF7] leading-tight" data-id="element-5151" data-mp="o25e69slny3v">
              A seal arrived in your inbox
            </h4>
          </div>
          <div className="mt-auto mb-auto w-full flex flex-col items-center" data-id="element-5152" data-mp="s2z3n7loxkyv">
            <div className="flex gap-2 mb-4" data-id="element-5153" data-mp="x21w7skhhypy">
              {[...Array(6)].map((_, i) => <div key={i} className="w-8 h-10 border border-[#D4AF37]/40 bg-[#0B0F19]" data-id="element-5154" data-mp="11ta6eqtn1o46" data-mpi={i} />)}
            </div>
            <p className="text-[#FDFBF7]/50 text-[10px] font-light text-center mb-8" data-id="element-5155" data-mp="1brpwcqgxirc3">
              Enter the 6-digit code from the Darbar
            </p>
            <div className="w-full border border-[#D4AF37]/40 py-3 text-[#D4AF37] text-[10px] tracking-[0.2em] uppercase font-medium text-center" data-id="element-5156" data-mp="1ftuofkrqbfwm">
              Verify
            </div>
          </div>
        </div>

        <ArrowDown className="lg:hidden w-6 h-6 text-[#D4AF37]/30 my-2 shrink-0" data-id="element-5157" data-mp="2wbv4c8gtgtn5" />
        <ArrowRight className="hidden lg:block w-6 h-6 text-[#D4AF37]/30 mx-2 shrink-0" data-id="element-5158" data-mp="2rb7cxlooease" />

        {/* Screen 03 */}
        <div className="w-full max-w-[260px] h-[460px] border border-[#D4AF37]/30 bg-[#131B2B] rounded-2xl p-6 flex flex-col relative shadow-2xl shrink-0 snap-center" data-id="element-5159" data-mp="2mdp4dweklxkx">
          <div className="text-center mb-8 mt-4" data-id="element-5160" data-mp="3f1kxkr4xl7cy">
            <h4 className="font-serif text-2xl text-[#FDFBF7] leading-tight" data-id="element-5161" data-mp="3a9jn9arbpqpk">
              Your court name
            </h4>
          </div>
          <div className="mt-auto mb-auto w-full space-y-8" data-id="element-5162" data-mp="359f93rdw182i">
            <div className="w-full border-b border-[#D4AF37]/40 bg-transparent pb-2 text-center" data-id="element-5163" data-mp="6kgfmj4mn07y">
              <span className="text-[#FDFBF7]/80 text-lg font-serif" data-id="element-5164" data-mp="1tq5ksl9a96x">
                @harnoor
              </span>
            </div>
            <div className="flex justify-center gap-4" data-id="element-5165" data-mp="1w2mzo23iw1kz">
              <div className="w-12 h-12 rounded-full border border-[#D4AF37] bg-[#D4AF37]/10 flex items-center justify-center" data-id="element-5166" data-mp="2035um50ppnre">
                <Crown className="w-5 h-5 text-[#D4AF37]" data-id="element-5167" data-mp="1mck9e9nsd1qc" />
              </div>
              <div className="w-12 h-12 rounded-full border border-[#D4AF37]/20 bg-[#0B0F19] flex items-center justify-center" data-id="element-5168" data-mp="1ralsgul7p11t">
                <Gem className="w-5 h-5 text-[#D4AF37]/40" data-id="element-5169" data-mp="1ccjmyeume5hx" />
              </div>
              <div className="w-12 h-12 rounded-full border border-[#D4AF37]/20 bg-[#0B0F19] flex items-center justify-center" data-id="element-5170" data-mp="1hd7cew37aea1">
                <Shield className="w-5 h-5 text-[#D4AF37]/40" data-id="element-5171" data-mp="13e7yojh87jsn" />
              </div>
            </div>
            <div className="w-full bg-[#D4AF37] py-3 text-[#0B0F19] text-[10px] tracking-[0.2em] uppercase font-medium text-center" data-id="element-5172" data-mp="18csnznuxwj2b">
              Enter the Court
            </div>
          </div>
        </div>

        <ArrowDown className="lg:hidden w-6 h-6 text-[#D4AF37]/30 my-2 shrink-0" data-id="element-5173" data-mp="32pik8c8s64cv" />
        <ArrowRight className="hidden lg:block w-6 h-6 text-[#D4AF37]/30 mx-2 shrink-0" data-id="element-5174" data-mp="3cv0ijv5lva8f" />

        {/* Screen 04 */}
        <div className="w-full max-w-[260px] h-[460px] border border-[#D4AF37]/30 bg-[#131B2B] rounded-2xl p-6 flex flex-col relative shadow-2xl shrink-0 snap-center" data-id="element-5175" data-mp="37wfl23hqn4no">
          <div className="mt-auto mb-auto w-full flex flex-col items-center text-center" data-id="element-5176" data-mp="3gvao56phjymr">
            <div className="relative w-32 h-auto mb-6" data-id="element-5177" data-mp="3vjxdm18tu6rp">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.4),_transparent_70%)] blur-xl" data-id="element-5178" data-mp="3vmjmq5bd8uc1" />
              <img src="https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png" alt="Envelope" className="relative w-full drop-shadow-xl" data-id="element-5179" data-mp="8g96qcnyn30q" />
            </div>
            <h4 className="font-serif text-xl text-[#FDFBF7] leading-tight mb-2" data-id="element-5180" data-mp="2a7m8wtyjmwt3">
              A welcome gift from the Darbar
            </h4>
            <p className="text-[#FDFBF7]/50 text-[10px] font-light mb-8" data-id="element-5181" data-mp="2uqgr5adte9rd">
              Your founder packet is ready
            </p>
            <div className="w-full bg-[#8B0000] py-3 text-[#FDFBF7] text-[10px] tracking-[0.2em] uppercase font-medium text-center" data-id="element-5182" data-mp="2zoi9ft3f251s">
              Open Now
            </div>
          </div>
        </div>

        <ArrowDown className="lg:hidden w-6 h-6 text-[#D4AF37]/30 my-2 shrink-0" data-id="element-5183" data-mp="38gsmdi5p4omq" />
        <ArrowRight className="hidden lg:block w-6 h-6 text-[#D4AF37]/30 mx-2 shrink-0" data-id="element-5184" data-mp="2bs4kpweafdzh" />

        {/* Screen 05 */}
        <div className="w-full max-w-[260px] h-[460px] border border-[#D4AF37]/30 bg-[#131B2B] rounded-2xl p-6 flex flex-col relative shadow-2xl shrink-0 snap-center" data-id="element-5185" data-mp="2grpfdumfd06e">
          <div className="mt-auto mb-auto w-full flex flex-col items-center text-center" data-id="element-5186" data-mp="2pr48l1tz7oul">
            <h4 className="font-serif text-2xl text-[#FDFBF7] leading-tight mb-6" data-id="element-5187" data-mp="223rv73ufv3">
              You are now Sevadar
            </h4>
            <div className="w-24 aspect-[4/5] border border-[#D4AF37] bg-[#0B0F19] p-1 shadow-[0_0_20px_rgba(212,175,55,0.3)] mb-6" data-id="element-5188" data-mp="503qcq3qoxbb">
              <img src="https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png" alt="Card" className="w-full h-full object-cover" data-id="element-5189" data-mp="f4afftoco38d" />
            </div>
            <p className="text-[#FDFBF7]/50 text-[10px] font-light mb-8" data-id="element-5190" data-mp="1dx2te0ik0cp">
              Your treasury begins.
            </p>
            <div className="w-full bg-[#D4AF37] py-3 text-[#0B0F19] text-[10px] tracking-[0.2em] uppercase font-medium text-center" data-id="element-5191" data-mp="3skzv68s38coi">
              Enter Dashboard
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>;
// ---------- 4.5 Member Dashboard Preview Section ----------
// Image previews mirror the canonical artifacts in the Collections section below.
type DashboardCollection = {
  name: string;
  sub: string;
  rarity: string;
  total: number;
  status: string;
  isFlagship?: boolean;
  sponsoredBy?: string;
  unlockedCards: {
    img: string;
    name: string;
    role?: string;
  }[];
};
const dashboardCollections: DashboardCollection[] = [{
  name: 'Lahore Court Collection',
  sub: 'The Founding Faces · Flagship',
  rarity: "Founders' Edition",
  total: 8,
  status: '5 To Unlock',
  isFlagship: true,
  sponsoredBy: 'Akal Steel',
  unlockedCards: [{
    img: 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png',
    name: 'Maharaja Ranjit Singh',
    role: 'Sher-e-Punjab'
  }, {
    img: 'https://cdn.magicpatterns.com/uploads/nVFG4hPLbQSgSkuEzm99Kr/Jind_Kaur.png',
    name: 'Maharani Jind Kaur',
    role: 'Regent of Punjab'
  }, {
    img: 'https://cdn.magicpatterns.com/uploads/ekyaLxoHxw9b6NSM5wgnik/Hari_Singh_Nalwa.png',
    name: 'Hari Singh Nalwa',
    role: 'Commander-in-Chief'
  }]
}, {
  name: "Founders' Seals",
  sub: 'Sovereign Marks',
  rarity: "Founders' Edition",
  total: 6,
  status: 'In Progress',
  unlockedCards: [{
    img: 'https://cdn.magicpatterns.com/uploads/ecLy6LTMHkK3K5wmXXnV8A/Royal_Seal_of_Maharaja_Ranjit_Singh.png',
    name: 'Royal Seal of Ranjit Singh',
    role: 'Sovereign Mohr'
  }]
}, {
  name: 'Court Treasures',
  sub: 'Royal Regalia',
  rarity: "Founders' Edition",
  total: 6,
  status: 'In Progress',
  unlockedCards: [{
    img: 'https://cdn.magicpatterns.com/uploads/jyrMYyNM9JskmMzEnBrLTw/Koh-i-noor_Armlet.png',
    name: 'Koh-i-Noor Armlet',
    role: 'Sovereign Jewel'
  }, {
    img: 'https://cdn.magicpatterns.com/uploads/itiARdFbZcdkmdrUEtYH65/Golden_Throne_of_Lahore.png',
    name: 'Golden Throne of Lahore',
    role: 'Seat of the Sarkar'
  }]
}, {
  name: 'Court Scenes',
  sub: 'Living Tableaux',
  rarity: 'Treasury Rare',
  total: 8,
  status: 'In Progress',
  unlockedCards: [{
    img: 'https://cdn.magicpatterns.com/uploads/i8yqzvFJ6TFd6dTb6YJFfy/Lahor_Darbar.png',
    name: 'The Lahore Darbar',
    role: 'The Full Court'
  }, {
    img: 'https://cdn.magicpatterns.com/uploads/ndFukn5EhjaGeCm7W2a9F8/Council_of_Empire.png',
    name: 'Council of Empire',
    role: 'War Council'
  }]
}, {
  name: 'Fracture & Memory',
  sub: 'The Empire After the Light',
  rarity: 'Treasury Rare',
  total: 8,
  status: 'In Progress',
  unlockedCards: [{
    img: 'https://cdn.magicpatterns.com/uploads/pUc7dhUDTQ8ibLS8igHkeA/Last_Light_of_Lahore.png',
    name: 'Last Light of Lahore',
    role: 'Memory Fragment'
  }]
}, {
  name: 'Sacred Authority',
  sub: 'Reverence Before Rule',
  rarity: "Founders' Edition",
  total: 6,
  status: 'Locked',
  unlockedCards: []
}, {
  name: 'Frontier Defenders',
  sub: 'Martial Prestige Objects',
  rarity: 'Epic',
  total: 5,
  status: 'Locked',
  unlockedCards: []
}, {
  name: 'Lahore Court Inner Life',
  sub: 'Intimate Spaces',
  rarity: 'Treasury Rare',
  total: 7,
  status: 'Locked',
  unlockedCards: []
}];
const MemberDashboardPreviewSection = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);
  const [tradeTab, setTradeTab] = useState<'duplicates' | 'market' | 'incoming'>('incoming');
  const [packetState, setPacketState] = useState<'sealed' | 'opening' | 'revealed' | 'added'>('sealed');
  // Per-event code-redeem state, keyed by `${eventType}-${eventIndex}`
  const [eventCodes, setEventCodes] = useState<Record<string, string>>({});
  const [redeemedEvents, setRedeemedEvents] = useState<Record<string, boolean>>({});
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const [isKnightingOpen, setIsKnightingOpen] = useState(false);
  const [knightedAvatar, setKnightedAvatar] = useState<string | null>(null);
  const [mislView, setMislView] = useState<'home' | 'create'>('home');
  useEffect(() => {
    const handleOpen = () => setIsKnightingOpen(true);
    window.addEventListener('open-knighting-ceremony', handleOpen);
    return () => window.removeEventListener('open-knighting-ceremony', handleOpen);
  }, []);
  const tabs = [{
    id: 'dashboard',
    label: 'Dashboard',
    icon: Crown
  }, {
    id: 'daily-packet',
    label: 'Daily Packet',
    icon: Gift,
    badge: 'dot'
  }, {
    id: 'collections',
    label: 'Collections',
    icon: BookOpen
  }, {
    id: 'trade-inbox',
    label: 'Trade Inbox',
    icon: Repeat,
    badge: '2'
  }, {
    id: 'rewards',
    label: 'Rewards',
    icon: Trophy
  }, {
    id: 'my-misl',
    label: 'My Clan',
    icon: Shield
  }, {
    id: 'restoration',
    label: 'Restore Punjab',
    icon: MapIcon
  }, {
    id: 'live-events',
    label: 'Live Events',
    icon: Calendar
  }, {
    id: 'referrals',
    label: 'Referrals',
    icon: Users
  }, {
    id: 'profile',
    label: 'Profile',
    icon: User
  }];
  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <>
            {/* Header — Welcome row */}
            <div className="flex items-center gap-4" data-id="element-5192" data-mp="11xlgywmkzxj1">
              {knightedAvatar ? <button type="button" onClick={() => setActiveTab('profile')} className="relative shrink-0 group" aria-label="View your knighted portrait" data-id="element-5193" data-mp="16wy3fg9jj2l3">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 border-2 border-[#D4AF37] overflow-hidden shadow-[0_0_20px_rgba(212,175,55,0.3)]" data-id="element-5194" data-mp="wzjwq8df5s5z">
                    <img src={knightedAvatar} alt="Your knighted portrait" className="w-full h-full object-cover" data-id="element-5195" data-mp="2rd2n8y918ahs" />
                  </div>
                  <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-[#D4AF37] border-2 border-[#0B0F19] flex items-center justify-center" data-id="element-5196" data-mp="3van7055bddd0">
                    <Shield className="w-2.5 h-2.5 text-[#0B0F19]" data-id="element-5197" data-mp="3rb615alz6e7b" />
                  </span>
                </button> : <button type="button" onClick={() => setIsKnightingOpen(true)} className="w-14 h-14 sm:w-16 sm:h-16 border-2 border-dashed border-[#D4AF37]/40 hover:border-[#D4AF37] bg-[#131B2B]/50 flex items-center justify-center shrink-0 transition-colors group" aria-label="Begin your knighting ceremony" data-id="element-5198" data-mp="mvcyae6x59w9">
                  <Shield className="w-5 h-5 text-[#D4AF37]/40 group-hover:text-[#D4AF37] transition-colors" data-id="element-5199" data-mp="16jca7sd7yn39" />
                </button>}
              <div className="flex flex-col gap-1 min-w-0" data-id="element-5200" data-mp="11qaqn722hzdc">
                <div className="flex items-center gap-3 text-[#D4AF37]/60 text-[10px] tracking-[0.3em] uppercase" data-id="element-5201" data-mp="194jzqygh6ifs">
                  <span data-id="element-5202" data-mp="1e352b4j79e7e">Court Dashboard</span>
                  <span className="text-[#D4AF37]/25" data-id="element-5203" data-mp="1j1puqhs7mtla">·</span>
                  <span data-id="element-5204" data-mp="1j0enu93gx5py">Day 47</span>
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl text-[#FDFBF7]" data-id="element-5205" data-mp="1n3jpyajmz5e2">
                  Welcome back, Harnoor
                </h3>
              </div>
            </div>

            {/* Virtual Knighting Ceremony CTA */}
            <div className="relative w-full border border-[#D4AF37] bg-gradient-to-r from-[#0B0F19] via-[#131B2B] to-[#0B0F19] p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6 overflow-hidden group" data-id="element-5206" data-mp="1wzwi33zj9b6p">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.15),_transparent_70%)] pointer-events-none" data-id="element-5207" data-mp="26td9y2g40g7q" />
              <div className="relative z-10 flex items-center gap-6" data-id="element-5208" data-mp="2fr787ixfpjxj">
                <div className="w-16 h-16 rounded-full border-2 border-[#D4AF37] bg-[#0B0F19] flex items-center justify-center shrink-0 shadow-[0_0_20px_rgba(212,175,55,0.3)]" data-id="element-5209" data-mp="312x8zn4os9l2">
                  <Shield className="w-8 h-8 text-[#D4AF37]" data-id="element-5210" data-mp="2w5x2xc7u76ty" />
                </div>
                <div data-id="element-5211" data-mp="3b0bqzr69d6fh">
                  <div className="flex items-center gap-2 mb-1" data-id="element-5212" data-mp="3ayquhw0dvdez">
                    <Sparkles className="w-3 h-3 text-[#D4AF37]" data-id="element-5213" data-mp="362jo6242rzwi" />
                    <span className="text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase font-bold" data-id="element-5214" data-mp="2ibmlzh230gyg">
                      Optional Rite · +2,500 XP
                    </span>
                  </div>
                  <h4 className="font-serif text-2xl text-[#FDFBF7] mb-1" data-id="element-5215" data-mp="2s2h88lfbvgjw">
                    Begin Your Knighting Ceremony
                  </h4>
                  <p className="text-[#FDFBF7]/60 text-sm" data-id="element-5216" data-mp="2n9fr06rnf4x5">
                    Take the oath and receive your official Founders' Edition
                    portrait.
                  </p>
                </div>
              </div>
              <button onClick={() => {
              // We will add state for this modal
              const event = new CustomEvent('open-knighting-ceremony');
              window.dispatchEvent(event);
            }} className="relative z-10 shrink-0 px-8 py-4 bg-[#D4AF37] text-[#0B0F19] font-bold uppercase tracking-widest text-sm hover:bg-[#E5C158] transition-colors shadow-xl" data-id="element-5217" data-mp="a52nn5dvp1el">
                Start Ceremony
              </button>
            </div>

            {/* Full-width Court Standing strip — proper dashboard KPI bar */}
            <div className="relative w-full border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/8 via-[#131B2B] to-[#0B0F19] mt-6" data-id="element-5218" data-mp="jum3s3a7ih5r">
              {/* Gold seam top */}
              <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/70 to-transparent" data-id="element-5219" data-mp="nyadosmb9ou6" />

              {/* Eyebrow strip */}
              <div className="px-4 sm:px-5 pt-2.5 pb-2 border-b border-[#D4AF37]/15 flex items-center justify-between" data-id="element-5220" data-mp="38nztb7uu9enx">
                <p className="text-[#D4AF37]/60 text-[8px] tracking-[0.35em] uppercase flex items-center gap-2" data-id="element-5221" data-mp="1hzhs5322r5te">
                  <Shield className="w-2.5 h-2.5" data-id="element-5222" data-mp="1mu5f2xhfjs5v" />
                  Court Standing
                </p>
                <p className="text-[#D4AF37]/40 text-[8px] tracking-[0.25em] uppercase hidden xs:block" data-id="element-5223" data-mp="1e00li6aww2mn">
                  Live · Updated every drop
                </p>
              </div>

              {/* KPI row */}
              <div className="flex flex-col lg:flex-row items-stretch" data-id="element-5224" data-mp="25rosedax7wy3">
                {/* Rank ladder — takes most space */}
                <div className="relative flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-4 flex-1 min-w-0" data-id="element-5225" data-mp="1rqm3ztwgeak0">
                  {/* Current */}
                  <div className="flex flex-col items-center gap-1.5 shrink-0 w-[70px] sm:w-[80px]" data-id="element-5226" data-mp="1wsbffanl8q8m">
                    <div className="relative w-10 h-10 sm:w-11 sm:h-11 border border-[#D4AF37] bg-[#0B0F19] flex items-center justify-center shadow-[0_0_20px_rgba(212,175,55,0.35)]" data-id="element-5227" data-mp="lbbrer89wxke">
                      <HonourGlyph type="gold-medallion" className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37]" data-id="element-5228" data-mp="dtx3qeiv4v0h" />
                      <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-[#D4AF37] rounded-full shadow-[0_0_8px_rgba(212,175,55,0.8)]" data-id="element-5229" data-mp="mrz18k3e15j0" />
                    </div>
                    <span className="font-serif text-[10px] sm:text-[11px] text-[#FDFBF7] leading-tight text-center" data-id="element-5230" data-mp="3qa0q860zkwwe">
                      Gold Medallion
                    </span>
                    <span className="text-[#D4AF37]/50 text-[7px] tracking-[0.25em] uppercase" data-id="element-5231" data-mp="3twgwilsbwrj">
                      You
                    </span>
                  </div>

                  {/* Progress track */}
                  <div className="flex-1 flex flex-col justify-center min-w-[50px]" data-id="element-5232" data-mp="37hoguzbx0vcr">
                    <div className="flex items-center justify-between text-[7px] sm:text-[8px] tracking-[0.25em] uppercase mb-1.5" data-id="element-5233" data-mp="32keq9gcsidnc">
                      <span className="text-[#FDFBF7]/40" data-id="element-5234" data-mp="16fyvu3426tn9">Rising</span>
                      <span className="text-[#D4AF37] font-mono" data-id="element-5235" data-mp="1bejn3d1c22zk">
                        4,150 / 5k
                      </span>
                    </div>
                    <div className="relative w-full h-1 bg-[#0B0F19] overflow-hidden" data-id="element-5236" data-mp="1g8nxk69t4t5e">
                      <div className="absolute inset-y-0 left-0 w-[83%] bg-gradient-to-r from-[#D4AF37] via-[#E5C158] to-[#D4AF37]" data-id="element-5237" data-mp="1l7h8bqxpm61v" />
                      <div className="absolute top-1/2 left-[83%] -translate-y-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-[#FDFBF7] rounded-full shadow-[0_0_10px_rgba(212,175,55,1)]" data-id="element-5238" data-mp="sg7crnyoqn2e" />
                    </div>
                    <span className="text-[#FDFBF7]/40 text-[7px] sm:text-[8px] tracking-widest uppercase mt-1.5 text-center" data-id="element-5239" data-mp="11hx4yw0e5q18">
                      850 XP to next
                    </span>
                  </div>

                  {/* Next */}
                  <div className="flex flex-col items-center gap-1.5 shrink-0 w-[70px] sm:w-[80px] opacity-60" data-id="element-5240" data-mp="2cur7hvumt48m">
                    <div className="relative w-10 h-10 sm:w-11 sm:h-11 border border-dashed border-[#D4AF37]/40 bg-[#0B0F19]/60 flex items-center justify-center" data-id="element-5241" data-mp="1bbgteryuntf">
                      <HonourGlyph type="order-of-rs" className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37]/60" data-id="element-5242" data-mp="3ol048u2w7ej1" />
                    </div>
                    <span className="font-serif text-[10px] sm:text-[11px] text-[#FDFBF7]/70 leading-tight text-center" data-id="element-5243" data-mp="3eowpmoqutnf7">
                      Order of RS
                    </span>
                    <span className="text-[#D4AF37]/40 text-[7px] tracking-[0.25em] uppercase" data-id="element-5244" data-mp="35mnk6dag0k8o">
                      Next
                    </span>
                  </div>
                </div>

                {/* Divider */}
                <div className="hidden lg:block w-px bg-[#D4AF37]/15 my-4" data-id="element-5245" data-mp="13vk7ligzsvlh" />
                <div className="lg:hidden h-px bg-[#D4AF37]/15 mx-4" data-id="element-5246" data-mp="yy0tqjo7iku2" />

                {/* Stats: XP / Streak / Sets */}
                <div className="grid grid-cols-3 lg:flex lg:items-stretch" data-id="element-5247" data-mp="3rrzb5rolav2h">
                  {/* XP */}
                  <div className="flex flex-col items-center justify-center px-2 sm:px-4 py-4 min-w-[80px] sm:min-w-[90px]" data-id="element-5248" data-mp="kdc6cctfgry">
                    <div className="flex items-center gap-1 mb-1" data-id="element-5249" data-mp="3npujuzlm6aj0">
                      <Sparkles className="w-2.5 h-2.5 text-[#D4AF37]/50" data-id="element-5250" data-mp="3no1bnfvcri8w" />
                      <span className="text-[#D4AF37]/50 text-[8px] tracking-[0.3em] uppercase" data-id="element-5251" data-mp="38zx14z6ged17">
                        XP
                      </span>
                    </div>
                    <span className="text-[#D4AF37] font-serif text-xl sm:text-2xl leading-none" data-id="element-5252" data-mp="300tgvrvjvrl4">
                      4,150
                    </span>
                    <span className="text-[#FDFBF7]/35 text-[8px] tracking-widest uppercase mt-1.5" data-id="element-5253" data-mp="33zskn95jpiaz">
                      Total
                    </span>
                  </div>

                  {/* Streak */}
                  <div className="flex flex-col items-center justify-center px-2 sm:px-4 py-4 min-w-[80px] sm:min-w-[90px] border-l border-[#D4AF37]/15" data-id="element-5254" data-mp="2v31eg3xa4n2o">
                    <div className="flex items-center gap-1 mb-1" data-id="element-5255" data-mp="26hlx7fb7zk3h">
                      <Calendar className="w-2.5 h-2.5 text-[#D4AF37]/50" data-id="element-5256" data-mp="2ldb8aievactf" />
                      <span className="text-[#D4AF37]/50 text-[8px] tracking-[0.3em] uppercase" data-id="element-5257" data-mp="2lb8d7jyuxthu">
                        Streak
                      </span>
                    </div>
                    <span className="text-[#D4AF37] font-serif text-xl sm:text-2xl leading-none" data-id="element-5258" data-mp="2ucg61milu24j">
                      47
                    </span>
                    <span className="text-[#FDFBF7]/35 text-[8px] tracking-widest uppercase mt-1.5" data-id="element-5259" data-mp="344c7n7jwgadi">
                      Days
                    </span>
                  </div>

                  {/* Sets */}
                  <div className="flex flex-col items-center justify-center px-2 sm:px-4 py-4 min-w-[80px] sm:min-w-[90px] border-l border-[#D4AF37]/15" data-id="element-5260" data-mp="3d5sithijggis">
                    <div className="flex items-center gap-1 mb-1" data-id="element-5261" data-mp="391cehkokph5o">
                      <BookOpen className="w-2.5 h-2.5 text-[#D4AF37]/50" data-id="element-5262" data-mp="3g7x17n7yigo4" />
                      <span className="text-[#D4AF37]/50 text-[8px] tracking-[0.3em] uppercase" data-id="element-5263" data-mp="3l6i27ov194dm">
                        Sets
                      </span>
                    </div>
                    <span className="text-[#D4AF37] font-serif text-xl sm:text-2xl leading-none" data-id="element-5264" data-mp="3uvrvh81m4t3b">
                      4<span className="text-[#FDFBF7]/30 text-base" data-id="element-5265" data-mp="2rz31f0ze4f9">/7</span>
                    </span>
                    <span className="text-[#FDFBF7]/35 text-[8px] tracking-widest uppercase mt-1.5" data-id="element-5266" data-mp="7q0schp02zz6">
                      Complete
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* HERO · Today's Packet — full-width, atmospheric, clickable */}
            <button type="button" onClick={() => setActiveTab('daily-packet')} className="group relative w-full text-left border border-[#D4AF37]/30 hover:border-[#D4AF37]/70 bg-gradient-to-br from-[#131B2B] via-[#0B0F19] to-[#0B0F19] transition-colors overflow-hidden" data-id="element-5267" data-mp="2ei1ljkntbalr">
              {/* Atmospheric glow */}
              <div className="absolute left-0 top-0 bottom-0 w-1/2 bg-[radial-gradient(ellipse_at_left,_rgba(212,175,55,0.18),_transparent_65%)] pointer-events-none" data-id="element-5268" data-mp="rsev9v8ls7u0" />
              <div className="absolute -left-20 -bottom-20 w-72 h-72 bg-[radial-gradient(circle,_rgba(139,0,0,0.18),_transparent_65%)] blur-2xl pointer-events-none" data-id="element-5269" data-mp="mrh6gzt38fjj" />
              <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent" data-id="element-5270" data-mp="1fjkd2fy3ta6y" />

              <div className="relative flex flex-col xl:flex-row items-center xl:items-center gap-6 xl:gap-10 p-5 sm:p-8 xl:p-10" data-id="element-5271" data-mp="1al919zo90pm3">
                {/* Envelope + text row (always horizontal once room allows) */}
                <div className="flex flex-col sm:flex-row items-center gap-5 sm:gap-8 flex-1 w-full" data-id="element-5272" data-mp="3v70wzr2djjuw">
                  <div className="relative w-28 sm:w-40 xl:w-48 shrink-0" data-id="element-5273" data-mp="1u75nxjnt9zxc">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.35),_transparent_65%)] blur-2xl group-hover:opacity-110" data-id="element-5274" data-mp="1u5uo1uxhlcbi" />
                    <img src="https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png" alt="Sealed envelope from the Darbar" className="relative w-full drop-shadow-[0_20px_40px_rgba(0,0,0,0.6)] transition-transform duration-500 group-hover:-translate-y-1" data-id="element-5275" data-mp="1ka1964wap6qk" />
                  </div>

                  <div className="flex-1 min-w-0 text-center sm:text-left" data-id="element-5276" data-mp="238lybremm21k">
                    <p className="text-[#8B0000] text-[9px] sm:text-[10px] uppercase tracking-[0.35em] font-bold mb-2 sm:mb-3" data-id="element-5277" data-mp="286ni6ejvm5e3">
                      Today's Packet · Unopened
                    </p>
                    <h4 className="font-serif text-lg sm:text-2xl xl:text-3xl text-[#FDFBF7] mb-2 sm:mb-3 leading-tight" data-id="element-5278" data-mp="30my8uhbxhhl8">
                      A sealed envelope from the Darbar awaits.
                    </h4>
                    <p className="text-[#FDFBF7]/50 text-[10px] sm:text-xs font-light tracking-wider flex flex-wrap items-center justify-center sm:justify-start gap-x-2 gap-y-1" data-id="element-5279" data-mp="k9o3xccffeec">
                      <Calendar className="w-3 h-3 text-[#D4AF37]/60" data-id="element-5280" data-mp="tyz76icmtoub" />
                      <span data-id="element-5281" data-mp="u129a0r278fe">Resets in</span>
                      <span className="text-[#D4AF37] font-mono tracking-wider" data-id="element-5282" data-mp="68v72kynk782">
                        04:12:38
                      </span>
                    </p>
                  </div>
                </div>

                <div className="shrink-0 w-full xl:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-[#D4AF37] group-hover:bg-[#E5C158] text-[#0B0F19] text-[9px] sm:text-[10px] tracking-[0.3em] uppercase font-bold transition-colors flex items-center justify-center gap-2 shadow-xl" data-id="element-5283" data-mp="fabfin5wpz9n">
                  <Sparkles className="w-3.5 h-3.5" data-id="element-5284" data-mp="1mnmwqq4ez0do" /> Open Packet
                  <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" data-id="element-5285" data-mp="1mnd9haar77bo" />
                </div>
              </div>
            </button>

            {/* CENTERPIECE · What I Can Unlock Next — full width */}
            <button type="button" onClick={() => {
            setActiveTab('collections');
            setSelectedCollection('Lahore Court Collection');
          }} className="group w-full text-left bg-[#0B0F19] border border-[#D4AF37]/25 hover:border-[#D4AF37]/55 p-5 sm:p-8 flex flex-col transition-colors" data-id="element-5286" data-mp="1rbryurasbrgy">
              <div className="flex items-start justify-between gap-4 mb-4" data-id="element-5287" data-mp="2f3p7yamuo5ed">
                <div data-id="element-5288" data-mp="2a9bbe4wgbs84">
                  <p className="text-[#D4AF37]/60 text-[9px] sm:text-[10px] tracking-[0.3em] uppercase mb-2" data-id="element-5289" data-mp="2a8jemqr4bog9">
                    What I Can Unlock Next
                  </p>
                  <h4 className="font-serif text-xl sm:text-3xl text-[#FDFBF7] leading-tight group-hover:text-[#D4AF37] transition-colors" data-id="element-5290" data-mp="32sroff5qzs7a">
                    The Founder Seal
                  </h4>
                </div>
                <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-[#8B0000] font-bold whitespace-nowrap bg-[#8B0000]/10 border border-[#8B0000]/30 px-2 sm:px-3 py-1 sm:py-1.5" data-id="element-5291" data-mp="36b78qbp2s61v">
                  1 To Go
                </span>
              </div>

              <p className="text-[#FDFBF7]/60 text-xs sm:text-sm font-light leading-relaxed mb-6 sm:mb-8 max-w-2xl" data-id="element-5292" data-mp="3kagr9i0r7c90">
                Complete the Royal Court Collection to claim your{' '}
                <span className="text-[#D4AF37]" data-id="element-5293" data-mp="3t898irq1ctz1">Founder Seal</span> — a
                permanent identity badge — plus 500 XP.
              </p>

              {/* Big 5-card slot grid — full width, lots of breathing room */}
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-4 mb-6 sm:mb-8" data-id="element-5294" data-mp="6t7s1hrpw6cq">
                {[{
                name: 'Ranjit Singh',
                img: 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png',
                isRarest: true
              }, {
                name: 'Jind Kaur',
                img: 'https://cdn.magicpatterns.com/uploads/nVFG4hPLbQSgSkuEzm99Kr/Jind_Kaur.png'
              }, {
                name: 'Hari Singh Nalwa',
                img: 'https://cdn.magicpatterns.com/uploads/ekyaLxoHxw9b6NSM5wgnik/Hari_Singh_Nalwa.png'
              }, {
                name: 'Akali Phula Singh',
                missing: true
              }, {
                name: 'Moran Sarkar',
                img: 'https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png'
              }].map((slot, i) => <div key={i} className={`relative aspect-[3/4] overflow-hidden ${slot.missing ? 'border-2 border-dashed border-[#8B0000]/60 bg-[#131B2B]/60' : slot.isRarest ? 'border-2 border-[#D4AF37] shadow-[0_0_25px_rgba(212,175,55,0.3)]' : 'border border-[#D4AF37]/30'}`} data-id="element-5295" data-mp="2ll3d21c6st0m" data-mpi={i}>
                    {slot.missing ? <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 sm:gap-2 p-1 sm:p-2 text-center" data-id="element-5296" data-mp="mdn050ami4yf">
                        <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-[#8B0000]/20 border border-[#8B0000]/50 flex items-center justify-center mb-0.5 sm:mb-1" data-id="element-5297" data-mp="ran67b7h37pj">
                          <Lock className="w-3 h-3 sm:w-4 sm:h-4 text-[#8B0000]" data-id="element-5298" data-mp="heaay8wa4lso" />
                        </div>
                        <p className="text-[#8B0000] text-[7px] sm:text-[9px] uppercase tracking-widest font-bold" data-id="element-5299" data-mp="3falx04uu4vz">
                          Missing
                        </p>
                        <p className="text-[#FDFBF7]/50 text-[7px] sm:text-[9px] font-light leading-snug px-0.5" data-id="element-5300" data-mp="1xrs25nra04r4">
                          {slot.name}
                        </p>
                      </div> : <>
                        <img src={slot.img} alt={slot.name} className="w-full h-full object-cover" data-id="element-5301" data-mp="2n12nuy0apia" />
                        {slot.isRarest && <div className="absolute top-1 left-1 sm:top-1.5 sm:left-1.5 px-1 sm:px-1.5 py-0.5 bg-[#D4AF37] text-[#0B0F19] text-[6px] sm:text-[8px] uppercase tracking-widest font-bold flex items-center gap-0.5 sm:gap-1" data-id="element-5302" data-mp="3kvjmuihncak4">
                            <Crown className="w-2 sm:w-2.5 h-2 sm:h-2.5" data-id="element-5303" data-mp="3b3m851rfj0gn" />{' '}
                            Rarest
                          </div>}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/80 to-transparent px-1.5 py-1 sm:px-2 sm:py-1.5" data-id="element-5304" data-mp="325aawgw254f8">
                          <p className="text-[7px] sm:text-[9px] uppercase tracking-widest text-[#FDFBF7]/90 truncate font-medium" data-id="element-5305" data-mp="2s6cobji6zkyv">
                            {slot.name}
                          </p>
                        </div>
                      </>}
                  </div>)}
              </div>

              {/* Progress + Find a Trade footer */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-5 pt-5 sm:pt-6 border-t border-[#D4AF37]/15 mt-auto" data-id="element-5306" data-mp="1lntbd5gz270m">
                <div className="flex-1 min-w-0" data-id="element-5307" data-mp="oz3unih2zkg8">
                  <div className="flex justify-between text-[8px] sm:text-[9px] uppercase tracking-widest text-[#FDFBF7]/50 mb-2" data-id="element-5308" data-mp="twdt1nmg76g7">
                    <span data-id="element-5309" data-mp="xx614djl35aw">Progress · 4 / 5</span>
                    <span className="text-[#D4AF37] font-bold" data-id="element-5310" data-mp="xwe5x3twf9l5">80%</span>
                  </div>
                  <div className="w-full h-1 bg-[#131B2B] rounded-full overflow-hidden" data-id="element-5311" data-mp="12qs7jnz5mcy9">
                    <div className="w-[80%] h-full bg-gradient-to-r from-[#8B0000] to-[#D4AF37]" data-id="element-5312" data-mp="64yl9fbwjwxk" />
                  </div>
                </div>
                <span role="button" onClick={e => {
                e.stopPropagation();
                setActiveTab('trade-inbox');
                setTradeTab('market');
              }} className="px-5 py-2.5 sm:px-6 sm:py-3 border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 text-[9px] sm:text-[10px] uppercase tracking-[0.2em] flex items-center justify-center gap-2 transition-colors whitespace-nowrap cursor-pointer" data-id="element-5313" data-mp="1dj0rmvzguy7x">
                  <Repeat className="w-3 h-3" data-id="element-5314" data-mp="pmwlw2wyte1t" /> Find Trade
                  <ArrowRight className="w-3 h-3" data-id="element-5315" data-mp="13l4gw5afy5ac" />
                </span>
              </div>
            </button>

            {/* My Misl — full-width prominent card */}
            <div className="relative bg-gradient-to-br from-[#131B2B] via-[#0B0F19] to-[#0B0F19] border border-[#D4AF37]/30 overflow-hidden" data-id="element-5316" data-mp="zlvob3r50dgd">
              <div className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/70 to-transparent" data-id="element-5317" data-mp="2azhd2nt34d1d" />
              <div className="absolute left-0 top-0 bottom-0 w-1/3 bg-[radial-gradient(ellipse_at_left,_rgba(139,0,0,0.15),_transparent_65%)] pointer-events-none" data-id="element-5318" data-mp="2igvz6w2uk7j6" />

              <div className="px-4 sm:px-5 pt-3 pb-2 border-b border-[#D4AF37]/15 flex items-center justify-between" data-id="element-5319" data-mp="28l2o7h4s0m3i">
                <p className="text-[#D4AF37]/60 text-[8px] tracking-[0.35em] uppercase flex items-center gap-2" data-id="element-5320" data-mp="2dk5hk4be93vn">
                  <Users className="w-2.5 h-2.5" data-id="element-5321" data-mp="319gjvq8e76bw" /> My Clan (Misl)
                </p>
                <button type="button" onClick={() => setActiveTab('referrals')} className="text-[#D4AF37] hover:text-[#E5C158] text-[8px] tracking-[0.3em] uppercase font-bold flex items-center gap-1.5" data-id="element-5322" data-mp="366qa383lvm0s">
                  Invite to Clan <ArrowRight className="w-2.5 h-2.5" data-id="element-5323" data-mp="3k6rkbe5rg5sx" />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-px bg-[#D4AF37]/10" data-id="element-5324" data-mp="3ozknvypht83f">
                {/* Left: Misl identity + your rank in it */}
                <div className="p-5 sm:p-6 bg-[#0B0F19] flex flex-col gap-4" data-id="element-5325" data-mp="1k5l1lzk287qf">
                  <div className="flex items-center gap-4" data-id="element-5326" data-mp="1k3gnkosg5scf">
                    <div className="relative w-14 h-14 border border-[#D4AF37] bg-[#131B2B] flex items-center justify-center shadow-[0_0_20px_rgba(212,175,55,0.25)] shrink-0" data-id="element-5327" data-mp="1g510kkwd8jz0">
                      <Crown className="w-6 h-6 text-[#D4AF37]" data-id="element-5328" data-mp="27vnom194ani6" />
                      <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5 bg-[#8B0000] text-[#FDFBF7] text-[7px] tracking-[0.2em] uppercase font-bold whitespace-nowrap" data-id="element-5329" data-mp="27st3s8m6z0o8">
                        #3 Globally
                      </span>
                    </div>
                    <div className="min-w-0" data-id="element-5330" data-mp="1tv4gku4njhg0">
                      <p className="text-[#D4AF37]/60 text-[9px] tracking-[0.3em] uppercase mb-0.5" data-id="element-5331" data-mp="nftw75a8d05u">
                        Sukerchakia Misl
                      </p>
                      <h4 className="font-serif text-xl sm:text-2xl text-[#FDFBF7] leading-tight" data-id="element-5332" data-mp="iik7xsykuujl">
                        Led by <span className="text-[#D4AF37]" data-id="element-5333" data-mp="dbdh4e4krn33">@ranjit_s</span>
                      </h4>
                      <p className="text-[#FDFBF7]/40 text-[10px] mt-1" data-id="element-5334" data-mp="iagnq307e1d6">
                        1,520 members · 480k XP total · Founded Day 1
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 sm:gap-3 pt-3 border-t border-[#D4AF37]/10" data-id="element-5335" data-mp="r3a3s2nh54s3">
                    <div data-id="element-5336" data-mp="w1bw7e6sigg8">
                      <p className="text-[#D4AF37]/50 text-[8px] tracking-[0.3em] uppercase mb-1" data-id="element-5337" data-mp="w0k104h3ukqh">
                        Your Place
                      </p>
                      <p className="font-serif text-xl text-[#FDFBF7]" data-id="element-5338" data-mp="15zrdyqjxgdd2">
                        #47
                        <span className="text-[#FDFBF7]/30 text-sm" data-id="element-5339" data-mp="32skxphf4ldht">
                          {' '}
                          / 1,520
                        </span>
                      </p>
                    </div>
                    <div data-id="element-5340" data-mp="1fquxi6oogf4s">
                      <p className="text-[#D4AF37]/50 text-[8px] tracking-[0.3em] uppercase mb-1" data-id="element-5341" data-mp="1frmspged4auj">
                        Clan Rank
                      </p>
                      <p className="font-serif text-xl text-[#D4AF37]" data-id="element-5342" data-mp="1oq88x4wd3ds5">#3</p>
                      <p className="text-[#FDFBF7]/35 text-[8px] uppercase tracking-widest mt-0.5" data-id="element-5343" data-mp="1thgh5xlygt3q">
                        of 20 clans
                      </p>
                    </div>
                    <div data-id="element-5344" data-mp="1yint88v4seg7">
                      <p className="text-[#D4AF37]/50 text-[8px] tracking-[0.3em] uppercase mb-1" data-id="element-5345" data-mp="23fe5sm7q8rwv">
                        Climbing
                      </p>
                      <p className="font-serif text-xl text-emerald-400 flex items-center gap-1" data-id="element-5346" data-mp="27i0ysqg4wivn">
                        +12
                        <ArrowUp className="w-3.5 h-3.5" data-id="element-5347" data-mp="d38895u3yiak" />
                      </p>
                      <p className="text-[#FDFBF7]/35 text-[8px] uppercase tracking-widest mt-0.5" data-id="element-5348" data-mp="d5b446hxz5n7">
                        this week
                      </p>
                    </div>
                  </div>
                </div>

                {/* Right: Mildars of the Month leaderboard */}
                <div className="p-5 sm:p-6 bg-[#0B0F19] flex flex-col gap-3" data-id="element-5349" data-mp="5pz8skh5p8b6">
                  <div className="flex items-center justify-between" data-id="element-5350" data-mp="tiibjmhihux">
                    <p className="text-[#D4AF37]/60 text-[9px] tracking-[0.3em] uppercase flex items-center gap-1.5" data-id="element-5351" data-mp="3s01ub2424dut">
                      <Trophy className="w-3 h-3" data-id="element-5352" data-mp="tgbqgn27ejey" /> Mildars of the Month
                    </p>
                    <p className="text-[#FDFBF7]/35 text-[8px] tracking-widest uppercase" data-id="element-5353" data-mp="ojv15pj9pz0a">
                      Longest reigning
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5" data-id="element-5354" data-mp="fin2h6cuaw5o">
                    {[{
                    rank: 1,
                    name: '@maharaja',
                    misl: 'Lahore Darbar',
                    days: 38,
                    color: 'text-[#D4AF37]',
                    bg: 'bg-[#D4AF37]/10 border-[#D4AF37]/40'
                  }, {
                    rank: 2,
                    name: '@ranjit_s',
                    misl: 'Sukerchakia',
                    days: 24,
                    color: 'text-[#FDFBF7]/80',
                    bg: 'bg-[#FDFBF7]/[0.04] border-[#FDFBF7]/15',
                    isYou: true
                  }, {
                    rank: 3,
                    name: '@jassa_singh',
                    misl: 'Ahluwalia',
                    days: 19,
                    color: 'text-[#FDFBF7]/80',
                    bg: 'bg-[#FDFBF7]/[0.04] border-[#FDFBF7]/15'
                  }, {
                    rank: 4,
                    name: '@khalsa_s',
                    misl: 'Sarkar-e-Khalsa',
                    days: 15,
                    color: 'text-[#FDFBF7]/60',
                    bg: 'bg-[#FDFBF7]/[0.04] border-[#FDFBF7]/15'
                  }].map(m => <div key={m.rank} className={`flex items-center gap-2.5 px-2.5 py-2 border ${m.bg} text-left`} data-id="element-5355" data-mp="1gggxqd2nd51j">
                        <span className={`font-serif text-base w-5 text-center ${m.color}`} data-id="element-5356" data-mp="1pgwz9gzty23r">
                          {m.rank}
                        </span>
                        <div className="flex-1 min-w-0" data-id="element-5357" data-mp="1zb5ljmy9oyth">
                          <p className="text-[#FDFBF7] text-[11px] font-medium truncate" data-id="element-5358" data-mp="2d33hew2ywy7p">
                            {m.name}
                            {m.isYou && <span className="ml-1.5 text-[#D4AF37] text-[8px] tracking-[0.2em] uppercase" data-id="element-5359" data-mp="2n1rkmoqan8rc">
                                · Your Clan
                              </span>}
                          </p>
                          <p className="text-[#FDFBF7]/40 text-[9px] uppercase tracking-widest truncate" data-id="element-5360" data-mp="325ca29um0pm0">
                            {m.misl}
                          </p>
                        </div>
                        <span className="text-[#D4AF37] text-[10px] font-mono whitespace-nowrap" data-id="element-5361" data-mp="3azz9mpdle7qc">
                          {m.days}d
                        </span>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>

            {/* Take Action · XP Quests — what to do to earn */}
            <div className="bg-[#0B0F19] border border-[#D4AF37]/25 overflow-hidden" data-id="element-5362" data-mp="2s6d81a4cns7z">
              <div className="px-4 sm:px-5 pt-3 pb-2 border-b border-[#D4AF37]/15 flex items-center justify-between" data-id="element-5363" data-mp="2x6riszxc5g9p">
                <p className="text-[#D4AF37]/60 text-[8px] tracking-[0.35em] uppercase flex items-center gap-2" data-id="element-5364" data-mp="7ge2t8eovx92">
                  <Zap className="w-2.5 h-2.5" data-id="element-5365" data-mp="cepdtmgq0dsv" /> Earn XP · Take Action
                </p>
                <p className="text-[#FDFBF7]/35 text-[8px] tracking-[0.25em] uppercase" data-id="element-5366" data-mp="1wwzy5idyc9w5">
                  4 active quests
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-[#D4AF37]/10" data-id="element-5367" data-mp="1n7zuvmsf2sc5">
                {[{
                icon: UserPlus,
                label: 'Invite a friend',
                xp: '+500 XP',
                sub: 'Each qualified referral',
                cta: 'Invite',
                tab: 'referrals',
                accent: 'text-[#D4AF37]'
              }, {
                icon: Repeat,
                label: 'Make a trade',
                xp: '+150 XP',
                sub: '2 incoming offers',
                cta: 'View Inbox',
                tab: 'trade-inbox',
                accent: 'text-[#D4AF37]',
                badge: '2'
              }, {
                icon: Calendar,
                label: 'Add Treasury Night',
                xp: '+200 XP',
                sub: 'RSVP to Jul 18 event',
                cta: 'Add to Calendar',
                tab: 'live-events',
                accent: 'text-[#8B0000]'
              }, {
                icon: Gift,
                label: 'Open daily packet',
                xp: '+50 XP',
                sub: 'Resets in 04:12:38',
                cta: 'Open Now',
                tab: 'daily-packet',
                accent: 'text-[#D4AF37]'
              }].map(q => {
                const Icon = q.icon;
                return <button key={q.label} type="button" onClick={() => setActiveTab(q.tab)} className="group flex flex-col items-start gap-2 p-4 hover:bg-[#D4AF37]/[0.04] transition-colors text-left" data-id="element-5368" data-mp="3mex2q7pfimhq">
                      <div className="flex items-center justify-between w-full" data-id="element-5369" data-mp="3dz7mrds4d0qv">
                        <div className="w-8 h-8 border border-[#D4AF37]/30 bg-[#131B2B] flex items-center justify-center group-hover:border-[#D4AF37] transition-colors relative" data-id="element-5370" data-mp="3sw8cxcunwnn8">
                          <Icon className={`w-4 h-4 ${q.accent} opacity-80 group-hover:opacity-100 transition-opacity`} data-id="element-5371" data-mp="307149l1huho9" />
                          {q.badge && <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-[#8B0000] text-[#FDFBF7] text-[8px] flex items-center justify-center rounded-full font-bold" data-id="element-5372" data-mp="39xcji3qmodag">
                              {q.badge}
                            </span>}
                        </div>
                        <span className={`text-[10px] font-bold ${q.accent}`} data-id="element-5373" data-mp="2ci8gwcyxti59">
                          {q.xp}
                        </span>
                      </div>
                      <p className="text-[#FDFBF7] text-xs font-medium leading-tight" data-id="element-5374" data-mp="1vx73bbwc13b2">
                        {q.label}
                      </p>
                      <p className="text-[#FDFBF7]/40 text-[10px] leading-snug" data-id="element-5375" data-mp="20vicdkesz9s8">
                        {q.sub}
                      </p>
                      <span className="mt-1 text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase font-bold flex items-center gap-1 group-hover:gap-2 transition-all" data-id="element-5376" data-mp="24vk4b1uqkoub">
                        {q.cta}
                        <ArrowRight className="w-3 h-3" data-id="element-5377" data-mp="2etq3vct2liw0" />
                      </span>
                    </button>;
              })}
              </div>
            </div>

            {/* Bottom 2-up: My Rank + Next Treasury Night */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6" data-id="element-5378" data-mp="2soisr1uuobf9">
              {/* My Rank */}
              <button type="button" onClick={() => setActiveTab('profile')} className="group bg-[#0B0F19] border border-[#D4AF37]/25 hover:border-[#D4AF37]/55 p-5 sm:p-8 flex flex-col text-left transition-colors" data-id="element-5379" data-mp="2xnc5ut6860ew">
                <div className="flex items-center justify-between mb-5" data-id="element-5380" data-mp="cxr0z2y3yd03">
                  <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.3em] uppercase" data-id="element-5381" data-mp="7zft2xrdicki">
                    My Rank
                  </p>
                  <ArrowRight className="w-4 h-4 text-[#D4AF37]/0 group-hover:text-[#D4AF37]/70 transition-all -translate-x-2 group-hover:translate-x-0" data-id="element-5382" data-mp="14ongycfgsxrl" />
                </div>
                <div className="flex items-center gap-4 mb-6" data-id="element-5383" data-mp="vwv5pzim9kly">
                  <div className="w-12 h-12 border border-[#D4AF37] bg-[#131B2B] flex items-center justify-center shadow-[0_0_15px_rgba(212,175,55,0.2)]" data-id="element-5384" data-mp="vv1z2k8070dy">
                    <Sword className="w-6 h-6 text-[#D4AF37]" data-id="element-5385" data-mp="1oer12kqp5w4o" />
                  </div>
                  <div data-id="element-5386" data-mp="1jmphedr7934m">
                    <p className="font-serif text-2xl text-[#FDFBF7] leading-none" data-id="element-5387" data-mp="1jlegclp529h7">
                      Sardar
                    </p>
                    <p className="text-[#FDFBF7]/40 text-[10px] mt-1.5 uppercase tracking-widest" data-id="element-5388" data-mp="1bikofv7jpwmd">
                      4,150 XP
                    </p>
                  </div>
                </div>
                <div className="mt-auto" data-id="element-5389" data-mp="1lckuq0ekd7wo">
                  <div className="flex justify-between text-[9px] uppercase tracking-widest text-[#FDFBF7]/50 mb-1.5" data-id="element-5390" data-mp="1get02ymjb7os">
                    <span data-id="element-5391" data-mp="1ue2fvx6tvzs8">Progress to Noble</span>
                    <span className="text-[#D4AF37]" data-id="element-5392" data-mp="1qea3f9ebd9hl">83%</span>
                  </div>
                  <div className="w-full h-1 bg-[#131B2B] rounded-full overflow-hidden" data-id="element-5393" data-mp="245f8ws4d4vzu">
                    <div className="w-[83%] h-full bg-gradient-to-r from-[#8B0000] to-[#D4AF37]" data-id="element-5394" data-mp="2479k3epu19qq" />
                  </div>
                  <p className="text-[#FDFBF7]/40 text-[10px] font-light mt-2" data-id="element-5395" data-mp="2e3cv7a2nwipt">
                    850 XP to next rank
                  </p>
                </div>
              </button>

              {/* Next Treasury Night */}
              <button type="button" onClick={() => setActiveTab('live-events')} className="group bg-[#0B0F19] border border-[#D4AF37]/25 hover:border-[#D4AF37]/55 p-5 sm:p-8 relative overflow-hidden text-left transition-colors" data-id="element-5396" data-mp="2srcyvcu9u5nu">
                <div className="absolute top-0 inset-x-0 h-0.5 bg-[#8B0000]" data-id="element-5397" data-mp="2nqfdkrdysvi4" />
                <div className="flex items-center justify-between mb-5" data-id="element-5398" data-mp="292kk38s4un3z">
                  <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.3em] uppercase" data-id="element-5399" data-mp="290hng5wh5vqa">
                    Next Treasury Night
                  </p>
                  <ArrowRight className="w-4 h-4 text-[#D4AF37]/0 group-hover:text-[#D4AF37]/70 transition-all -translate-x-2 group-hover:translate-x-0" data-id="element-5400" data-mp="202p66w770e09" />
                </div>
                <div className="flex items-start gap-4" data-id="element-5401" data-mp="1q2ycgwqgi318">
                  <div className="flex flex-col items-center px-4 py-3 border border-[#8B0000]/40 bg-[#131B2B] shrink-0" data-id="element-5402" data-mp="1q5jfvq6ekr13">
                    <span className="text-[#8B0000] text-[10px] tracking-[0.2em] uppercase font-bold" data-id="element-5403" data-mp="1bxy092089mcc">
                      JUL
                    </span>
                    <span className="font-serif text-3xl text-[#FDFBF7] leading-none my-1" data-id="element-5404" data-mp="175n1w3l4y8lw">
                      18
                    </span>
                    <span className="text-[#D4AF37]/60 text-[9px] tracking-widest uppercase" data-id="element-5405" data-mp="x91rt3u9e7b7">
                      7:00 PM
                    </span>
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-5406" data-mp="1uupq5ua9yzpd">
                    <p className="font-serif text-lg text-[#FDFBF7] leading-snug mb-2" data-id="element-5407" data-mp="1pxfu3vsdrpsk">
                      The Court Awakens
                    </p>
                    <p className="text-[#FDFBF7]/50 text-[11px] font-light flex items-center gap-1.5 mb-3" data-id="element-5408" data-mp="1l0fn9inpij0e">
                      <MapPin className="w-3 h-3 text-[#D4AF37]/60" data-id="element-5409" data-mp="9jfwirh0cc8h" />{' '}
                      Illuminarium · Toronto
                    </p>
                    <div className="flex items-center gap-2" data-id="element-5410" data-mp="4jbcirgw4zdi">
                      <span className="px-2 py-1 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-[9px] uppercase tracking-widest font-bold" data-id="element-5411" data-mp="2g7pwzxj8mrts">
                        +200 XP RSVP
                      </span>
                      <span className="text-[#D4AF37] text-[10px] uppercase tracking-widest font-bold" data-id="element-5412" data-mp="2q5m8wrk01qsy">
                        In 12 days
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </>;
      case 'daily-packet':
        return <div className="flex-1 flex flex-col items-center justify-center text-center min-h-[560px] relative overflow-hidden py-8" data-id="element-5413" data-mp="33vhd7c7wfp0c">
            {/* Ambient atmospheric background */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.06),_transparent_70%)] pointer-events-none" data-id="element-5414" data-mp="3duyfrkrutms3" />

            <AnimatePresence mode="wait" data-id="element-5415" data-mp="1jh79mv8qhl1e">
              {/* ============ SEALED ============ */}
              {packetState === 'sealed' && <motion.div key="sealed" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              scale: 0.95
            }} transition={{
              duration: 0.5
            }} className="flex flex-col items-center text-center max-w-md relative z-10" data-id="element-5416" data-mp="39x9geewtxyvk">
                  <motion.div animate={{
                y: [0, -8, 0]
              }} transition={{
                duration: 3.5,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="relative w-56 mb-10" data-id="element-5417" data-mp="301eu99tedlo1">
                    <motion.div animate={{
                  opacity: [0.4, 0.7, 0.4]
                }} transition={{
                  duration: 2.8,
                  repeat: Infinity
                }} className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.35),_transparent_65%)] blur-2xl pointer-events-none" data-id="element-5418" data-mp="1tetsikwfwgkb" />
                    <motion.button type="button" onClick={() => {
                  setPacketState('opening');
                  setTimeout(() => setPacketState('revealed'), 1900);
                }} whileHover={{
                  scale: 1.04
                }} whileTap={{
                  scale: 0.96
                }} aria-label="Break the seal and open your daily packet" className="relative block w-full cursor-pointer" data-id="element-5419" data-mp="2c5tfxhoadibh">
                      <img src="https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png" alt="Sealed envelope" className="w-full drop-shadow-[0_25px_50px_rgba(0,0,0,0.7)]" data-id="element-5420" data-mp="2tedr0chcnjee" />
                      <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 text-[#D4AF37]/60 text-[9px] tracking-[0.3em] uppercase whitespace-nowrap" data-id="element-5421" data-mp="2jmq6ywn4h4my">
                        Click to open
                      </span>
                    </motion.button>
                  </motion.div>
                  <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-3" data-id="element-5422" data-mp="25qvxocifsd9b">
                    Day 47 · The Darbar
                  </p>
                  <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-3" data-id="element-5423" data-mp="3wdgx9wuozzh">
                    Your Daily Packet has Arrived
                  </h3>
                  <p className="text-[#FDFBF7]/60 text-sm font-light mb-8 leading-relaxed" data-id="element-5424" data-mp="6hukr6bshe1v">
                    A sealed envelope from the Maharaja's court awaits. Break
                    the wax — discover the artifact within.
                  </p>
                  <motion.button whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} onClick={() => {
                setPacketState('opening');
                setTimeout(() => setPacketState('revealed'), 1900);
              }} className="px-10 py-4 bg-[#8B0000] hover:bg-[#A00000] text-[#FDFBF7] text-xs tracking-[0.3em] uppercase font-medium shadow-2xl transition-colors flex items-center gap-3" data-id="element-5425" data-mp="g8z3mh4l5j1z">
                    <Sparkles className="w-4 h-4" data-id="element-5426" data-mp="18vtgarv5ve4w" /> Break the Seal
                  </motion.button>
                </motion.div>}

              {/* ============ OPENING ============ */}
              {packetState === 'opening' && <motion.div key="opening" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} className="relative flex items-center justify-center w-full h-full min-h-[560px]" data-id="element-5427" data-mp="1u0nlkuba95ay">
                  {/* Expanding gold burst */}
                  <motion.div initial={{
                scale: 0.4,
                opacity: 0
              }} animate={{
                scale: 4,
                opacity: [0, 0.9, 0]
              }} transition={{
                duration: 1.8,
                ease: 'easeOut'
              }} className="absolute w-96 h-96 rounded-full bg-[radial-gradient(circle,_rgba(212,175,55,0.6),_transparent_60%)] blur-3xl" data-id="element-5428" data-mp="2dqfran73l88j" />

                  {/* Shaking + shrinking envelope */}
                  <motion.div initial={{
                scale: 1,
                opacity: 1
              }} animate={{
                scale: [1, 1.1, 0.6],
                opacity: [1, 1, 0],
                rotateZ: [0, -3, 4, -2, 0]
              }} transition={{
                duration: 1.8,
                times: [0, 0.5, 1],
                ease: 'easeInOut'
              }} className="relative w-64 sm:w-72 z-10" data-id="element-5429" data-mp="2u8u33xuadkf1">
                    <img src="https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png" alt="Opening envelope" className="w-full drop-shadow-[0_0_80px_rgba(212,175,55,0.7)]" data-id="element-5430" data-mp="9il5k1w3z679" />
                  </motion.div>

                  {/* Radial sparkle particles */}
                  {Array.from({
                length: 20
              }).map((_, i) => {
                const angle = i / 20 * Math.PI * 2;
                const distance = 280;
                return <motion.div key={i} initial={{
                  x: 0,
                  y: 0,
                  opacity: 0,
                  scale: 0
                }} animate={{
                  x: Math.cos(angle) * distance,
                  y: Math.sin(angle) * distance,
                  opacity: [0, 1, 0],
                  scale: [0, 1.2, 0]
                }} transition={{
                  duration: 1.6,
                  delay: 0.3 + i % 4 * 0.05,
                  ease: 'easeOut'
                }} className="absolute w-2 h-2 bg-[#D4AF37] rounded-full shadow-[0_0_14px_rgba(212,175,55,1)]" data-id="element-5431" data-mp="v7abxx7o7utb" data-mpi={i} />;
              })}

                  {/* Caption */}
                  <motion.p initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: [0, 1, 1, 0],
                y: 0
              }} transition={{
                duration: 1.8,
                times: [0, 0.3, 0.7, 1]
              }} className="absolute bottom-16 font-serif text-xl text-[#D4AF37] tracking-[0.35em] uppercase" data-id="element-5432" data-mp="dw54uu0zq80k">
                    The seal breaks…
                  </motion.p>
                </motion.div>}

              {/* ============ REVEALED ============ */}
              {packetState === 'revealed' && <motion.div key="revealed" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0,
              y: -30
            }} transition={{
              duration: 0.6
            }} className="flex flex-col items-center text-center max-w-2xl mx-auto" data-id="element-5433" data-mp="3s4rwlkdlagb0">
                  <motion.p initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.4
              }} className="text-[#D4AF37] text-[10px] tracking-[0.4em] uppercase mb-4" data-id="element-5434" data-mp="jqt08hmpgswz">
                    A Treasure from the Court
                  </motion.p>

                  <motion.div initial={{
                y: 200,
                scale: 0.5,
                rotateY: 90,
                opacity: 0
              }} animate={{
                y: 0,
                scale: 1,
                rotateY: 0,
                opacity: 1
              }} transition={{
                duration: 0.9,
                ease: [0.16, 1, 0.3, 1]
              }} className="relative mb-6" style={{
                perspective: 1000
              }} data-id="element-5435" data-mp="2e82tzycj0oob">
                    {/* Gold glow halo */}
                    <motion.div animate={{
                  opacity: [0.4, 0.75, 0.4]
                }} transition={{
                  duration: 3,
                  repeat: Infinity
                }} className="absolute -inset-10 bg-[radial-gradient(circle,_rgba(212,175,55,0.4),_transparent_60%)] blur-2xl" data-id="element-5436" data-mp="3futyukopa0r1" />

                    <motion.div animate={{
                  y: [0, -8, 0]
                }} transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} className="relative w-64 sm:w-72" data-id="element-5437" data-mp="2ve7vtddjeph9">
                      <div className="border-2 border-[#D4AF37] shadow-[0_0_50px_rgba(212,175,55,0.5)] overflow-hidden bg-[#0B0F19]" data-id="element-5438" data-mp="3eb92r3qniznu">
                        <img src="https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png" alt="Moran Sarkar" className="w-full" data-id="element-5439" data-mp="anqjtl3l8xw6" />
                      </div>
                      <div className="absolute -top-3 -right-3 px-3 py-1 bg-[#D4AF37] text-[#0B0F19] text-[9px] uppercase tracking-widest font-bold shadow-lg" data-id="element-5440" data-mp="1qcyh7rqjsis8">
                        Treasury Rare
                      </div>
                    </motion.div>
                  </motion.div>

                  <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.9,
                duration: 0.6
              }} className="text-center" data-id="element-5441" data-mp="1cfjniat9houg">
                    <h3 className="font-serif text-3xl text-[#FDFBF7] mb-2" data-id="element-5442" data-mp="2sub65w0lvhqk">
                      Moran Sarkar
                    </h3>
                    <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase mb-4" data-id="element-5443" data-mp="1pi4843te3u3h">
                      Royal Court Collection · Treasury Rare
                    </p>
                    <p className="text-[#FDFBF7]/60 text-sm font-light italic max-w-md mx-auto mb-8 leading-relaxed" data-id="element-5444" data-mp="1zi4s7rz32e8q">
                      "A dancer who became consort to the Sher-e-Punjab — beauty
                      and intellect at the heart of the Darbar."
                    </p>

                    <motion.button whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} onClick={() => setPacketState('added')} className="px-10 py-4 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0B0F19] text-xs tracking-[0.3em] uppercase font-bold shadow-2xl transition-colors flex items-center gap-3 mx-auto" data-id="element-5445" data-mp="1bsagjdqi2vq2">
                      <BookOpen className="w-4 h-4" data-id="element-5446" data-mp="kifq0dc9zgey" /> Add to Royal Court
                    </motion.button>
                  </motion.div>
                </motion.div>}

              {/* ============ ADDED ============ */}
              {packetState === 'added' && <motion.div key="added" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} className="flex flex-col items-center text-center max-w-md mx-auto relative" data-id="element-5447" data-mp="6kr555i7k99w">
                  {/* Flying card animation */}
                  <motion.div initial={{
                x: 0,
                y: 0,
                scale: 1,
                opacity: 1
              }} animate={{
                x: -260,
                y: -180,
                scale: 0.18,
                opacity: 0,
                rotate: -15
              }} transition={{
                duration: 1.1,
                ease: [0.7, 0, 0.84, 0]
              }} className="relative w-44 mb-8 absolute" data-id="element-5448" data-mp="3jzdwt64cszny">
                    <div className="border-2 border-[#D4AF37] shadow-[0_0_40px_rgba(212,175,55,0.6)] overflow-hidden" data-id="element-5449" data-mp="xojy70hm8zg9">
                      <img src="https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png" alt="Moran Sarkar" className="w-full" data-id="element-5450" data-mp="iznq53fk7mfn" />
                    </div>
                  </motion.div>

                  {/* Sparkle trail */}
                  {Array.from({
                length: 10
              }).map((_, i) => <motion.div key={i} initial={{
                x: 0,
                y: 0,
                opacity: 0,
                scale: 0
              }} animate={{
                x: -260 + Math.random() * 50,
                y: -180 + Math.random() * 50,
                opacity: [0, 1, 0],
                scale: [0, 1, 0]
              }} transition={{
                duration: 1.3,
                delay: 0.15 + i * 0.06,
                ease: 'easeOut'
              }} className="absolute w-1.5 h-1.5 bg-[#D4AF37] rounded-full shadow-[0_0_8px_rgba(212,175,55,1)]" data-id="element-5451" data-mp="51z9y8wft3gx" data-mpi={i} />)}

                  {/* Success state */}
                  <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 1.0,
                duration: 0.6
              }} className="flex flex-col items-center" data-id="element-5452" data-mp="n4hwdg6pv1ez">
                    <div className="w-14 h-14 rounded-full border-2 border-[#D4AF37] bg-[#D4AF37]/10 flex items-center justify-center mb-5" data-id="element-5453" data-mp="16ilooa9sb9nh">
                      <Sparkles className="w-6 h-6 text-[#D4AF37]" data-id="element-5454" data-mp="11kk2vhkz4w8b" />
                    </div>
                    <p className="text-[#D4AF37]/80 text-[10px] tracking-[0.35em] uppercase mb-3" data-id="element-5455" data-mp="wrr0v1gw41zx">
                      Added to your Archive
                    </p>
                    <h3 className="font-serif text-3xl text-[#FDFBF7] mb-3" data-id="element-5456" data-mp="mum8ul0d5cb7">
                      Moran Sarkar joins your Court
                    </h3>
                    <p className="text-[#FDFBF7]/60 text-sm font-light leading-relaxed mb-2 max-w-sm" data-id="element-5457" data-mp="ishlu54f7dxj">
                      Filed under the{' '}
                      <span className="text-[#D4AF37]" data-id="element-5458" data-mp="2phjjurjt61br">
                        Royal Court Collection
                      </span>{' '}
                      · You now own this artifact{' '}
                      <span className="text-[#D4AF37] font-bold" data-id="element-5459" data-mp="2yhy6pvcf7ohd">×2</span>.
                    </p>
                    <p className="text-[#FDFBF7]/40 text-xs font-light mb-8" data-id="element-5460" data-mp="33h2echjp5e5l">
                      Your duplicate is now tradeable on the Open Market.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-3 w-full" data-id="element-5461" data-mp="26op3puag835y">
                      <button onClick={() => {
                    setPacketState('sealed');
                    setActiveTab('collections');
                  }} className="flex-1 px-6 py-3.5 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0B0F19] text-[10px] tracking-[0.3em] uppercase font-bold transition-colors flex items-center justify-center gap-2" data-id="element-5462" data-mp="2aswtzvwk1r6a">
                        <BookOpen className="w-3.5 h-3.5" data-id="element-5463" data-mp="1rzj96jmk314e" /> View in Collection
                      </button>
                      <button onClick={() => {
                    setPacketState('sealed');
                    setActiveTab('trade-inbox');
                    setTradeTab('duplicates');
                  }} className="flex-1 px-6 py-3.5 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 text-[10px] tracking-[0.3em] uppercase transition-colors flex items-center justify-center gap-2" data-id="element-5464" data-mp="2qdq6q241ejhw">
                        <Repeat className="w-3.5 h-3.5" data-id="element-5465" data-mp="2lelx5ad7ajr1" /> Post Duplicate
                      </button>
                    </div>

                    <button onClick={() => setPacketState('sealed')} className="mt-6 text-[#FDFBF7]/40 hover:text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase transition-colors" data-id="element-5466" data-mp="2gf98ck2rr2lt">
                      Replay the unboxing
                    </button>
                  </motion.div>
                </motion.div>}
            </AnimatePresence>
          </div>;
      case 'collections':
        if (selectedCollection) {
          return <div className="flex flex-col gap-8" data-id="element-5467" data-mp="3qf39x3g01whc">
              <div className="flex justify-between items-end border-b border-[#D4AF37]/20 pb-4" data-id="element-5468" data-mp="3vcd7559mrcfq">
                <div data-id="element-5469" data-mp="mx1xegzl7xku">
                  <button onClick={() => setSelectedCollection(null)} className="text-[#D4AF37]/60 hover:text-[#D4AF37] text-[10px] uppercase tracking-widest flex items-center gap-2 mb-4 transition-colors" data-id="element-5470" data-mp="mwjs6uuy690s">
                    ← Back to Collections
                  </button>
                  <h3 className="font-serif text-2xl text-[#FDFBF7]" data-id="element-5471" data-mp="i48tfvby0t9t">
                    {selectedCollection}
                  </h3>
                  <p className="text-[#D4AF37]/60 text-xs tracking-[0.2em] uppercase mt-1" data-id="element-5472" data-mp="1ame6lgux05n5">
                    Collection Details
                  </p>
                </div>
                <div className="text-right" data-id="element-5473" data-mp="3i3usld45surk">
                  <span className="font-serif text-2xl text-[#D4AF37]" data-id="element-5474" data-mp="3e4481r587avh">
                    {(() => {
                    const col = dashboardCollections.find(c => c.name === selectedCollection);
                    if (!col) return '0/0';
                    return `${col.unlockedCards.length}/${col.total}`;
                  })()}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-id="element-5475" data-mp="rk9ijfj4tqd">
                {(() => {
                const col = dashboardCollections.find(c => c.name === selectedCollection);
                if (!col) return null;
                return Array.from({
                  length: col.total
                }).map((_, i) => {
                  const card = col.unlockedCards[i];
                  const isUnlocked = !!card;
                  return <div key={i} className={`aspect-[2.5/3.5] border ${isUnlocked ? 'border-[#D4AF37]/50 bg-[#131B2B] shadow-[0_0_20px_rgba(212,175,55,0.15)]' : 'border-[#FDFBF7]/10 bg-[#0B0F19]'} relative overflow-hidden group/card`} data-id="element-5476" data-mp="vy1f853hkr5b" data-mpi={i}>
                        {isUnlocked ? <>
                            <img src={card.img} alt={card.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" data-id="element-5477" data-mp="1ex5lj63n86ta" />
                            {/* Dark gradient overlay for legibility */}
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/40 to-transparent" data-id="element-5478" data-mp="1tnwam6duorsp" />
                            {/* Caption */}
                            <div className="absolute inset-x-0 bottom-0 p-2.5" data-id="element-5479" data-mp="3upm5rvff89vx">
                              <p className="font-serif text-[11px] sm:text-xs text-[#FDFBF7] leading-tight line-clamp-2" data-id="element-5480" data-mp="3ppzq7fc4ehjy">
                                {card.name}
                              </p>
                              <p className="text-[#D4AF37]/80 text-[8px] uppercase tracking-widest mt-1" data-id="element-5481" data-mp="2k8g2513bo6b">
                                Unlocked
                              </p>
                            </div>
                            {/* Founders crown chip top-right */}
                            <div className="absolute top-2 right-2 w-5 h-5 rounded-full border border-[#D4AF37]/60 bg-[#0B0F19]/80 backdrop-blur-sm flex items-center justify-center" data-id="element-5482" data-mp="qaci47ymrfv7">
                              <Crown className="w-2.5 h-2.5 text-[#D4AF37]" data-id="element-5483" data-mp="m6wpdmpolzns" />
                            </div>
                          </> : <div className="absolute inset-0 flex flex-col items-center justify-center p-3" data-id="element-5484" data-mp="2o1oygjjsbw6h">
                            <Lock className="w-6 h-6 text-[#FDFBF7]/20 mb-2" data-id="element-5485" data-mp="2dwz4azumhwct" />
                            <p className="text-[#FDFBF7]/30 text-[10px] uppercase tracking-widest" data-id="element-5486" data-mp="2hwhccv1cicy8">
                              Locked
                            </p>
                          </div>}
                      </div>;
                });
              })()}
              </div>
            </div>;
        }
        return <div className="flex flex-col gap-8" data-id="element-5487" data-mp="1z7jepn6tvwyp">
            <div className="flex justify-between items-end border-b border-[#D4AF37]/20 pb-4" data-id="element-5488" data-mp="1z56pcoyqqkbm">
              <div data-id="element-5489" data-mp="1l9l3urrpuiny">
                <h3 className="font-serif text-2xl text-[#FDFBF7]" data-id="element-5490" data-mp="1q6lb360axrgn">
                  Your Collections
                </h3>
                <p className="text-[#D4AF37]/60 text-xs tracking-[0.2em] uppercase mt-1" data-id="element-5491" data-mp="3fkzr91x9860j">
                  The Archive
                </p>
              </div>
              <div className="text-right" data-id="element-5492" data-mp="3kijg0vrkw16q">
                <span className="font-serif text-2xl text-[#D4AF37]" data-id="element-5493" data-mp="wzx8nk0bqz6q">
                  12
                  <span className="text-[#FDFBF7]/30 text-lg" data-id="element-5494" data-mp="s02ixjsacfn9"> artifacts</span>
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-5495" data-mp="n8b0xxn73i8q">
              {dashboardCollections.map((col, i) => {
              const unlockedCount = col.unlockedCards.length;
              const pct = unlockedCount / col.total * 100;
              const isLocked = col.status === 'Locked';
              const rarityColor = col.rarity.includes('Founders') ? 'text-[#D4AF37] border-[#D4AF37]/40 bg-[#D4AF37]/10' : col.rarity === 'Treasury Rare' ? 'text-[#D4AF37]/80 border-[#D4AF37]/30 bg-[#D4AF37]/5' : 'text-[#8B0000] border-[#8B0000]/40 bg-[#8B0000]/10';
              return <div key={i} onClick={() => setSelectedCollection(col.name)} className={`relative bg-[#0B0F19] border ${col.isFlagship ? 'border-[#D4AF37]/50 shadow-[0_0_30px_rgba(212,175,55,0.12)]' : 'border-[#D4AF37]/20'} p-6 flex flex-col cursor-pointer hover:border-[#D4AF37]/60 transition-colors group overflow-hidden`} data-id="element-5496" data-mp="1uoqqna67a198" data-mpi={i}>
                    {col.isFlagship && <span className="absolute -top-px left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" data-id="element-5497" data-mp="2tmg8lih64u4m" />}
                    {col.sponsoredBy && <div className="mb-3 -mt-1 flex items-center gap-2" data-id="element-5498" data-mp="32n4qio9onems">
                        <span className="text-[#D4AF37]/50 text-[8px] tracking-[0.3em] uppercase" data-id="element-5499" data-mp="32p7n5r5cc60h">
                          Sponsored by
                        </span>
                        <span className="font-serif text-[11px] text-[#D4AF37] leading-none" data-id="element-5500" data-mp="hw44e1gbs2y8">
                          {col.sponsoredBy}
                        </span>
                      </div>}
                    <div className="flex justify-between items-start mb-3 gap-3" data-id="element-5501" data-mp="ivikkix7ek0z">
                      <div className="min-w-0 flex-1" data-id="element-5502" data-mp="evi5qx6706te">
                        <span className={`inline-block px-2 py-0.5 text-[8px] tracking-[0.25em] uppercase font-bold border ${rarityColor} mb-2`} data-id="element-5503" data-mp="sny25f4l96lc">
                          {col.rarity}
                        </span>
                        <h4 className="font-serif text-xl text-[#FDFBF7] group-hover:text-[#D4AF37] transition-colors leading-tight" data-id="element-5504" data-mp="key8qozpef">
                          {col.name}
                        </h4>
                        <p className="text-[#D4AF37]/50 text-[9px] tracking-[0.25em] uppercase mt-1" data-id="element-5505" data-mp="9ukhc2ulagjg">
                          {col.sub}
                        </p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[#D4AF37]/0 group-hover:text-[#D4AF37] transition-all transform -translate-x-2 group-hover:translate-x-0 shrink-0 mt-1" data-id="element-5506" data-mp="1le6mndmlk333" />
                    </div>

                    {/* Mini card slot strip with real artifact images for unlocked slots */}
                    <div className="flex gap-1 mb-4" data-id="element-5507" data-mp="337mavlcj6ei6">
                      {Array.from({
                    length: col.total
                  }).map((_, idx) => {
                    const card = col.unlockedCards[idx];
                    const unlocked = !!card;
                    return <div key={idx} className={`flex-1 aspect-[2.5/3.5] overflow-hidden ${unlocked ? col.isFlagship ? 'border border-[#D4AF37]/60 shadow-[0_0_8px_rgba(212,175,55,0.25)]' : 'border border-[#D4AF37]/40' : 'bg-[#131B2B] border border-[#FDFBF7]/10 flex items-center justify-center'}`} data-id="element-5508" data-mp="3lzylevdc8m3e" data-mpi={idx}>
                            {unlocked ? <img src={card.img} alt={card.name} className="w-full h-full object-cover" loading="lazy" data-id="element-5509" data-mp="9juwkx7qs84b" /> : <Lock className="w-2 h-2 text-[#FDFBF7]/25" data-id="element-5510" data-mp="9f17icv2jifq" />}
                          </div>;
                  })}
                    </div>

                    <div className="flex justify-between text-[10px] uppercase tracking-widest mb-2 mt-auto" data-id="element-5511" data-mp="h8qn49p82q8">
                      <span className="text-[#D4AF37]/80 font-mono" data-id="element-5512" data-mp="3rsilvqiayhj6">
                        {unlockedCount}/{col.total}
                      </span>
                      <span className={isLocked ? 'text-[#FDFBF7]/30' : col.status.includes('Unlock') ? 'text-[#8B0000] font-bold' : 'text-[#D4AF37]/70'} data-id="element-5513" data-mp="3hx7572nc308s">
                        {col.status}
                      </span>
                    </div>
                    <div className="w-full h-1 bg-[#131B2B] rounded-full overflow-hidden border border-[#D4AF37]/20" data-id="element-5514" data-mp="1sh4w2lkr7y5j">
                      <div className="h-full bg-gradient-to-r from-[#8B0000] to-[#D4AF37]" style={{
                    width: `${pct}%`
                  }} data-id="element-5515" data-mp="22d7v3ikb7go9" />
                    </div>
                  </div>;
            })}
            </div>
          </div>;
      case 'trade-inbox':
        return <div className="flex flex-col gap-8" data-id="element-5516" data-mp="1jf415vczto71">
            <div className="flex justify-between items-end border-b border-[#D4AF37]/20 pb-4" data-id="element-5517" data-mp="1jfwxs3i8mxbg">
              <div data-id="element-5518" data-mp="lyy74pjzbtxy">
                <h3 className="font-serif text-2xl text-[#FDFBF7]" data-id="element-5519" data-mp="lxdmbrngvpda">
                  Trade Inbox
                </h3>
                <p className="text-[#D4AF37]/60 text-xs tracking-[0.2em] uppercase mt-1" data-id="element-5520" data-mp="2sn77r1mat46p">
                  The Exchange
                </p>
              </div>
              <div className="px-3 py-1 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-[10px] uppercase tracking-widest cursor-pointer hover:bg-[#D4AF37]/20 transition-colors" data-id="element-5521" data-mp="2onq1i5z1s30h">
                New Trade
              </div>
            </div>

            <div className="flex gap-6 border-b border-[#FDFBF7]/10" data-id="element-5522" data-mp="3caoe2sk1qqt4">
              <button onClick={() => setTradeTab('incoming')} className={`pb-3 text-[10px] uppercase tracking-widest transition-colors relative ${tradeTab === 'incoming' ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/40 hover:text-[#FDFBF7]/80'}`} data-id="element-5523" data-mp="37cd50k1kskby">
                Incoming Requests
                <span className="ml-2 bg-[#8B0000] text-[#FDFBF7] px-1.5 py-0.5 rounded-full text-[8px]" data-id="element-5524" data-mp="1qxleydrwjp5t">
                  2
                </span>
                {tradeTab === 'incoming' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4AF37]" data-id="element-5525" data-mp="1ghgqobh201u" />}
              </button>
              <button onClick={() => setTradeTab('market')} className={`pb-3 text-[10px] uppercase tracking-widest transition-colors relative ${tradeTab === 'market' ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/40 hover:text-[#FDFBF7]/80'}`} data-id="element-5526" data-mp="6ercb013z2nz">
                Market
                {tradeTab === 'market' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4AF37]" data-id="element-5527" data-mp="3juyy4p5np9zd" />}
              </button>
              <button onClick={() => setTradeTab('duplicates')} className={`pb-3 text-[10px] uppercase tracking-widest transition-colors relative ${tradeTab === 'duplicates' ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/40 hover:text-[#FDFBF7]/80'}`} data-id="element-5528" data-mp="35vzejvx03la2">
                My Duplicates
                {tradeTab === 'duplicates' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4AF37]" data-id="element-5529" data-mp="2h2cxj914i3vq" />}
              </button>
            </div>

            <div className="space-y-4" data-id="element-5530" data-mp="2c2ichm4148il">
              {tradeTab === 'incoming' && <>
                  {(() => {
                const cardImg: Record<string, string> = {
                  'Jamrud Fort': 'https://cdn.magicpatterns.com/uploads/qPy5nBUtnmTQ5b2tqAUf9h/Jamrud_Fort.png',
                  'Moran Sarkar': 'https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png',
                  'Akali Phula Singh': 'https://cdn.magicpatterns.com/uploads/h1jugirBbdMAbGN9UBoKma/Akali_Phula_Singh.png',
                  'Hari Singh Nalwa': 'https://cdn.magicpatterns.com/uploads/ekyaLxoHxw9b6NSM5wgnik/Hari_Singh_Nalwa.png',
                  'Maharaja Ranjit Singh': 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png',
                  'Harmandir Sahib': 'https://cdn.magicpatterns.com/uploads/st6W3BtxYpFsMW4u23CAxv/Harmandir_Sahib_Amritsar.png',
                  'Valley of Kashmir': 'https://cdn.magicpatterns.com/uploads/ac4oQu8QYLHUDnoQhYPvFC/Valley_of_Kashmir.png',
                  'Jind Kaur': 'https://cdn.magicpatterns.com/uploads/nVFG4hPLbQSgSkuEzm99Kr/Jind_Kaur.png'
                };
                const trades = [{
                  user: '@arjun',
                  rank: 'Sardar',
                  offer: 'Jamrud Fort',
                  seek: 'Moran Sarkar',
                  status: 'pending'
                }, {
                  user: '@simran',
                  rank: 'Courtier',
                  offer: 'Akali Phula Singh',
                  seek: 'Hari Singh Nalwa',
                  status: 'pending'
                }, {
                  user: '@karan',
                  rank: 'Noble',
                  offer: 'Maharaja Ranjit Singh',
                  seek: 'Harmandir Sahib',
                  status: 'completed'
                }];
                return trades.map((trade, i) => <div key={i} className={`bg-[#0B0F19] border ${trade.status === 'pending' ? 'border-[#D4AF37]/30' : 'border-[#FDFBF7]/10 opacity-60'} p-5 flex flex-col lg:flex-row lg:items-center gap-5`} data-id="element-5531" data-mp="u5pxlygldfx7" data-mpi={i}>
                        <div className="flex items-center gap-3 lg:w-44 shrink-0" data-id="element-5532" data-mp="141szr4bg1ek5">
                          <div className="w-9 h-9 rounded-full bg-[#131B2B] border border-[#D4AF37]/30 flex items-center justify-center text-[10px] text-[#D4AF37]" data-id="element-5533" data-mp="gcgrc6nxxaiw">
                            {trade.user.substring(1, 3).toUpperCase()}
                          </div>
                          <div className="min-w-0" data-id="element-5534" data-mp="2rtk36jg59wp9">
                            <p className="text-sm text-[#D4AF37] font-medium truncate" data-id="element-5535" data-mp="2rv3hw5m5jzox">
                              {trade.user}
                            </p>
                            <p className="text-[#FDFBF7]/40 text-[9px] uppercase tracking-widest" data-id="element-5536" data-mp="2i48uuz130w2f">
                              {trade.rank}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-5537" data-mp="3aps97pgzeblm">
                          <div className="flex-1 flex items-center gap-3 min-w-0" data-id="element-5538" data-mp="2ws3ochmwz4gk">
                            <img src={cardImg[trade.offer]} alt={trade.offer} className="w-12 h-12 object-cover border border-[#D4AF37]/30 shrink-0" data-id="element-5539" data-mp="30sdrznn8his9" />
                            <div className="min-w-0" data-id="element-5540" data-mp="ofuf4i9aate7">
                              <p className="text-[#D4AF37]/60 text-[9px] uppercase tracking-widest" data-id="element-5541" data-mp="oh5hqeqztv3q">
                                Offers
                              </p>
                              <p className="font-serif text-sm text-[#FDFBF7] truncate" data-id="element-5542" data-mp="xg92ro9q0kkv">
                                {trade.offer}
                              </p>
                            </div>
                          </div>
                          <ArrowRight className="w-4 h-4 text-[#D4AF37]/40 shrink-0" data-id="element-5543" data-mp="5mqjircia6gy" />
                          <div className="flex-1 flex items-center gap-3 min-w-0" data-id="element-5544" data-mp="9o2bv4mrl5su">
                            <img src={cardImg[trade.seek]} alt={trade.seek} className="w-12 h-12 object-cover border border-[#D4AF37]/30 shrink-0" data-id="element-5545" data-mp="enoll43dw3ww" />
                            <div className="min-w-0" data-id="element-5546" data-mp="3n49p45d8y4sm">
                              <p className="text-[#D4AF37]/60 text-[9px] uppercase tracking-widest" data-id="element-5547" data-mp="3n2ou6en0sju8">
                                Wants
                              </p>
                              <p className="font-serif text-sm text-[#FDFBF7] truncate" data-id="element-5548" data-mp="3amyx1sb888sm">
                                {trade.seek}
                              </p>
                            </div>
                          </div>
                        </div>
                        {trade.status === 'pending' ? <div className="flex gap-2 w-full lg:w-auto shrink-0" data-id="element-5549" data-mp="2wor6q38p45qf">
                            <button className="flex-1 lg:flex-none px-5 py-2 bg-[#D4AF37]/10 text-[#D4AF37] text-[10px] uppercase tracking-widest border border-[#D4AF37]/30 hover:bg-[#D4AF37]/20 transition-colors" data-id="element-5550" data-mp="2rud3jenskub7">
                              Accept
                            </button>
                            <button className="flex-1 lg:flex-none px-4 py-2 text-[#FDFBF7]/60 text-[10px] uppercase tracking-widest border border-[#FDFBF7]/20 hover:text-[#FDFBF7] hover:border-[#D4AF37] transition-colors" data-id="element-5551" data-mp="2duvfx2m8fn3f">
                              Counter
                            </button>
                            <button className="flex-1 lg:flex-none px-4 py-2 text-[#FDFBF7]/40 text-[10px] uppercase tracking-widest hover:text-[#FDFBF7]/80 transition-colors" data-id="element-5552" data-mp="23buqpr7zyjlz">
                              Decline
                            </button>
                          </div> : <span className="text-[10px] uppercase tracking-widest text-[#FDFBF7]/30 border border-[#FDFBF7]/10 px-4 py-1.5 shrink-0" data-id="element-5553" data-mp="2i0r3u2owumtg">
                            Completed
                          </span>}
                      </div>);
              })()}
                </>}

              {tradeTab === 'market' && <>
                  <div className="flex flex-wrap gap-2 mb-2" data-id="element-5554" data-mp="30wqqqz317066">
                    {['All', "Founders' Edition", 'Epic', 'Treasury Rare'].map((chip, i) => <span key={chip} className={`px-3 py-1 text-[9px] uppercase tracking-widest border cursor-pointer transition-colors ${i === 0 ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/40' : 'text-[#FDFBF7]/40 border-[#FDFBF7]/10 hover:border-[#D4AF37]/30 hover:text-[#D4AF37]/70'}`} data-id="element-5555" data-mp="u8qcr6mn09lt" data-mpi={i}>
                          {chip}
                        </span>)}
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-5556" data-mp="6eozd48rgunl">
                    {[{
                  user: '@neha',
                  offer: 'Valley of Kashmir',
                  img: 'https://cdn.magicpatterns.com/uploads/ac4oQu8QYLHUDnoQhYPvFC/Valley_of_Kashmir.png',
                  rarity: 'Epic',
                  seeking: 'Any Founders Edition',
                  time: '2m ago'
                }, {
                  user: '@vikram',
                  offer: 'Jind Kaur',
                  img: 'https://cdn.magicpatterns.com/uploads/nVFG4hPLbQSgSkuEzm99Kr/Jind_Kaur.png',
                  rarity: "Founders' Edition",
                  seeking: 'Maharaja Ranjit Singh',
                  time: '1h ago'
                }, {
                  user: '@prabh',
                  offer: 'Harmandir Sahib',
                  img: 'https://cdn.magicpatterns.com/uploads/st6W3BtxYpFsMW4u23CAxv/Harmandir_Sahib_Amritsar.png',
                  rarity: 'Treasury Rare',
                  seeking: 'Hari Singh Nalwa',
                  time: '5h ago'
                }, {
                  user: '@tara',
                  offer: 'Akali Phula Singh',
                  img: 'https://cdn.magicpatterns.com/uploads/h1jugirBbdMAbGN9UBoKma/Akali_Phula_Singh.png',
                  rarity: 'Epic',
                  seeking: 'Open to offers',
                  time: '3h ago'
                }].map((trade, i) => <div key={i} className="bg-[#0B0F19] border border-[#D4AF37]/20 p-4 flex gap-4 hover:border-[#D4AF37]/40 transition-colors" data-id="element-5557" data-mp="184vmqbaj7e2t" data-mpi={i}>
                        <img src={trade.img} alt={trade.offer} className="w-20 h-24 object-cover border border-[#D4AF37]/30 shrink-0" data-id="element-5558" data-mp="1vsvb54kdese8" />
                        <div className="flex-1 flex flex-col min-w-0" data-id="element-5559" data-mp="1m20ohz37pqs9">
                          <p className="text-[#D4AF37]/60 text-[9px] uppercase tracking-widest" data-id="element-5560" data-mp="2jihd1zs4nlpx">
                            {trade.rarity}
                          </p>
                          <h4 className="font-serif text-base text-[#FDFBF7] truncate" data-id="element-5561" data-mp="ctxgqpi684r0">
                            {trade.offer}
                          </h4>
                          <p className="text-xs text-[#FDFBF7]/50 mt-1 truncate" data-id="element-5562" data-mp="gv0yjhjs4auv">
                            <span className="text-[#D4AF37]" data-id="element-5563" data-mp="332rdc9mop1c">{trade.user}</span>{' '}
                            · {trade.time}
                          </p>
                          <p className="text-[10px] text-[#FDFBF7]/70 mt-2" data-id="element-5564" data-mp="3pbc7ehxnjgek">
                            <span className="text-[#D4AF37]/60 uppercase tracking-widest text-[9px]" data-id="element-5565" data-mp="3pdgmlw1041u5">
                              Wants:
                            </span>{' '}
                            {trade.seeking}
                          </p>
                          <div className="flex gap-2 mt-auto pt-3" data-id="element-5566" data-mp="3l8z4s2e7bym0">
                            <button className="flex-1 py-1.5 bg-[#D4AF37] text-[#0B0F19] text-[9px] uppercase tracking-widest font-bold hover:bg-[#E5C158] transition-colors" data-id="element-5567" data-mp="1ejaxtos6kkia">
                              Make Offer
                            </button>
                            <button className="px-3 py-1.5 border border-[#D4AF37]/30 text-[#D4AF37]/70 hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors" data-id="element-5568" data-mp="1tmu8zavu8tgv">
                              <Heart className="w-3 h-3" data-id="element-5569" data-mp="22ew6p1eivvng" />
                            </button>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </>}

              {tradeTab === 'duplicates' && <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-5570" data-mp="27d7gxd8qc867">
                    {[{
                  name: 'Moran Sarkar',
                  count: 2,
                  collection: 'Royal Court',
                  rarity: 'Treasury Rare',
                  img: 'https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png'
                }, {
                  name: 'Jamrud Fort',
                  count: 3,
                  collection: 'Frontier / Martial',
                  rarity: 'Epic',
                  img: 'https://cdn.magicpatterns.com/uploads/qPy5nBUtnmTQ5b2tqAUf9h/Jamrud_Fort.png'
                }, {
                  name: 'Maharaja Ranjit Singh',
                  count: 2,
                  collection: 'Royal Court',
                  rarity: "Founders' Edition",
                  img: 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png'
                }].map((dup, i) => <div key={i} className="bg-[#0B0F19] border border-[#D4AF37]/20 flex flex-col relative overflow-hidden hover:border-[#D4AF37]/40 transition-colors" data-id="element-5571" data-mp="16ksehgcpo7zq" data-mpi={i}>
                        <div className="relative aspect-[3/4]" data-id="element-5572" data-mp="2i0z5hqivcpj0">
                          <img src={dup.img} alt={dup.name} className="w-full h-full object-cover" data-id="element-5573" data-mp="2s1i300hlhj3z" />
                          <div className="absolute top-2 right-2 px-2 py-1 bg-[#8B0000] border border-[#D4AF37] text-[#FDFBF7] text-[10px] uppercase tracking-widest font-bold" data-id="element-5574" data-mp="2ibgp21iyhh2s">
                            ×{dup.count}
                          </div>
                        </div>
                        <div className="p-3 flex flex-col flex-1" data-id="element-5575" data-mp="24c76iv7a2avn">
                          <p className="text-[#D4AF37]/60 text-[8px] uppercase tracking-widest" data-id="element-5576" data-mp="1zdvwajd2lycw">
                            {dup.rarity}
                          </p>
                          <h4 className="font-serif text-sm text-[#FDFBF7] leading-tight mb-1 truncate" data-id="element-5577" data-mp="1ven7lsww0qo7">
                            {dup.name}
                          </h4>
                          <p className="text-[#FDFBF7]/40 text-[8px] uppercase tracking-widest mb-3" data-id="element-5578" data-mp="3pr38sfattem7">
                            {dup.collection}
                          </p>
                          <div className="space-y-1.5 mt-auto" data-id="element-5579" data-mp="1sajavmd9e21a">
                            <button className="w-full py-1.5 bg-[#D4AF37] text-[#0B0F19] text-[9px] uppercase tracking-widest font-bold hover:bg-[#E5C158] transition-colors" data-id="element-5580" data-mp="1nk2t5q2enu94">
                              Post to Market
                            </button>
                            <button className="w-full py-1.5 border border-[#D4AF37]/30 text-[#D4AF37]/80 text-[9px] uppercase tracking-widest hover:bg-[#D4AF37]/10 transition-colors" data-id="element-5581" data-mp="1dngbtb9aflbu">
                              Direct Trade
                            </button>
                          </div>
                        </div>
                      </div>)}
                  </div>
                  <div className="mt-2 p-3 border border-[#D4AF37]/10 bg-[#0B0F19]/50 text-[#FDFBF7]/50 text-[10px] font-light" data-id="element-5582" data-mp="2yyptrgt70kcw">
                    Duplicates carry no penalty — they are tradeable assets.
                    Open Market listings carry a 1 XP Darbar fee per successful
                    trade.
                  </div>
                </>}
            </div>
          </div>;
      case 'restoration':
        return <div className="flex flex-col gap-8 h-full" data-id="element-5583" data-mp="3g8ajxx6hemq9">
            <div data-id="element-5584" data-mp="3g7qzo9tczm92">
              <h3 className="font-serif text-2xl text-[#FDFBF7] mb-2" data-id="element-5585" data-mp="3gad6g78fdxq8">
                Restore Punjab
              </h3>
              <p className="text-[#FDFBF7]/50 text-sm font-light max-w-2xl" data-id="element-5586" data-mp="2njmr9gjekv2o">
                Your collection holds story power. Contribute your artifacts to
                illuminate the lost regions of the Sikh Empire. As you restore
                the digital memory map, you unlock exclusive access for yourself
                and your community.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-5587" data-mp="buc2eg6lzcjt">
              <div className="bg-[#0B0F19] border border-[#D4AF37]/20 p-4 rounded-lg" data-id="element-5588" data-mp="bsiwx47qeyde">
                <p className="text-[#D4AF37]/60 text-[10px] uppercase tracking-widest mb-1" data-id="element-5589" data-mp="28tezhmkx0dhe">
                  Cards Contributed
                </p>
                <p className="text-2xl font-serif text-[#FDFBF7]" data-id="element-5590" data-mp="2hvefcbk71bir">12</p>
              </div>
              <div className="bg-[#0B0F19] border border-[#D4AF37]/20 p-4 rounded-lg" data-id="element-5591" data-mp="2cwjy3e1vjv0r">
                <p className="text-[#D4AF37]/60 text-[10px] uppercase tracking-widest mb-1" data-id="element-5592" data-mp="2cuqts5eqhmvx">
                  Zones Helped
                </p>
                <p className="text-2xl font-serif text-[#FDFBF7]" data-id="element-5593" data-mp="1l6f7gxniubr3">3</p>
              </div>
              <div className="bg-[#0B0F19] border border-[#D4AF37]/20 p-4 rounded-lg" data-id="element-5594" data-mp="1yx25enlgjxm4">
                <p className="text-[#D4AF37]/60 text-[10px] uppercase tracking-widest mb-1" data-id="element-5595" data-mp="1u5k581r9e127">
                  School Contribution
                </p>
                <p className="text-2xl font-serif text-[#FDFBF7]" data-id="element-5596" data-mp="16eoihj1ntw1t">450 pts</p>
              </div>
              <div className="bg-[#0B0F19] border border-[#D4AF37]/20 p-4 rounded-lg" data-id="element-5597" data-mp="11h4rrjnrzqsz">
                <p className="text-[#D4AF37]/60 text-[10px] uppercase tracking-widest mb-1" data-id="element-5598" data-mp="3d3yllb6xikgo">
                  Next Unlock
                </p>
                <p className="text-sm font-serif text-[#D4AF37] mt-1" data-id="element-5599" data-mp="34421znzecbsh">
                  Treasury Night Invite
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/20 p-6 rounded-xl flex flex-col sm:flex-row items-center justify-between gap-6" data-id="element-5600" data-mp="393ena4amdasy">
              <div data-id="element-5601" data-mp="4nnby8l8favx">
                <h4 className="font-serif text-lg text-[#FDFBF7] mb-1" data-id="element-5602" data-mp="4o5bpf73s78l">
                  Treasury Guide Recommendation
                </h4>
                <p className="text-sm text-[#FDFBF7]/60" data-id="element-5603" data-mp="3rt4rtj46pay2">
                  You have 2 cards that can restore{' '}
                  <span className="text-[#D4AF37]" data-id="element-5604" data-mp="2bf4rw85c9lat">Sacred Punjab</span>. Your
                  school is 8% away from Treasury Night Invite eligibility.
                </p>
              </div>
              <Link to="/restore-punjab" className="bg-[#D4AF37] text-black px-6 py-2 rounded-sm text-sm font-medium hover:bg-[#FDFBF7] transition-colors whitespace-nowrap" data-id="element-5605" data-mp="2voe4id6hs09u">
                Go to Map
              </Link>
            </div>
          </div>;
      case 'live-events':
        {
          const nights = [{
            month: 'JUL',
            day: '18',
            time: '7:00 PM',
            title: 'The Court Awakens — Opening Preview',
            loc: 'Illuminarium · Toronto',
            req: 'All Ranks'
          }, {
            month: 'AUG',
            day: '15',
            time: '7:00 PM',
            title: 'The Chamber of Scent & Song',
            loc: 'Illuminarium · Toronto',
            req: 'All Ranks'
          }, {
            month: 'SEP',
            day: '19',
            time: '7:00 PM',
            title: 'The Royal Regalia Reveal',
            loc: 'Illuminarium · Toronto',
            req: 'Courtier+'
          }, {
            month: 'OCT',
            day: '17',
            time: '7:00 PM',
            title: "Founders' Feast",
            loc: 'Illuminarium · Toronto',
            req: 'Sardar+'
          }];
          const sessions = [{
            month: 'JUL',
            day: '25',
            time: '8:00 PM',
            title: 'The AI Artists Behind the Faces',
            loc: 'Virtual · Court Channel',
            req: 'All Ranks'
          }, {
            month: 'AUG',
            day: '29',
            time: '8:00 PM',
            title: 'Building the Darbar — Toronto Immersive Team',
            loc: 'Virtual · Court Channel',
            req: 'Courtier+'
          }, {
            month: 'SEP',
            day: '26',
            time: '8:00 PM',
            title: 'The Sound of the Kingdom — Composing the Score',
            loc: 'Virtual · Court Channel',
            req: 'All Ranks'
          }, {
            month: 'OCT',
            day: '24',
            time: '8:00 PM',
            title: 'Taste of Lahore — The Royal Culinary Reveal',
            loc: 'Virtual · Court Channel',
            req: 'Sardar+'
          }];
          const renderEventCard = (evt: {
            month: string;
            day: string;
            time: string;
            title: string;
            loc: string;
            req: string;
          }, i: number, isNights: boolean) => {
            const key = `${isNights ? 'nights' : 'sessions'}-${i}`;
            const codeValue = eventCodes[key] ?? '';
            const isRedeemed = !!redeemedEvents[key];
            return <div key={key} className={`bg-[#0B0F19] border p-6 flex flex-col relative overflow-hidden transition-colors ${isNights ? 'border-[#D4AF37]/25 hover:border-[#D4AF37]/50' : 'border-[#D4AF37]/20 hover:border-[#D4AF37]/45'}`} data-id="element-5606" data-mp="y39g7jileq0t">
              {/* Refined gold top accent — solid for Nights, hairline for Sessions */}
              <div className={`absolute top-0 left-0 right-0 ${isNights ? 'h-[2px] bg-[#D4AF37]/70' : 'h-px bg-[#D4AF37]/40'}`} data-id="element-5607" data-mp="1vou2ibmdifb0" />
              {/* Subtle in-person glow for Nights */}
              {isNights && <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(212,175,55,0.06),_transparent_60%)] pointer-events-none" data-id="element-5608" data-mp="1mlt1uqga1geq" />}
              <div className="flex justify-between items-start mb-6 relative z-10" data-id="element-5609" data-mp="1cqhvowixphil">
                <div className="flex flex-col" data-id="element-5610" data-mp="yyl4qvt05dns">
                  <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#D4AF37]" data-id="element-5611" data-mp="yzw9oyy6orgh">
                    {evt.month}
                  </span>
                  <span className="font-serif text-3xl text-[#FDFBF7] leading-none mt-1" data-id="element-5612" data-mp="ozlwvfg29m1u">
                    {evt.day}
                  </span>
                  <span className="text-[#D4AF37]/60 text-[10px] tracking-widest uppercase mt-2" data-id="element-5613" data-mp="u11h55aezoj6">
                    {evt.time}
                  </span>
                </div>
                <span className="px-2 py-1 text-[8px] uppercase tracking-widest border border-[#D4AF37]/30 bg-[#D4AF37]/5 text-[#D4AF37]/80" data-id="element-5614" data-mp="14j6nr03vdszi">
                  {isNights ? <span className="inline-flex items-center gap-1.5" data-id="element-5615" data-mp="vi02yeu6tw0g">
                      <span className="w-1 h-1 rounded-full bg-[#D4AF37]/80" data-id="element-5616" data-mp="qi42uqom2oq6" />
                      {evt.req}
                    </span> : evt.req}
                </span>
              </div>
              <h4 className="font-serif text-lg text-[#FDFBF7] mb-4 leading-snug" data-id="element-5617" data-mp="gt3wunboyt2h">
                {evt.title}
              </h4>
              <p className="text-[#FDFBF7]/50 text-xs font-light flex items-center gap-2 mb-6" data-id="element-5618" data-mp="cow84q58hd49">
                {isNights ? <MapPin className="w-4 h-4 text-[#D4AF37]/60" data-id="element-5619" data-mp="2rzwwcnyacw0" /> : <Video className="w-4 h-4 text-[#D4AF37]/60" data-id="element-5620" data-mp="2efv9kuud2xbz" />}
                {evt.loc}
              </p>

              <button className="w-full py-3 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 text-[10px] uppercase tracking-widest flex items-center justify-center gap-2 transition-colors mt-auto" data-id="element-5621" data-mp="2je6ldb47vhwu">
                <Plus className="w-3 h-3" data-id="element-5622" data-mp="2secwiqcg05su" /> Add to Calendar
              </button>

              <div className="mt-4 pt-4 border-t border-dashed border-[#D4AF37]/15" data-id="element-5623" data-mp="1vq6mtp0nyn7p">
                {isRedeemed ? <div className="flex items-center justify-between gap-3 px-3 py-2.5 border border-[#D4AF37]/40 bg-[#D4AF37]/10" data-id="element-5624" data-mp="20p9s9akk2vg7">
                    <span className="flex items-center gap-2 text-[#D4AF37] text-[10px] tracking-widest uppercase font-bold" data-id="element-5625" data-mp="24o8sihvce41b">
                      <Check className="w-3.5 h-3.5" data-id="element-5626" data-mp="1cz5qz9uub7sd" /> Code Redeemed
                    </span>
                    <span className="text-[#D4AF37] text-[10px] font-mono font-bold" data-id="element-5627" data-mp="1h094vqtbutqy">
                      +200 XP
                    </span>
                  </div> : <>
                    <div className="flex items-center justify-between mb-2" data-id="element-5628" data-mp="vq6lyxln43sh">
                      <p className="text-[#D4AF37]/70 text-[9px] tracking-[0.25em] uppercase font-bold" data-id="element-5629" data-mp="hpvtjq4omh57">
                        Code provided at event
                      </p>
                      <p className="text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase font-bold" data-id="element-5630" data-mp="mr31bp6trgbv">
                        +200 XP
                      </p>
                    </div>
                    <div className="flex gap-2" data-id="element-5631" data-mp="cs3ri3abpena">
                      <input type="text" value={codeValue} onChange={e => setEventCodes(prev => ({
                      ...prev,
                      [key]: e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '')
                    }))} placeholder="ENTER CODE" maxLength={12} className="flex-1 min-w-0 px-3 py-2.5 bg-[#070A12] border border-[#D4AF37]/15 text-[#D4AF37] text-[11px] tracking-[0.25em] uppercase placeholder:text-[#FDFBF7]/20 font-mono focus:outline-none focus:border-[#D4AF37]/50 transition-colors" data-id="element-5632" data-mp="3v5bp8jyejixp" />
                      <button type="button" disabled={codeValue.trim().length < 4} onClick={() => setRedeemedEvents(prev => ({
                      ...prev,
                      [key]: true
                    }))} className="shrink-0 px-4 py-2.5 bg-[#D4AF37] hover:bg-[#E5C158] disabled:bg-[#D4AF37]/30 disabled:cursor-not-allowed text-[#0B0F19] text-[10px] tracking-[0.25em] uppercase font-bold transition-colors" data-id="element-5633" data-mp="3phpz2udim6sm">
                        Redeem
                      </button>
                    </div>
                    <p className="text-[#FDFBF7]/35 text-[10px] mt-2 leading-snug" data-id="element-5634" data-mp="k5m614sjlwt0">
                      {isNights ? 'Your code is announced during the live experience at the Illuminarium.' : 'Your code is shared at the end of the virtual session.'}
                    </p>
                  </>}
              </div>
            </div>;
          };
          return <div className="flex flex-col gap-10" data-id="element-5635" data-mp="12t91gh99jd3j">
            <div className="flex justify-between items-end border-b border-[#D4AF37]/20 pb-4" data-id="element-5636" data-mp="17tnbu5yc6z4q">
              <div data-id="element-5637" data-mp="1ctrsbvz8vtuy">
                <h3 className="font-serif text-2xl text-[#FDFBF7]" data-id="element-5638" data-mp="1cs6y67guecxm">
                  Live Events
                </h3>
                <p className="text-[#D4AF37]/60 text-xs tracking-[0.2em] uppercase mt-1" data-id="element-5639" data-mp="39n32h2ufcdzp">
                  In-person & Virtual · The Court Calendar
                </p>
              </div>
            </div>

            {/* Treasury Nights group */}
            <div className="flex flex-col gap-5" data-id="element-5640" data-mp="32ana5jkcfviz">
              <div className="flex items-end justify-between gap-4 border-l-2 border-[#8B0000] pl-4" data-id="element-5641" data-mp="328srkbwjofi2">
                <div data-id="element-5642" data-mp="2xbk53z4d5pb0">
                  <h4 className="font-serif text-xl text-[#FDFBF7]" data-id="element-5643" data-mp="2x8xwrx9nf5rq">
                    Treasury Nights
                  </h4>
                  <p className="text-[#8B0000]/80 text-[10px] tracking-[0.3em] uppercase mt-1" data-id="element-5644" data-mp="3q0hns3chlo4a">
                    In Person · Illuminarium Toronto
                  </p>
                </div>
                <span className="text-[#FDFBF7]/30 text-[10px] tracking-widest uppercase shrink-0" data-id="element-5645" data-mp="3g1icse491ge4">
                  {nights.length} Events
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-5646" data-mp="24mllexklef3l">
                {nights.map((e, i) => renderEventCard(e, i, true))}
              </div>
            </div>

            {/* Court Sessions group */}
            <div className="flex flex-col gap-5" data-id="element-5647" data-mp="3ohzfumkdmbdb">
              <div className="flex items-end justify-between gap-4 border-l-2 border-[#D4AF37] pl-4" data-id="element-5648" data-mp="3ogeo14pg56j5">
                <div data-id="element-5649" data-mp="3t9r0yhkrjow2">
                  <h4 className="font-serif text-xl text-[#FDFBF7]" data-id="element-5650" data-mp="3ta91hqegkp9s">
                    Court Sessions
                  </h4>
                  <p className="text-[#D4AF37]/80 text-[10px] tracking-[0.3em] uppercase mt-1" data-id="element-5651" data-mp="3ji3eyw6z701z">
                    Virtual · Behind the Scenes
                  </p>
                </div>
                <span className="text-[#FDFBF7]/30 text-[10px] tracking-widest uppercase shrink-0" data-id="element-5652" data-mp="g1mher0eabi8">
                  {sessions.length} Sessions
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-5653" data-mp="63qakbehq2px">
                {sessions.map((e, i) => renderEventCard(e, i, false))}
              </div>
            </div>
          </div>;
        }
      case 'my-misl':
        {
          const misl = {
            name: 'Sukerchakia',
            tagline: 'The Founding House · Gujranwala',
            category: 'Historic Prestige',
            banner: 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png',
            motto: 'Akal Sahai — the Timeless One is our help.',
            members: '1,520',
            xp: '480,210',
            rank: '#1 · Historic Prestige',
            founded: '1748',
            region: 'Gujranwala · Punjab',
            history: 'Founded in 1748, the Sukerchakia Misl rose to dominate the western Punjab and ultimately gave the Khalsa Confederacy its most consequential ruler — Maharaja Ranjit Singh — whose unification of the Misls in 1799 founded the Sikh Empire. To stand under this Misl is to stand at the source of sovereignty.',
            misldar: {
              handle: '@ranjit_s',
              name: 'Ranjit S.',
              xp: '12,400',
              artifacts: 47,
              joined: 'Mar 2024'
            },
            you: {
              rank: 14,
              xp: '2,840',
              artifacts: 12,
              contribution: '+520 this week'
            },
            leaderboard: [{
              rank: 1,
              handle: '@ranjit_s',
              name: 'Ranjit S.',
              xp: '12,400',
              isMisldar: true
            }, {
              rank: 2,
              handle: '@harjeet_k',
              name: 'Harjeet K.',
              xp: '9,820'
            }, {
              rank: 3,
              handle: '@arjan_s',
              name: 'Arjan S.',
              xp: '8,150'
            }, {
              rank: 4,
              handle: '@simran_k',
              name: 'Simran K.',
              xp: '7,640'
            }, {
              rank: 5,
              handle: '@dilraj_s',
              name: 'Dilraj S.',
              xp: '6,910'
            }, {
              rank: 14,
              handle: '@harnoor_s',
              name: 'Harnoor S. (you)',
              xp: '2,840',
              isYou: true
            }],
            activity: [{
              who: 'Ranjit S.',
              what: 'unlocked',
              target: 'Maharaja Ranjit Singh',
              when: '2h ago',
              tone: 'gold'
            }, {
              who: 'Simran K.',
              what: 'traded',
              target: 'Royal Seal of Ranjit Singh',
              when: '6h ago',
              tone: 'neutral'
            }, {
              who: 'Harjeet K.',
              what: 'attended',
              target: 'The Court Awakens — Opening Preview',
              when: '1d ago',
              tone: 'red'
            }, {
              who: 'Arjan S.',
              what: 'completed',
              target: "Founders' Seals (1/6)",
              when: '2d ago',
              tone: 'gold'
            }]
          };
          // Create-your-own-Misl flow inside the My Clan tab
          if (mislView === 'create') {
            return <CreateMislPanel embedded onClose={() => setMislView('home')} data-id="element-5654" data-mp="12xg8xtxybjxc" />;
          }
          return <div className="flex flex-col gap-8" data-id="element-5655" data-mp="17uorhvn0hpz4">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 border-b border-[#D4AF37]/20 pb-4" data-id="element-5656" data-mp="t5amt0udrwq2">
              <div data-id="element-5657" data-mp="y3lwnbkoe78a">
                <h3 className="font-serif text-2xl text-[#FDFBF7]" data-id="element-5658" data-mp="y32ebtr45gtq">
                  My Clan{' '}
                  <span className="text-[#FDFBF7]/40 text-lg" data-id="element-5659" data-mp="k805oyuvtdd8">(Misl)</span>
                </h3>
                <p className="text-[#D4AF37]/60 text-xs tracking-[0.2em] uppercase mt-1" data-id="element-5660" data-mp="p2dycti5t6e7">
                  Your House · Your Sangat
                </p>
              </div>
              <div className="flex items-center gap-2" data-id="element-5661" data-mp="f72pgrtfmted">
                <button onClick={() => setMislView('create')} className="flex items-center gap-1.5 bg-[#D4AF37] text-[#0B0F19] hover:bg-[#E5C158] text-[10px] uppercase tracking-widest px-3 py-2 transition-colors font-bold shadow-[0_0_20px_rgba(212,175,55,0.2)]" data-id="element-5662" data-mp="24kza5tbjqxre">
                  <Plus className="w-3 h-3" data-id="element-5663" data-mp="3p4ks17bmr5dc" />
                  Create Your Own Misl
                </button>
                <button className="text-[#D4AF37] hover:text-[#E5C158] text-[10px] uppercase tracking-widest border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 px-3 py-2 transition-colors" data-id="element-5664" data-mp="7nth91wnx0qw">
                  Switch Clan
                </button>
              </div>
            </div>

            {/* Founding Misl status — appears if user has created their own */}
            <FoundingMislStatus onManage={() => setMislView('create')} data-id="element-5665" data-mp="qlnn572huw52" />

            {/* Banner / hero */}
            <div className="relative overflow-hidden border border-[#D4AF37]/40 bg-[#0B0F19]" data-id="element-5666" data-mp="lmb46xequ97r">
              <div className="absolute inset-0" data-id="element-5667" data-mp="2scqyxzo4egr7">
                <img src={misl.banner} alt={misl.name} className="w-full h-full object-cover opacity-30" data-id="element-5668" data-mp="2sdixnjd0kulp" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F19] via-[#0B0F19]/85 to-[#0B0F19]/20" data-id="element-5669" data-mp="3ofabgt73ic49" />
              </div>
              <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" data-id="element-5670" data-mp="3kf0359vtz9m8" />
              <div className="relative p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5" data-id="element-5671" data-mp="3td2fgll0loot">
                <div className="w-14 h-14 shrink-0 border border-[#D4AF37] bg-[#0B0F19] flex items-center justify-center shadow-[0_0_20px_rgba(212,175,55,0.3)]" data-id="element-5672" data-mp="b7gy4urzqnuu">
                  <Shield className="w-7 h-7 text-[#D4AF37]" data-id="element-5673" data-mp="74au7tscp8rj" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-5674" data-mp="kx0i63rfwnra">
                  <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase mb-1.5" data-id="element-5675" data-mp="fy76mgvpr6tr">
                    {misl.category}
                  </p>
                  <h4 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] leading-tight" data-id="element-5676" data-mp="utvhupzg8u75">
                    {misl.name} Misl
                  </h4>
                  <p className="text-[#FDFBF7]/55 text-sm mt-2 italic" data-id="element-5677" data-mp="19iexlsu12bba">
                    {misl.tagline}
                  </p>
                </div>
              </div>
              <div className="relative grid grid-cols-2 sm:grid-cols-4 border-t border-[#D4AF37]/20" data-id="element-5678" data-mp="zkhbbavqaolb">
                {[{
                  label: 'Members',
                  value: misl.members,
                  icon: Users
                }, {
                  label: 'Clan XP',
                  value: misl.xp,
                  icon: Sparkles
                }, {
                  label: 'Global Rank',
                  value: misl.rank,
                  icon: Crown
                }, {
                  label: 'Founded',
                  value: misl.founded,
                  icon: BookOpen
                }].map((kpi, i) => <div key={i} className={`p-4 sm:p-5 text-center ${i < 3 ? 'border-r border-[#D4AF37]/15' : ''} ${i < 2 ? 'sm:border-r border-b sm:border-b-0 border-[#D4AF37]/15' : ''} ${i < 2 ? 'border-b' : ''}`} data-id="element-5679" data-mp="bx0tx3a04e95" data-mpi={i}>
                    <kpi.icon className="w-3.5 h-3.5 text-[#D4AF37]/60 mx-auto mb-2" data-id="element-5680" data-mp="3f6e1phb3fc4y" />
                    <p className="font-serif text-base sm:text-lg text-[#D4AF37] leading-tight" data-id="element-5681" data-mp="3a7ui3njz2cfr">
                      {kpi.value}
                    </p>
                    <p className="text-[#FDFBF7]/40 text-[9px] tracking-widest uppercase mt-1" data-id="element-5682" data-mp="6q2rachknyj5">
                      {kpi.label}
                    </p>
                  </div>)}
              </div>
            </div>

            {/* Two-column: Misldar + Your Standing */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-5683" data-mp="3szn6f4o3j9h5">
              {/* Misldar */}
              <div className="border border-[#D4AF37]/30 bg-[#0B0F19] p-5 sm:p-6 relative" data-id="element-5684" data-mp="2meuelmbdldxo">
                <span className="absolute -top-px left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent" data-id="element-5685" data-mp="2hk891zrz6fli" />
                <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.3em] uppercase mb-4" data-id="element-5686" data-mp="11ymrovhmmd95">
                  The Misldar{' '}
                  <span className="text-[#FDFBF7]/40" data-id="element-5687" data-mp="16xhe07esyjy0">(Clan Chief)</span>
                </p>
                <div className="flex items-center gap-4" data-id="element-5688" data-mp="1bpra656igg8q">
                  <div className="w-12 h-12 shrink-0 rounded-full border border-[#D4AF37] bg-[#D4AF37]/10 flex items-center justify-center" data-id="element-5689" data-mp="1goca03huoxwn">
                    <Crown className="w-5 h-5 text-[#D4AF37]" data-id="element-5690" data-mp="1kocmvjpax11l" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-5691" data-mp="1pps9hg74nfm3">
                    <p className="font-serif text-lg text-[#FDFBF7] leading-tight" data-id="element-5692" data-mp="1pmxrrwfi08wd">
                      {misl.misldar.name}
                    </p>
                    <p className="text-[#D4AF37]/70 text-[10px] tracking-widest uppercase font-mono mt-0.5" data-id="element-5693" data-mp="3rh5cu0tg5fg1">
                      {misl.misldar.handle}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-3 mt-5 pt-4 border-t border-[#D4AF37]/15 text-center" data-id="element-5694" data-mp="1qpd13uf54bfi">
                  <div data-id="element-5695" data-mp="1lrl80x2rej9q">
                    <p className="font-mono text-[#D4AF37] text-sm" data-id="element-5696" data-mp="1lu7h515at6u2">
                      {misl.misldar.xp}
                    </p>
                    <p className="text-[#FDFBF7]/40 text-[9px] tracking-widest uppercase mt-0.5" data-id="element-5697" data-mp="1vpr776x6lbaw">
                      XP
                    </p>
                  </div>
                  <div className="border-x border-[#D4AF37]/15" data-id="element-5698" data-mp="131bwuxozvh5w">
                    <p className="font-mono text-[#D4AF37] text-sm" data-id="element-5699" data-mp="1336heawct79g">
                      {misl.misldar.artifacts}
                    </p>
                    <p className="text-[#FDFBF7]/40 text-[9px] tracking-widest uppercase mt-0.5" data-id="element-5700" data-mp="1c2a2fkf2zwql">
                      Artifacts
                    </p>
                  </div>
                  <div data-id="element-5701" data-mp="oa2y9z346lgm">
                    <p className="font-mono text-[#FDFBF7]/70 text-sm" data-id="element-5702" data-mp="kb3uiht4cuqr">
                      {misl.misldar.joined}
                    </p>
                    <p className="text-[#FDFBF7]/40 text-[9px] tracking-widest uppercase mt-0.5" data-id="element-5703" data-mp="a65ewhtrqhaa">
                      Joined
                    </p>
                  </div>
                </div>
              </div>

              {/* Your Standing */}
              <div className="border border-[#D4AF37]/30 bg-[#0B0F19] p-5 sm:p-6 relative" data-id="element-5704" data-mp="57u5u9basat4">
                <span className="absolute -top-px left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent" data-id="element-5705" data-mp="sxezbm52uqgn" />
                <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.3em] uppercase mb-4" data-id="element-5706" data-mp="xvggu2muuhq0">
                  Your Standing
                </p>
                <div className="flex items-baseline gap-2" data-id="element-5707" data-mp="j1le7edalon9">
                  <span className="font-serif text-4xl text-[#D4AF37]" data-id="element-5708" data-mp="nx0oxxg074fg">
                    #{misl.you.rank}
                  </span>
                  <span className="text-[#FDFBF7]/40 text-[10px] uppercase tracking-widest" data-id="element-5709" data-mp="33rkkljeovpnr">
                    in {misl.name}
                  </span>
                </div>
                <p className="text-emerald-400/80 text-[10px] tracking-widest uppercase mt-1.5" data-id="element-5710" data-mp="1f5ao1h37ngwx">
                  ↑ {misl.you.contribution}
                </p>
                <div className="grid grid-cols-2 mt-5 pt-4 border-t border-[#D4AF37]/15 text-center" data-id="element-5711" data-mp="15fhaiajicrq1">
                  <div className="border-r border-[#D4AF37]/15" data-id="element-5712" data-mp="10hfqnne9codi">
                    <p className="font-mono text-[#D4AF37] text-sm" data-id="element-5713" data-mp="10f3cllbmey5q">
                      {misl.you.xp}
                    </p>
                    <p className="text-[#FDFBF7]/40 text-[9px] tracking-widest uppercase mt-0.5" data-id="element-5714" data-mp="rfq1ypbrg3jf">
                      Your XP
                    </p>
                  </div>
                  <div data-id="element-5715" data-mp="hi1vj24sl7lf">
                    <p className="font-mono text-[#D4AF37] text-sm" data-id="element-5716" data-mp="hilcafiphpxv">
                      {misl.you.artifacts}
                    </p>
                    <p className="text-[#FDFBF7]/40 text-[9px] tracking-widest uppercase mt-0.5" data-id="element-5717" data-mp="2gunvrprbykbj">
                      Artifacts
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Leaderboard */}
            <div className="border border-[#D4AF37]/25 bg-[#0B0F19] p-5 sm:p-6" data-id="element-5718" data-mp="2odx4rhvxch02">
              <div className="flex justify-between items-center mb-4" data-id="element-5719" data-mp="2t5oy206gt0tw">
                <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.3em] uppercase" data-id="element-5720" data-mp="2t7i2d8tlv8yq">
                  Clan Leaderboard
                </p>
                <p className="text-[#FDFBF7]/40 text-[9px] tracking-widest uppercase" data-id="element-5721" data-mp="20hjbr8zrj3ry">
                  Top 5 + You
                </p>
              </div>
              <div className="flex flex-col divide-y divide-[#D4AF37]/10" data-id="element-5722" data-mp="25hntt3gbk6ka">
                {misl.leaderboard.map(row => <div key={row.handle} className={`flex items-center gap-3 py-3 first:pt-0 last:pb-0 ${row.isYou ? 'bg-[#D4AF37]/[0.04] -mx-3 px-3 border-l-2 border-[#D4AF37]' : ''}`} data-id="element-5723" data-mp="2fdqr9w087h0w">
                    <span className={`w-7 text-center font-mono text-sm ${row.rank <= 3 ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/50'}`} data-id="element-5724" data-mp="3qrmabfu949xe">
                      {row.rank}
                    </span>
                    <div className={`w-8 h-8 shrink-0 rounded-full border ${row.isMisldar ? 'border-[#D4AF37] bg-[#D4AF37]/15' : 'border-[#D4AF37]/30 bg-[#131B2B]'} flex items-center justify-center`} data-id="element-5725" data-mp="phn4lf5ulgib">
                      {row.isMisldar ? <Crown className="w-3.5 h-3.5 text-[#D4AF37]" data-id="element-5726" data-mp="yk671y5q1r41" /> : <User className="w-3.5 h-3.5 text-[#D4AF37]/60" data-id="element-5727" data-mp="uj2qf9funr1r" />}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-5728" data-mp="13ho70z1rgvzw">
                      <p className={`text-sm leading-tight truncate ${row.isYou ? 'text-[#D4AF37] font-bold' : 'text-[#FDFBF7]'}`} data-id="element-5729" data-mp="13i7oyfrevkdx">
                        {row.name}
                      </p>
                      <p className="text-[#FDFBF7]/40 text-[9px] tracking-widest uppercase font-mono mt-0.5 truncate" data-id="element-5730" data-mp="1kr0hzgvgfgyw">
                        {row.handle}
                      </p>
                    </div>
                    <span className={`font-mono text-xs font-bold whitespace-nowrap ${row.isYou ? 'text-[#D4AF37]' : 'text-[#D4AF37]/70'}`} data-id="element-5731" data-mp="1b179wpuitjze">
                      {row.xp} XP
                    </span>
                  </div>)}
              </div>
            </div>

            {/* History + Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" data-id="element-5732" data-mp="16x96i2y5xn0j">
              {/* History */}
              <div className="lg:col-span-3 border border-[#D4AF37]/25 bg-[#0B0F19] p-5 sm:p-6 relative" data-id="element-5733" data-mp="s4610txuizq6">
                <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.3em] uppercase mb-3" data-id="element-5734" data-mp="x2zfaokyiure">
                  The Story of {misl.name}
                </p>
                <p className="text-[#FDFBF7]/75 text-sm leading-relaxed font-light mb-5" data-id="element-5735" data-mp="2re4bj7tps4wp">
                  {misl.history}
                </p>
                <blockquote className="font-serif italic text-[#D4AF37]/90 text-base leading-relaxed border-l-2 border-[#D4AF37]/40 pl-4" data-id="element-5736" data-mp="3slzwzz0bybyb">
                  "{misl.motto}"
                </blockquote>
                <p className="text-[#D4AF37]/40 text-[9px] tracking-widest uppercase mt-3 pl-4" data-id="element-5737" data-mp="5ha9zrjlbvir">
                  Clan Motto
                </p>
              </div>

              {/* Activity */}
              <div className="lg:col-span-2 border border-[#D4AF37]/25 bg-[#0B0F19] p-5 sm:p-6" data-id="element-5738" data-mp="fb99sjw6z37j">
                <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.3em] uppercase mb-4" data-id="element-5739" data-mp="k9uccpyx1yz5">
                  Clan Activity
                </p>
                <div className="flex flex-col divide-y divide-[#D4AF37]/10" data-id="element-5740" data-mp="t8xspm6p809y">
                  {misl.activity.map((a, i) => <div key={i} className="py-3 first:pt-0 last:pb-0" data-id="element-5741" data-mp="2q2iz2ydinnsu" data-mpi={i}>
                      <p className="text-[#FDFBF7]/80 text-xs leading-snug" data-id="element-5742" data-mp="2v13y4uh181fp">
                        <span className="font-bold text-[#FDFBF7]" data-id="element-5743" data-mp="2nh1p0k7zidi0">
                          {a.who}
                        </span>{' '}
                        <span className="text-[#FDFBF7]/55" data-id="element-5744" data-mp="2io8knxgfh76g">{a.what}</span>{' '}
                        <span className={a.tone === 'gold' ? 'text-[#D4AF37]' : a.tone === 'red' ? 'text-[#8B0000]' : 'text-[#FDFBF7]'} data-id="element-5745" data-mp="2drrwx4d54utw">
                          {a.target}
                        </span>
                      </p>
                      <p className="text-[#FDFBF7]/35 text-[9px] tracking-widest uppercase mt-1" data-id="element-5746" data-mp="2sfmryremfba5">
                        {a.when}
                      </p>
                    </div>)}
                </div>
              </div>
            </div>

            {/* CTA strip */}
            <div className="border border-[#D4AF37]/30 bg-[#131B2B] p-5 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-5747" data-mp="1h2u07gltymu3">
              <div className="text-center sm:text-left" data-id="element-5748" data-mp="1ngzikyr1v806">
                <p className="font-serif text-lg text-[#FDFBF7]" data-id="element-5749" data-mp="1nedej93dbamp">
                  Climb the Clan
                </p>
                <p className="text-[#FDFBF7]/55 text-xs mt-1" data-id="element-5750" data-mp="1dia31cmmlzn3">
                  Earn XP through packets, trades, events and referrals. The top
                  XP earner becomes Misldar (Clan Chief).
                </p>
              </div>
              <div className="flex items-center gap-3" data-id="element-5751" data-mp="14in8nasbdu3j">
                <button type="button" onClick={() => setActiveTab('referrals')} className="px-4 py-2.5 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 text-[10px] uppercase tracking-widest transition-colors flex items-center gap-2" data-id="element-5752" data-mp="pt7ncabjygv8">
                  <UserPlus className="w-3.5 h-3.5" data-id="element-5753" data-mp="ku4juub80ipd" /> Invite to Clan
                </button>
                <button type="button" onClick={() => setActiveTab('daily-packet')} className="px-4 py-2.5 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold transition-colors flex items-center gap-2" data-id="element-5754" data-mp="24sngd5fxuex">
                  <Gift className="w-3.5 h-3.5" data-id="element-5755" data-mp="66e6kg8kj4xk" /> Earn XP
                </button>
              </div>
            </div>
          </div>;
        }
      case 'referrals':
        {
          const inviteLink = 'https://lahore-treasury.app/join/HARNOOR-123';
          const inviteMessage = `You're invited to Treasures of the Sikh Empire — a premium collectible experience built around the Sikh Empire.\n\n` + `I've just joined Sukerchakia Misl. Every artifact (Maharajas, generals, jewels, fortresses) carries real historical weight. Open daily packets, complete sets, and build a treasury that means something.\n\n` + `Join me here and claim your free Founders' Edition card:\n${inviteLink}`;
          const tiers = [{
            count: 1,
            reward: '+500 XP',
            label: 'First invite bonus',
            achieved: true
          }, {
            count: 3,
            reward: 'Bonus Packet',
            label: 'A free Treasury packet',
            achieved: false
          }, {
            count: 5,
            reward: 'Founder Seal',
            label: 'A permanent identity seal',
            achieved: false
          }, {
            count: 10,
            reward: 'Live Night ticket',
            label: 'Reserved seat at Treasury Nights',
            achieved: false
          }];
          return <div className="flex flex-col gap-8" data-id="element-5756" data-mp="1dznv2961abr7">
            {/* Header */}
            <div className="flex justify-between items-end border-b border-[#D4AF37]/20 pb-4" data-id="element-5757" data-mp="2kfq74xqxm6md">
              <div data-id="element-5758" data-mp="2pdi62bpzut02">
                <h3 className="font-serif text-2xl sm:text-3xl text-[#FDFBF7]" data-id="element-5759" data-mp="2pcqd78ns79dh">
                  Bring the Court to Life
                </h3>
                <p className="text-[#D4AF37]/60 text-xs tracking-[0.2em] uppercase mt-1" data-id="element-5760" data-mp="dsirbhvfkx8j">
                  Your Royal Decree
                </p>
              </div>
              <div className="text-right" data-id="element-5761" data-mp="3zabya72evi6">
                <span className="font-serif text-2xl text-[#D4AF37]" data-id="element-5762" data-mp="3v760xd3vwgye">
                  2<span className="text-[#FDFBF7]/30 text-lg" data-id="element-5763" data-mp="3v6wcvv2egjvc"> referred</span>
                </span>
              </div>
            </div>

            {/* Invite message preview */}
            <div className="bg-[#0B0F19] border border-[#D4AF37]/20 p-5 sm:p-6" data-id="element-5764" data-mp="3m4wryro9kvn4">
              <div className="flex items-center justify-between mb-3" data-id="element-5765" data-mp="3h8g500sswnbm">
                <p className="text-[#D4AF37]/60 text-[9px] tracking-[0.3em] uppercase font-bold" data-id="element-5766" data-mp="3c8k0m0g6ytvj">
                  Your Invitation
                </p>
                <p className="text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase font-bold" data-id="element-5767" data-mp="1ffi91w3q9emg">
                  +500 XP / invite
                </p>
              </div>
              <p className="text-[#FDFBF7]/80 text-sm leading-relaxed whitespace-pre-line" data-id="element-5768" data-mp="34w4b0oxhwpii">
                {inviteMessage.split(inviteLink)[0]}
                <a href={inviteLink} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] underline decoration-[#D4AF37]/40 underline-offset-2 hover:decoration-[#D4AF37] break-all" data-id="element-5769" data-mp="39pyrzklpuqdw">
                  {inviteLink}
                </a>
              </p>
            </div>

            {/* Share buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-5770" data-mp="3skdq7807ef0o">
              <a href={`https://wa.me/?text=${encodeURIComponent(inviteMessage)}`} target="_blank" rel="noopener noreferrer" className="py-3.5 bg-[#25D366] text-white text-[10px] uppercase tracking-[0.2em] font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 shadow-lg" data-id="element-5771" data-mp="3emfde71j6qna">
                <MessageCircle className="w-3.5 h-3.5" data-id="element-5772" data-mp="1zoulpt74nq2r" /> WhatsApp
              </a>
              <button type="button" onClick={() => {
                navigator.clipboard.writeText(inviteMessage);
                alert('Invitation copied!');
              }} className="py-3.5 border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#D4AF37]/10 transition-colors flex items-center justify-center gap-2" data-id="element-5773" data-mp="1urkrm08smq8l">
                <Copy className="w-3.5 h-3.5" data-id="element-5774" data-mp="1c1uuh6hk5sdi" /> Copy Message
              </button>
              <button type="button" onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: 'Treasures of the Sikh Empire',
                    text: inviteMessage
                  });
                } else {
                  navigator.clipboard.writeText(inviteMessage);
                  alert('Invitation copied!');
                }
              }} className="py-3.5 border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#D4AF37]/10 transition-colors flex items-center justify-center gap-2" data-id="element-5775" data-mp="1qpzjes77f04q">
                <Share2 className="w-3.5 h-3.5" data-id="element-5776" data-mp="lrbcozvt8tl2" /> More
              </button>
            </div>

            {/* Reward ladder */}
            <div className="bg-[#0B0F19] border border-[#D4AF37]/20 p-5 sm:p-6" data-id="element-5777" data-mp="qp36jzg0mprw">
              <div className="flex items-center justify-between mb-5" data-id="element-5778" data-mp="vn4x15frtrde">
                <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.3em] uppercase font-bold" data-id="element-5779" data-mp="10ewg6uwlkuti">
                  Reward Ladder
                </p>
                <p className="text-[#FDFBF7]/40 text-[10px] tracking-widest uppercase" data-id="element-5780" data-mp="15gc18myrz1bw">
                  2 / 10 invites
                </p>
              </div>

              {/* Progress rail */}
              <div className="relative w-full h-1 bg-[#131B2B] rounded-full overflow-hidden mb-6" data-id="element-5781" data-mp="1ecuszvv9sj6w">
                <div className="absolute inset-y-0 left-0 w-[20%] bg-gradient-to-r from-[#8B0000] to-[#D4AF37]" data-id="element-5782" data-mp="2yuvjft4c5nz7" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-5783" data-mp="2pw0c2dpk27ub">
                {tiers.map(t => <div key={t.count} className={`relative border p-4 ${t.achieved ? 'border-[#D4AF37]/60 bg-[#D4AF37]/10' : 'border-[#FDFBF7]/15 bg-[#131B2B]/40'}`} data-id="element-5784" data-mp="2ky8o1us175ve">
                    <div className="flex items-center justify-between mb-2" data-id="element-5785" data-mp="3dme629rpagrj">
                      <span className={`font-serif text-lg ${t.achieved ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/70'}`} data-id="element-5786" data-mp="38u4dsn3454m3">
                        {t.count} invite{t.count > 1 ? 's' : ''}
                      </span>
                      {t.achieved ? <Check className="w-4 h-4 text-[#D4AF37]" data-id="element-5787" data-mp="eb4as778tej" /> : <Lock className="w-3.5 h-3.5 text-[#FDFBF7]/30" data-id="element-5788" data-mp="i3m8lsyvzqtl" />}
                    </div>
                    <p className={`text-[10px] tracking-[0.2em] uppercase font-bold mb-1 ${t.achieved ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/50'}`} data-id="element-5789" data-mp="d1wziiv85n85">
                      {t.reward}
                    </p>
                    <p className="text-[#FDFBF7]/45 text-[10px] leading-snug" data-id="element-5790" data-mp="10ssvls6pr4l7">
                      {t.label}
                    </p>
                  </div>)}
              </div>
            </div>

            {/* Referred friends list */}
            <div className="bg-[#0B0F19] border border-[#D4AF37]/20 p-5 sm:p-6" data-id="element-5791" data-mp="r1g4x3wnikgr">
              <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.3em] uppercase font-bold mb-4" data-id="element-5792" data-mp="m3ensoiscv7x">
                Your Court
              </p>
              <div className="flex flex-col divide-y divide-[#D4AF37]/10" data-id="element-5793" data-mp="1estwh7mzac0o">
                {[{
                  name: 'Gurjeet K.',
                  misl: 'Sukerchakia Misl',
                  joined: '3 days ago',
                  xp: '+500'
                }, {
                  name: 'Manveer S.',
                  misl: 'Sukerchakia Misl',
                  joined: '1 week ago',
                  xp: '+500'
                }].map((f, i) => <div key={i} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0" data-id="element-5794" data-mp="26157wkra9mbl" data-mpi={i}>
                    <div className="w-9 h-9 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center shrink-0" data-id="element-5795" data-mp="29zkreeo5ock9">
                      <User className="w-4 h-4 text-[#D4AF37]" data-id="element-5796" data-mp="2js0a5e7a1567" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-5797" data-mp="2ord40iwkfcia">
                      <p className="text-[#FDFBF7] text-sm font-medium leading-tight" data-id="element-5798" data-mp="2ooqp1y7chuq1">
                        {f.name}
                      </p>
                      <p className="text-[#FDFBF7]/40 text-[10px] uppercase tracking-widest" data-id="element-5799" data-mp="240zickymtsbc">
                        {f.misl} · joined {f.joined}
                      </p>
                    </div>
                    <span className="text-[#D4AF37] text-[10px] font-mono font-bold whitespace-nowrap" data-id="element-5800" data-mp="2ds3tf9l6st0w">
                      {f.xp} XP
                    </span>
                  </div>)}
                <div className="flex items-center gap-3 py-3 last:pb-0 opacity-40" data-id="element-5801" data-mp="2mrh6sdcfm1qw">
                  <div className="w-9 h-9 rounded-full border border-dashed border-[#FDFBF7]/25 flex items-center justify-center shrink-0" data-id="element-5802" data-mp="2htp54rlzj19i">
                    <UserPlus className="w-4 h-4 text-[#FDFBF7]/40" data-id="element-5803" data-mp="2wpy2toq896jt" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-5804" data-mp="2rsofqfqb96z5">
                    <p className="text-[#FDFBF7]/60 text-sm italic leading-tight" data-id="element-5805" data-mp="12cf62h40trzk">
                      One more invite to your next bonus packet…
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>;
        }
      case 'profile':
        return <div className="flex flex-col gap-8" data-id="element-5806" data-mp="3e020ervjiewy">
            <div className="flex justify-between items-end border-b border-[#D4AF37]/20 pb-4" data-id="element-5807" data-mp="3e1v5w3uf2t3d">
              <div data-id="element-5808" data-mp="2z8s0svy0i7tj">
                <h3 className="font-serif text-2xl text-[#FDFBF7]" data-id="element-5809" data-mp="349z8yw42h90q">
                  Your Profile
                </h3>
                <p className="text-[#D4AF37]/60 text-xs tracking-[0.2em] uppercase mt-1" data-id="element-5810" data-mp="2q9oezk7gnebc">
                  Identity & Status
                </p>
              </div>
              <div className="flex items-center gap-4" data-id="element-5811" data-mp="2u9gg5buiyi6o">
                <button type="button" onClick={() => setShowAdminDashboard(true)} className="px-4 py-2 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold transition-colors flex items-center gap-2" data-id="element-5812" data-mp="2gd1tqub82l8s">
                  <Shield className="w-3.5 h-3.5" data-id="element-5813" data-mp="jj86zg92pa9y" /> Admin Dashboard
                </button>
                <button type="button" className="text-[#D4AF37] text-[10px] uppercase tracking-widest border-b border-[#D4AF37]/40 pb-0.5" data-id="element-5814" data-mp="2fllewjfv1ja2">
                  Edit Profile
                </button>
              </div>
            </div>
            <div className="p-8 border border-[#D4AF37]/30 bg-[#0B0F19] flex flex-col sm:flex-row items-center gap-8" data-id="element-5815" data-mp="21m25lnbbc1w6">
              {knightedAvatar ? <div className="relative group" data-id="element-5816" data-mp="1wpv6addf9on7">
                  <div className="w-32 h-32 border-2 border-[#D4AF37] bg-[#131B2B] overflow-hidden relative shadow-[0_0_30px_rgba(212,175,55,0.25)]" data-id="element-5817" data-mp="1rwil6d7ybzz7">
                    <img src={knightedAvatar} alt="Your knighted portrait" className="w-full h-full object-cover" data-id="element-5818" data-mp="1iwm3wwnw63e6" />
                    <div className="absolute inset-0 ring-1 ring-inset ring-[#D4AF37]/40 pointer-events-none" data-id="element-5819" data-mp="18yg18cu9gt89" />
                  </div>
                  <div className="absolute -bottom-3 -right-3 w-8 h-8 rounded-full bg-[#D4AF37] border-2 border-[#0B0F19] flex items-center justify-center shadow-lg" data-id="element-5820" data-mp="3kkqash0akmer">
                    <Shield className="w-4 h-4 text-[#0B0F19]" data-id="element-5821" data-mp="3uk8seqsqbw3n" />
                  </div>
                  <button type="button" onClick={() => setIsKnightingOpen(true)} className="absolute inset-0 bg-[#0B0F19]/80 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-id="element-5822" data-mp="3ui4nc5j3gr1s">
                    <span className="text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase" data-id="element-5823" data-mp="3aw8f2e6vd5hz">
                      Retake
                    </span>
                  </button>
                </div> : <button type="button" onClick={() => setIsKnightingOpen(true)} className="w-32 h-32 border-2 border-dashed border-[#D4AF37]/40 bg-[#131B2B]/50 hover:border-[#D4AF37] hover:bg-[#131B2B] flex flex-col items-center justify-center gap-2 transition-colors relative group" data-id="element-5824" data-mp="2j5c2zfsmv4vr">
                  <Shield className="w-8 h-8 text-[#D4AF37]/40 group-hover:text-[#D4AF37] transition-colors" data-id="element-5825" data-mp="3lg463xu4ulnc" />
                  <span className="text-[#D4AF37]/50 group-hover:text-[#D4AF37] text-[8px] tracking-[0.25em] uppercase transition-colors text-center px-2 leading-tight" data-id="element-5826" data-mp="43jlcjb3ip0m">
                    Awaiting
                    <br data-id="element-5827" data-mp="37ka6y6j5vanf" />
                    Knighting
                  </span>
                </button>}
              <div className="text-center sm:text-left flex-1" data-id="element-5828" data-mp="3givqa3wgitp9">
                <p className="text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase mb-2" data-id="element-5829" data-mp="3gh0zwa2f29ds">
                  Gold Medallion of Ranjit Singh · 4,150 XP
                </p>
                <h3 className="font-serif text-4xl text-[#FDFBF7] mb-4" data-id="element-5830" data-mp="vrldrse578uh">
                  Harnoor S.
                </h3>
                <div className="flex flex-wrap gap-2 justify-center sm:justify-start" data-id="element-5831" data-mp="rr1f2kdx7x68">
                  <span className="px-3 py-1.5 bg-[#8B0000]/20 border border-[#8B0000]/50 text-[#8B0000] text-[9px] uppercase tracking-widest" data-id="element-5832" data-mp="z9975retid54">
                    Founder
                  </span>
                  <span className="px-3 py-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-[9px] uppercase tracking-widest" data-id="element-5833" data-mp="pe7g2q0zf0zw">
                    Live Attended
                  </span>
                  <span className="px-3 py-1.5 bg-[#131B2B] border border-[#FDFBF7]/20 text-[#FDFBF7]/60 text-[9px] uppercase tracking-widest" data-id="element-5834" data-mp="1i23lqk8j438t">
                    Early Adopter
                  </span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-5835" data-mp="1d9to0i169yvz">
              {[{
              label: 'Artifacts',
              value: '12'
            }, {
              label: 'Sets Complete',
              value: '4'
            }, {
              label: 'Trades Made',
              value: '7'
            }, {
              label: 'Events Attended',
              value: '1'
            }].map((stat, i) => <div key={i} className="bg-[#0B0F19] border border-[#D4AF37]/20 p-6 text-center" data-id="element-5836" data-mp="3szmjazu3tyg3" data-mpi={i}>
                  <p className="font-serif text-3xl text-[#D4AF37] mb-2" data-id="element-5837" data-mp="omhfj3rlye1a">
                    {stat.value}
                  </p>
                  <p className="text-[#FDFBF7]/50 text-[10px] uppercase tracking-widest" data-id="element-5838" data-mp="kmp3gh309pr6">
                    {stat.label}
                  </p>
                </div>)}
            </div>
          </div>;
      case 'rewards':
        {
          return <RewardsTabContent data-id="element-5839" data-mp="30dl3mycorei4" />;
        }
      default:
        return null;
    }
  };
  return <section id="member-dashboard" className="py-16 sm:py-24 bg-[#0B0F19] relative overflow-hidden" data-id="element-5840" data-mp="2ve8eu8297xcw">
      <div className="container mx-auto px-4 relative z-10" data-id="element-5841" data-mp="2latf3xkndsxo">
        <div className="max-w-6xl mx-auto" data-id="element-5842" data-mp="2l87aa5p55rj5">
          <div className="flex flex-col gap-8 sm:gap-12" data-id="element-5843" data-mp="2qa6aj6pl48bv">
            {/* Section Header */}
            <SectionHeader eyebrow="Member Experience" title={<>
                  ONCE YOU'RE <span className="text-[#D4AF37]" data-id="element-5845" data-mp="300heuud6kjja">IN</span>
                </>} description="The Treasury is more than a collection. It's your ticket to the inner circle of the Sikh Kingdoms." data-id="element-5844" data-mp="2v7y5k9lj0aka" />

            {/* Main Dashboard UI Container — swaps to inline Admin when active */}
            {showAdminDashboard ? <Suspense fallback={<div className="border border-[#D4AF37]/20 bg-[#131B2B]/30 min-h-[700px] flex items-center justify-center" data-id="element-5847" data-mp="3ix0r3sja6nk2">
                    <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.3em] uppercase animate-pulse" data-id="element-5848" data-mp="1ojrj656dva3x">
                      Loading Admin Console…
                    </p>
                  </div>} data-id="element-5846" data-mp="3iyby0180wbfe">
                <TreasuryAdminDashboard inline onClose={() => setShowAdminDashboard(false)} data-id="element-5849" data-mp="3kxx6gi1730a" />
              </Suspense> : <div className="flex flex-col lg:flex-row border border-[#D4AF37]/20 bg-[#131B2B]/30 backdrop-blur-sm min-h-[700px]" data-id="element-5850" data-mp="3qx8lxdp99czo">
                {/* Left Sidebar Navigation — Scrollable on mobile */}
                <div className="w-full lg:w-64 border-b lg:border-b-0 lg:border-r border-[#D4AF37]/20 flex lg:flex-col overflow-x-auto lg:overflow-x-visible no-scrollbar" data-id="element-5851" data-mp="mjtmpeiy2t7s">
                  {tabs.map(tab => <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-3 px-5 py-4 lg:py-5 text-left transition-all relative shrink-0 lg:shrink ${activeTab === tab.id ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'text-[#FDFBF7]/40 hover:text-[#FDFBF7]/70 hover:bg-[#FDFBF7]/5'}`} data-id="element-5852" data-mp="dhstcsqu0rbe">
                      {activeTab === tab.id && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 lg:h-auto lg:right-auto bg-[#D4AF37]" data-id="element-5853" data-mp="2zqi67tj03ro7" />}
                      <tab.icon className="w-4 h-4" data-id="element-5854" data-mp="26zi0ne50ufuf" />
                      <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.2em] font-medium whitespace-nowrap" data-id="element-5855" data-mp="230sfgzdqru08">
                        {tab.label}
                      </span>
                      {tab.badge && <span className="ml-auto w-4 h-4 bg-[#8B0000] text-[#FDFBF7] text-[8px] flex items-center justify-center rounded-full font-bold" data-id="element-5856" data-mp="2bzcpzd95exdd">
                          {tab.badge === 'dot' ? '' : tab.badge}
                        </span>}
                    </button>)}
                </div>

                {/* Right Content Area */}
                <div className="flex-1 p-5 sm:p-8 lg:p-10 flex flex-col gap-8 sm:gap-10 overflow-hidden" data-id="element-5857" data-mp="3oc1eobcgjqjv">
                  {renderContent()}
                </div>
              </div>}
          </div>
        </div>
      </div>

      <KnightingCeremony isOpen={isKnightingOpen} onClose={() => setIsKnightingOpen(false)} onComplete={url => {
      setKnightedAvatar(url);
    }} data-id="element-5858" data-mp="1zkoqu8zlwg0" />
    </section>;
};
// ---------- 5. Daily Court Packet Section ----------
const DailyPacketSection = ({
  onRegister
}: {
  onRegister?: () => void;
}) => {
  const [packetState, setPacketState] = useState<'sealed' | 'opening' | 'revealed' | 'added'>('sealed');
  // Once the "added" animation has played, hand off to registration.
  useEffect(() => {
    if (packetState !== 'added' || !onRegister) return;
    const timer = setTimeout(() => {
      onRegister();
      setPacketState('sealed');
    }, 1800);
    return () => clearTimeout(timer);
  }, [packetState, onRegister]);
  return <section id="how-it-works" className="px-4 py-24 bg-[#131B2B] relative border-t border-[#D4AF37]/10 overflow-hidden" data-id="element-5859" data-mp="2a66du4f1cccu">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.08),_transparent_60%)]" data-id="element-5860" data-mp="20ftj5jdib2sz" />
      <div className="max-w-4xl mx-auto relative text-center" data-id="element-5861" data-mp="1vgqomsv5kez9">
        <SectionHeader eyebrow="The Habit Loop" title={<>
              THE DAILY <span className="text-[#D4AF37]" data-id="element-5863" data-mp="1mfitukruhw9x">PACKET</span>
            </>} description="Every 24 hours, a sealed leather envelope arrives from the Darbar. Open it to reveal new artifacts, and answer the optional Court Question for bonus influence." data-id="element-5862" data-mp="1qhduro5v67n6" />

        <div className="relative mt-12 min-h-[640px] flex items-center justify-center" data-id="element-5864" data-mp="3grh3gnu2gu6c">
          <AnimatePresence mode="wait" data-id="element-5865" data-mp="3bsnoeqz4sv42">
            {/* ============ SEALED ============ */}
            {packetState === 'sealed' && <motion.div key="sealed" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.5
          }} className="flex flex-col items-center text-center max-w-md mx-auto" data-id="element-5866" data-mp="2djfpcasa1cxz">
                <motion.div animate={{
              y: [0, -8, 0]
            }} transition={{
              duration: 3.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="relative w-64 sm:w-72 mb-10" data-id="element-5867" data-mp="1gu83t2rnr3u2">
                  <motion.div animate={{
                opacity: [0.4, 0.7, 0.4]
              }} transition={{
                duration: 2.8,
                repeat: Infinity
              }} className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.4),_transparent_65%)] blur-2xl pointer-events-none" data-id="element-5868" data-mp="s78pxczmxd6x" />
                  <motion.button type="button" onClick={() => {
                setPacketState('opening');
                setTimeout(() => setPacketState('revealed'), 1900);
              }} whileHover={{
                scale: 1.04
              }} whileTap={{
                scale: 0.96
              }} aria-label="Break the seal and open your daily packet" className="relative block w-full cursor-pointer" data-id="element-5869" data-mp="1b51sge6cjl3s">
                    <img src="https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png" alt="Sealed envelope" className="w-full drop-shadow-[0_25px_50px_rgba(0,0,0,0.7)]" data-id="element-5870" data-mp="1x01worrpvize" />
                    <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 text-[#D4AF37]/60 text-[9px] tracking-[0.3em] uppercase whitespace-nowrap" data-id="element-5871" data-mp="2bnv77y1y3vb4">
                      Click to open
                    </span>
                  </motion.button>
                </motion.div>
                <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-3" data-id="element-5872" data-mp="2pnnz3wp7khko">
                  Day 47 · The Darbar
                </p>
                <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-3" data-id="element-5873" data-mp="2ulpqr60p9p7r">
                  Your Daily Packet has Arrived
                </h3>
                <p className="text-[#FDFBF7]/60 text-sm font-light mb-8 leading-relaxed" data-id="element-5874" data-mp="107ybnqzlqd4x">
                  A sealed envelope from the Maharaja's court awaits. Break the
                  wax — discover the artifact within.
                </p>
                <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} onClick={() => {
              setPacketState('opening');
              setTimeout(() => setPacketState('revealed'), 1900);
            }} className="px-12 py-4 bg-[#8B0000] hover:bg-[#A00000] text-[#FDFBF7] text-xs tracking-[0.3em] uppercase font-medium shadow-2xl transition-colors flex items-center gap-3" data-id="element-5875" data-mp="38qq10v7dbvu0">
                  <Sparkles className="w-4 h-4" data-id="element-5876" data-mp="3ih17aoeiyh42" /> Break the Seal
                </motion.button>
              </motion.div>}

            {/* ============ OPENING ============ */}
            {packetState === 'opening' && <motion.div key="opening" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="relative flex items-center justify-center w-full h-full min-h-[560px]" data-id="element-5877" data-mp="2750emyeji8j1">
                {/* Expanding gold burst */}
                <motion.div initial={{
              scale: 0.4,
              opacity: 0
            }} animate={{
              scale: 4,
              opacity: [0, 0.9, 0]
            }} transition={{
              duration: 1.8,
              ease: 'easeOut'
            }} className="absolute w-96 h-96 rounded-full bg-[radial-gradient(circle,_rgba(212,175,55,0.6),_transparent_60%)] blur-3xl" data-id="element-5878" data-mp="tpqvwo86qfv9" />

                {/* Shaking + shrinking envelope */}
                <motion.div initial={{
              scale: 1,
              opacity: 1
            }} animate={{
              scale: [1, 1.1, 0.6],
              opacity: [1, 1, 0],
              rotateZ: [0, -3, 4, -2, 0]
            }} transition={{
              duration: 1.8,
              times: [0, 0.5, 1],
              ease: 'easeInOut'
            }} className="relative w-64 sm:w-72 z-10" data-id="element-5879" data-mp="3258etqosw8n7">
                  <img src="https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png" alt="Opening envelope" className="w-full drop-shadow-[0_0_80px_rgba(212,175,55,0.7)]" data-id="element-5880" data-mp="2eae0x52k70fa" />
                </motion.div>

                {/* Radial sparkle particles */}
                {Array.from({
              length: 20
            }).map((_, i) => {
              const angle = i / 20 * Math.PI * 2;
              const distance = 280;
              return <motion.div key={i} initial={{
                x: 0,
                y: 0,
                opacity: 0,
                scale: 0
              }} animate={{
                x: Math.cos(angle) * distance,
                y: Math.sin(angle) * distance,
                opacity: [0, 1, 0],
                scale: [0, 1.2, 0]
              }} transition={{
                duration: 1.6,
                delay: 0.3 + i % 4 * 0.05,
                ease: 'easeOut'
              }} className="absolute w-2 h-2 bg-[#D4AF37] rounded-full shadow-[0_0_14px_rgba(212,175,55,1)]" data-id="element-5881" data-mp="33kkgq9deox37" data-mpi={i} />;
            })}

                {/* Caption */}
                <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: [0, 1, 1, 0],
              y: 0
            }} transition={{
              duration: 1.8,
              times: [0, 0.3, 0.7, 1]
            }} className="absolute bottom-12 font-serif text-lg text-[#D4AF37] tracking-[0.3em] uppercase" data-id="element-5882" data-mp="gn9qkpnm2ig8">
                  The seal breaks…
                </motion.p>
              </motion.div>}

            {/* ============ REVEALED ============ */}
            {packetState === 'revealed' && <motion.div key="revealed" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0,
            y: -30
          }} transition={{
            duration: 0.6
          }} className="flex flex-col items-center text-center max-w-2xl mx-auto" data-id="element-5883" data-mp="vfa8o0ptdlff">
                <motion.p initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4
            }} className="text-[#D4AF37] text-[10px] tracking-[0.4em] uppercase mb-4" data-id="element-5884" data-mp="2n75ixwzx4fx5">
                  A Treasure from the Court
                </motion.p>

                <motion.div initial={{
              y: 200,
              scale: 0.5,
              rotateY: 90,
              opacity: 0
            }} animate={{
              y: 0,
              scale: 1,
              rotateY: 0,
              opacity: 1
            }} transition={{
              duration: 0.9,
              ease: [0.16, 1, 0.3, 1]
            }} className="relative mb-6" style={{
              perspective: 1000
            }} data-id="element-5885" data-mp="1zdnqzlawi7hp">
                  <motion.div animate={{
                opacity: [0.4, 0.75, 0.4]
              }} transition={{
                duration: 3,
                repeat: Infinity
              }} className="absolute -inset-10 bg-[radial-gradient(circle,_rgba(212,175,55,0.4),_transparent_60%)] blur-2xl" data-id="element-5886" data-mp="3ibais3ve5tfe" />
                  <motion.div animate={{
                y: [0, -8, 0]
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="relative w-64 sm:w-72" data-id="element-5887" data-mp="17hedd1d41l09">
                    <div className="border-2 border-[#D4AF37] shadow-[0_0_50px_rgba(212,175,55,0.5)] overflow-hidden bg-[#0B0F19]" data-id="element-5888" data-mp="1k0tjgp6qtwe9">
                      <img src="https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png" alt="Moran Sarkar" className="w-full" data-id="element-5889" data-mp="1ezvv1uv5464b" />
                    </div>
                    <div className="absolute -top-3 -right-3 px-3 py-1 bg-[#D4AF37] text-[#0B0F19] text-[9px] uppercase tracking-widest font-bold shadow-lg" data-id="element-5890" data-mp="22pzurr57jlq1">
                      Treasury Rare
                    </div>
                  </motion.div>
                </motion.div>

                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.9,
              duration: 0.6
            }} className="text-center" data-id="element-5891" data-mp="27qnwb6r2bkyi">
                  <h3 className="font-serif text-3xl text-[#FDFBF7] mb-2" data-id="element-5892" data-mp="2mbs0vhv38d6i">
                    Moran Sarkar
                  </h3>
                  <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase mb-4" data-id="element-5893" data-mp="27mlyjl0ye6ue">
                    Royal Court Collection · Treasury Rare
                  </p>
                  <p className="text-[#FDFBF7]/60 text-sm font-light italic max-w-md mx-auto mb-8 leading-relaxed" data-id="element-5894" data-mp="1yojranqmmhyo">
                    "A dancer who became consort to the Sher-e-Punjab — beauty
                    and intellect at the heart of the Darbar."
                  </p>

                  <motion.button whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} onClick={() => setPacketState('added')} className="px-10 py-4 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0B0F19] text-xs tracking-[0.3em] uppercase font-bold shadow-2xl transition-colors flex items-center gap-3 mx-auto" data-id="element-5895" data-mp="1toff39wr49e9">
                    <BookOpen className="w-4 h-4" data-id="element-5896" data-mp="i72yrw9ykktk" /> Register to Add to Your
                    Collection
                  </motion.button>
                </motion.div>
              </motion.div>}

            {/* ============ ADDED ============ */}
            {packetState === 'added' && <motion.div key="added" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="flex flex-col items-center text-center max-w-md mx-auto relative" data-id="element-5897" data-mp="47ti6vhubop2">
                {/* Flying card animation */}
                <motion.div initial={{
              x: 0,
              y: 0,
              scale: 1,
              opacity: 1
            }} animate={{
              x: -260,
              y: -180,
              scale: 0.18,
              opacity: 0,
              rotate: -15
            }} transition={{
              duration: 1.1,
              ease: [0.7, 0, 0.84, 0]
            }} className="relative w-44 mb-8 absolute" data-id="element-5898" data-mp="2jsf40zvxr0gj">
                  <div className="border-2 border-[#D4AF37] shadow-[0_0_40px_rgba(212,175,55,0.6)] overflow-hidden" data-id="element-5899" data-mp="3ch2xhiqu8w56">
                    <img src="https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png" alt="Moran Sarkar" className="w-full" data-id="element-5900" data-mp="1d4icoxgp2xcq" />
                  </div>
                </motion.div>

                {/* Sparkle trail */}
                {Array.from({
              length: 10
            }).map((_, i) => <motion.div key={i} initial={{
              x: 0,
              y: 0,
              opacity: 0,
              scale: 0
            }} animate={{
              x: -260 + Math.random() * 50,
              y: -180 + Math.random() * 50,
              opacity: [0, 1, 0],
              scale: [0, 1, 0]
            }} transition={{
              duration: 1.3,
              delay: 0.15 + i * 0.06,
              ease: 'easeOut'
            }} className="absolute w-2 h-2 bg-[#D4AF37] rounded-full shadow-[0_0_10px_rgba(212,175,55,1)]" data-id="element-5901" data-mp="3tp3rt05zmevn" data-mpi={i} />)}

                {/* Success state */}
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 1.0,
              duration: 0.6
            }} className="flex flex-col items-center" data-id="element-5902" data-mp="swh5d4me21pn">
                  <div className="w-16 h-16 rounded-full border-2 border-[#D4AF37] bg-[#D4AF37]/10 flex items-center justify-center mb-5" data-id="element-5903" data-mp="1gjyrokmvw7l8">
                    <Sparkles className="w-7 h-7 text-[#D4AF37]" data-id="element-5904" data-mp="11uj4femkakaa" />
                  </div>
                  <p className="text-[#D4AF37]/80 text-[10px] tracking-[0.35em] uppercase mb-3" data-id="element-5905" data-mp="16sb2kqdsv2mx">
                    Reserved for your Archive
                  </p>
                  <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-3" data-id="element-5906" data-mp="1zagfcasv0czq">
                    Opening the Darbar gates…
                  </h3>
                  <p className="text-[#FDFBF7]/60 text-sm font-light leading-relaxed mb-2 max-w-md" data-id="element-5907" data-mp="318d329quu896">
                    Moran Sarkar is being filed under the{' '}
                    <span className="text-[#D4AF37]" data-id="element-5908" data-mp="3b1bqvpej1urq">
                      Royal Court Collection
                    </span>
                    . Finish registration to claim her.
                  </p>
                  <p className="text-[#FDFBF7]/40 text-xs font-light mb-8" data-id="element-5909" data-mp="2icwfdcultul5">
                    Redirecting you to the registry…
                  </p>
                </motion.div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </section>;
};
// ---------- 6. Collections Section ----------
interface CourtCard {
  img: string;
  name: string;
  rarity: string;
  role?: string;
  type?: string;
  set?: string;
  oneLineLore?: string;
  collectibleDescription?: string;
  historicalMeaning?: string;
  whyItIsRare?: string;
  collectorMeaning?: string;
  unlockPotential?: string;
  emblem?: string;
  isLocked?: boolean;
}
const CardModal = ({
  card,
  onClose
}: {
  card: CourtCard;
  onClose: () => void;
}) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-5910" data-mp="199jqh5wompcf">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="absolute inset-0 bg-[#0B0F19]/92 backdrop-blur-sm" data-id="element-5911" data-mp="14ca2zvsusnr8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.15),_transparent_50%)]" data-id="element-5912" data-mp="2trq0gdxf0l1a" />
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.4,
      type: 'spring',
      bounce: 0.2
    }} className="relative w-full max-w-5xl bg-[#0B0F19] border-2 border-[#D4AF37]/40 p-6 sm:p-10 shadow-2xl overflow-y-auto max-h-[90vh] scrollbar-hide" data-id="element-5913" data-mp="rzfsi1ngjeyr">
        <CornerOrnaments data-id="element-5914" data-mp="10ssuru7evsxi" />
        <button onClick={onClose} className="absolute top-4 right-4 p-2 text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors z-10" aria-label="Close" data-id="element-5915" data-mp="10tkqr64x7sob">
          <X className="w-6 h-6" data-id="element-5916" data-mp="3liia08c11gqq" />
        </button>

        <div className="flex flex-col md:flex-row gap-8 sm:gap-12" data-id="element-5917" data-mp="3qiwexhic0akj">
          {/* Left Col: Card artwork */}
          <div className="w-full md:w-2/5 shrink-0 flex flex-col items-center gap-4" data-id="element-5918" data-mp="28kqegpzsaglw">
            <div className="w-full aspect-[4/5] bg-[#131B2B] shadow-[0_0_40px_rgba(212,175,55,0.18)] overflow-hidden" data-id="element-5919" data-mp="23mot1yevy579">
              <img src={card.img} alt={card.name} className="w-full h-full object-cover" data-id="element-5920" data-mp="2hmhg9jr7e3ah" />
            </div>
            <RarityBadge rarity={card.rarity} data-id="element-5921" data-mp="1pus7kd7q2tju" />
            {(card.type || card.set) && <div className="text-center" data-id="element-5922" data-mp="1kvxfslrs1unl">
                {card.type && <p className="text-[#D4AF37]/60 text-[9px] tracking-[0.3em] uppercase" data-id="element-5923" data-mp="1ktufn8wwul55">
                    Type · {card.type}
                  </p>}
                {card.set && <p className="text-[#D4AF37]/60 text-[9px] tracking-[0.3em] uppercase mt-1" data-id="element-5924" data-mp="1tuiv881yctk5">
                    Set · {card.set}
                  </p>}
              </div>}
          </div>

          {/* Right Col: Details */}
          <div className="flex-1 flex flex-col" data-id="element-5925" data-mp="1evq38xm1ijky">
            <p className="text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase mb-2" data-id="element-5926" data-mp="1esu2z2mpzitc">
              {card.role || 'Court Figure'}
            </p>
            <h3 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] leading-tight mb-5" data-id="element-5927" data-mp="1ortfwxeiq0m5">
              {card.name}
            </h3>
            <OrnateDivider className="mb-6 justify-start" data-id="element-5928" data-mp="1tlnw3quwzxgh" />
            {card.oneLineLore && <p className="font-serif italic text-lg sm:text-xl text-[#FDFBF7]/90 mb-8 leading-relaxed" data-id="element-5929" data-mp="1yie7w1zos6w3">
                "{card.oneLineLore}"
              </p>}

            <div className="grid grid-cols-1 gap-6" data-id="element-5930" data-mp="27knj6u2s44aj">
              {card.collectibleDescription && <div data-id="element-5931" data-mp="2ch45diqf48kz">
                  <p className="text-[#D4AF37]/70 text-[9px] tracking-[0.3em] uppercase mb-2" data-id="element-5932" data-mp="2cgcmn8hx65c4">
                    Collectible Description
                  </p>
                  <p className="text-[#FDFBF7]/75 text-sm font-light leading-relaxed" data-id="element-5933" data-mp="i5h1l6pjsxsg">
                    {card.collectibleDescription}
                  </p>
                </div>}
              {card.historicalMeaning && <div data-id="element-5934" data-mp="5s3mexjiix3w">
                  <p className="text-[#D4AF37]/70 text-[9px] tracking-[0.3em] uppercase mb-2" data-id="element-5935" data-mp="uu03qrf71ka">
                    Historical Meaning
                  </p>
                  <p className="text-[#FDFBF7]/75 text-sm font-light leading-relaxed" data-id="element-5936" data-mp="3s2y4as7u44ev">
                    {card.historicalMeaning}
                  </p>
                </div>}
              {card.whyItIsRare && <div data-id="element-5937" data-mp="oitr8rsn8tze">
                  <p className="text-[#D4AF37]/70 text-[9px] tracking-[0.3em] uppercase mb-2" data-id="element-5938" data-mp="jl1y5ug9j1tm">
                    Why It Is Rare
                  </p>
                  <p className="text-[#FDFBF7]/75 text-sm font-light leading-relaxed" data-id="element-5939" data-mp="em8l234w1ctz">
                    {card.whyItIsRare}
                  </p>
                </div>}
              {card.collectorMeaning && <div data-id="element-5940" data-mp="1rdabliwppm5j">
                  <p className="text-[#D4AF37]/70 text-[9px] tracking-[0.3em] uppercase mb-2" data-id="element-5941" data-mp="1rcqvm7qmh7u5">
                    Collector Meaning
                  </p>
                  <p className="text-[#FDFBF7]/75 text-sm font-light leading-relaxed" data-id="element-5942" data-mp="1vcrhgdg1wlb8">
                    {card.collectorMeaning}
                  </p>
                </div>}
              {card.unlockPotential && <div data-id="element-5943" data-mp="17pjcxxqg5ga9">
                  <p className="text-[#D4AF37]/70 text-[9px] tracking-[0.3em] uppercase mb-2" data-id="element-5944" data-mp="17o8ac18qmekq">
                    Unlock Potential
                  </p>
                  <p className="text-[#FDFBF7]/75 text-sm font-light leading-relaxed" data-id="element-5945" data-mp="1chuf778nte5z">
                    {card.unlockPotential}
                  </p>
                </div>}
              {card.emblem && <div data-id="element-5946" data-mp="oqov2fvorzpv">
                  <p className="text-[#D4AF37]/70 text-[9px] tracking-[0.3em] uppercase mb-2" data-id="element-5947" data-mp="orghp17b2n40">
                    Emblem
                  </p>
                  <p className="text-[#FDFBF7]/75 text-sm font-light leading-relaxed" data-id="element-5948" data-mp="trmc2p4ymnps">
                    {card.emblem}
                  </p>
                </div>}

              {/* Restoration Section */}
              <div className="mt-4 border-t border-[#D4AF37]/20 pt-6" data-id="element-5949" data-mp="k1mi9moz6ie0">
                <div className="flex items-center gap-2 mb-4" data-id="element-5950" data-mp="jzt8hyj2fi1s">
                  <MapIcon className="w-4 h-4 text-[#D4AF37]" data-id="element-5951" data-mp="f1ruhs0hy8x6" />
                  <p className="text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase" data-id="element-5952" data-mp="t2cakgf11qmz">
                    Restore Punjab
                  </p>
                </div>
                <div className="bg-[#131B2B]/50 border border-[#D4AF37]/10 rounded-lg p-4 mb-4" data-id="element-5953" data-mp="oywg9qqfk2dg">
                  <div className="flex justify-between items-center mb-3" data-id="element-5954" data-mp="12rm6k7czrtgd">
                    <span className="text-[#FDFBF7]/60 text-xs" data-id="element-5955" data-mp="xulvtj5762ix">
                      Restoration Value
                    </span>
                    <span className="text-[#D4AF37] font-serif" data-id="element-5956" data-mp="1cogww2z42njk">
                      +
                      {card.rarity === "Founders' Edition" ? 50 : card.rarity === 'Epic' ? 25 : 10}{' '}
                      Pts
                    </span>
                  </div>
                  <div data-id="element-5957" data-mp="2dvb5i4iuy58">
                    <span className="text-[#FDFBF7]/60 text-xs block mb-1" data-id="element-5958" data-mp="2b921e1zgakw">
                      Eligible Zones
                    </span>
                    <div className="flex flex-wrap gap-2" data-id="element-5959" data-mp="35uki3580o9vf">
                      <span className="bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37] text-[10px] px-2 py-1 rounded" data-id="element-5960" data-mp="35ry3wmqmew48">
                        Lahore Court
                      </span>
                      {card.rarity === "Founders' Edition" && <span className="bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37] text-[10px] px-2 py-1 rounded" data-id="element-5961" data-mp="3kjqgws2xzy2s">
                          Treasury Vault
                        </span>}
                    </div>
                  </div>
                </div>
                <p className="text-[10px] text-[#FDFBF7]/40 mb-4 leading-relaxed" data-id="element-5962" data-mp="196i1vg5io3vr">
                  Contributing a card does not remove it from your collection.
                  It adds its story power to the Punjab Memory Map.
                </p>
                <Link to="/restore-punjab" className="w-full block text-center bg-[#D4AF37] text-black py-3 rounded-sm text-sm font-medium hover:bg-[#FDFBF7] transition-colors" data-id="element-5963" data-mp="108o7wbv85mbr">
                  Contribute to Restoration
                </Link>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>;
};
// Modal for browsing a collection that doesn't have full cards yet
interface FutureCollection {
  name: string;
  sub: string;
  rarity: string;
  img?: string;
  gradient?: string;
  description?: string;
  cardCount?: number;
  cards?: CourtCard[];
  isLive?: boolean;
}
const CollectionDetailModal = ({
  collection,
  onClose,
  onOpenCard
}: {
  collection: FutureCollection;
  onClose: () => void;
  onOpenCard?: (card: CourtCard) => void;
}) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);
  const cardCount = collection.cardCount ?? 8;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-5964" data-mp="t6is4jwunsdj">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="absolute inset-0 bg-[#0B0F19]/92 backdrop-blur-sm" data-id="element-5965" data-mp="y4aq9vo38aq6">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.12),_transparent_50%)]" data-id="element-5966" data-mp="2dl13wfh70dhc" />
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.4,
      type: 'spring',
      bounce: 0.2
    }} className="relative w-full max-w-5xl bg-[#0B0F19] border-2 border-[#D4AF37]/40 p-6 sm:p-10 shadow-2xl overflow-y-auto max-h-[90vh] scrollbar-hide" data-id="element-5967" data-mp="1wnyhanbvwy7">
        <CornerOrnaments data-id="element-5968" data-mp="36bw1mhcvamue" />
        <button onClick={onClose} className="absolute top-4 right-4 p-2 text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors z-10" aria-label="Close" data-id="element-5969" data-mp="36d77cmpvi4o5">
          <X className="w-6 h-6" data-id="element-5970" data-mp="14l2ftccad9dy" />
        </button>

        <div className="text-center mb-8" data-id="element-5971" data-mp="14igcjowfhg1j">
          <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.35em] uppercase mb-3" data-id="element-5972" data-mp="zlg6hdzkwdaf">
            {collection.sub}
          </p>
          <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-4" data-id="element-5973" data-mp="1iornv655qgb7">
            {collection.name}
          </h3>
          <div className="flex justify-center mb-5" data-id="element-5974" data-mp="1niw0a4x6zgjo">
            <RarityBadge rarity={collection.rarity} data-id="element-5975" data-mp="19qpm2odlnjmv" />
          </div>
          <p className="max-w-2xl mx-auto text-[#FDFBF7]/65 text-sm font-light leading-relaxed" data-id="element-5976" data-mp="1eohj1wt3pvxx">
            {collection.description ?? 'This collection is being assembled in the Treasury workshop. Cards will be revealed in upcoming drops. Members at this rank receive priority access to first editions.'}
          </p>
        </div>

        <OrnateDivider className="mb-8" data-id="element-5977" data-mp="1sfmgqtcwse5m" />

        {collection.cards && collection.cards.length > 0 ? <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6" data-id="element-5978" data-mp="1xh1zgerm68ku">
              {collection.cards.map((card, i) => <FlipCard key={i} card={card} onOpenModal={() => {
            if (onOpenCard) {
              onClose();
              setTimeout(() => onOpenCard(card), 250);
            }
          }} data-id="element-5979" data-mp="m0byba21s6y3" data-mpi={i} />)}
            </div>
            <p className="text-center text-[#D4AF37]/50 text-[10px] tracking-[0.3em] uppercase mt-10" data-id="element-5980" data-mp="nf4wkf7s5z5a">
              Hover to flip · Tap a card for the full lore
            </p>
          </> : <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-5981" data-mp="3mwcq8mplx9fr">
              {[...Array(cardCount)].map((_, i) => <div key={i} className="flex flex-col gap-2" data-id="element-5982" data-mp="4jet0nk78upa" data-mpi={i}>
                  <div className="relative aspect-[4/5] border border-[#D4AF37]/15 border-dashed bg-[#131B2B] overflow-hidden" data-id="element-5983" data-mp="3vx10d6bvqpnd">
                    <div className="absolute inset-3 border border-[#D4AF37]/10 rounded-t-full opacity-60" data-id="element-5984" data-mp="386oppnp92et3" />
                    <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-5985" data-mp="337bxsojiuhjn">
                      <Lock className="w-5 h-5 text-[#D4AF37]/40 mb-2" data-id="element-5986" data-mp="3gw5f92ziq6f8" />
                      <p className="text-[8px] uppercase tracking-[0.25em] text-[#D4AF37]/50" data-id="element-5987" data-mp="3ltnn0q1sufln">
                        In Production
                      </p>
                    </div>
                  </div>
                  <p className="font-serif text-xs text-[#FDFBF7]/40 text-center" data-id="element-5988" data-mp="3vk8ja6u0381e">
                    Card {String(i + 1).padStart(2, '0')}
                  </p>
                </div>)}
            </div>
            <p className="text-center text-[#D4AF37]/50 text-[10px] tracking-[0.3em] uppercase mt-8" data-id="element-5989" data-mp="8e94frx04fnn">
              Drop schedule announced to members first
            </p>
          </>}
      </motion.div>
    </div>;
};
const FlipCard = ({
  card,
  onOpenModal
}: {
  card: CourtCard;
  onOpenModal: () => void;
}) => {
  const [isFlipped, setIsFlipped] = useState(false);
  if (card.isLocked) {
    return <div className="flex flex-col gap-3" data-id="element-5990" data-mp="2k5d48jrfz4kb">
        <div className="relative aspect-[4/5] border border-dashed border-[#D4AF37]/20 bg-[#131B2B] flex flex-col items-center justify-center" data-id="element-5991" data-mp="119gs3ietlf3q">
          <Lock className="w-5 h-5 text-[#D4AF37]/40 mb-2" data-id="element-5992" data-mp="wi76qu0b0xxn" />
          <p className="text-[8px] uppercase tracking-[0.25em] text-[#D4AF37]/50" data-id="element-5993" data-mp="sgvmz54436xd">
            Locked
          </p>
        </div>
        <p className="font-serif text-xs text-[#FDFBF7]/40 text-center" data-id="element-5994" data-mp="njloz12npmxx">
          {card.name}
        </p>
      </div>;
  }
  return <div className="flex flex-col gap-3 cursor-pointer group" onMouseEnter={() => setIsFlipped(true)} onMouseLeave={() => setIsFlipped(false)} onClick={onOpenModal} data-id="element-5995" data-mp="1k6gtpz997rqd">
      <div className="relative aspect-[4/5] perspective-1000" data-id="element-5996" data-mp="1b9zhstt2fptk">
        <motion.div className="relative w-full h-full preserve-3d" animate={{
        rotateY: isFlipped ? 180 : 0
      }} transition={{
        duration: 0.6
      }} data-id="element-5997" data-mp="1680etl18mux5">
          {/* Front */}
          <div className="absolute inset-0 backface-hidden border border-[#D4AF37]/30 bg-[#131B2B] overflow-hidden" data-id="element-5998" data-mp="3sg6lg2d4cvbr">
            <img src={card.img} alt={card.name} className="w-full h-full object-cover" data-id="element-5999" data-mp="1aeytcyjw0tp" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-transparent to-transparent" data-id="element-6000" data-mp="3jezzhdrpasb4" />
            <div className="absolute bottom-0 left-0 right-0 p-3" data-id="element-6001" data-mp="3ocrvojzdp0l4">
              <p className="font-serif text-sm text-[#FDFBF7] leading-tight" data-id="element-6002" data-mp="39oni1uf6nf97">
                {card.name}
              </p>
              {card.role && <p className="text-[8px] tracking-[0.2em] uppercase text-[#D4AF37]/70 mt-1" data-id="element-6003" data-mp="30p0ile60kjis">
                  {card.role}
                </p>}
            </div>
          </div>
          {/* Back */}
          <div className="absolute inset-0 backface-hidden border border-[#D4AF37]/40 bg-[#0B0F19] p-4 flex flex-col" style={{
          transform: 'rotateY(180deg)'
        }} data-id="element-6004" data-mp="2qr44qelozagz">
            <div className="flex justify-between items-start mb-3" data-id="element-6005" data-mp="2c56vd829q2th">
              <RarityBadge rarity={card.rarity} data-id="element-6006" data-mp="275ap1263lzar" />
            </div>
            <p className="font-serif text-sm text-[#FDFBF7] mb-2 leading-tight" data-id="element-6007" data-mp="2775can4ue3i0">
              {card.name}
            </p>
            {card.oneLineLore && <p className="text-[10px] text-[#FDFBF7]/60 italic font-light leading-relaxed mb-3" data-id="element-6008" data-mp="2h271fiz4b5kl">
                "{card.oneLineLore}"
              </p>}
            <div className="mt-auto pt-2 border-t border-[#D4AF37]/20" data-id="element-6009" data-mp="2q3wv6vgh2glj">
              <p className="text-[8px] tracking-[0.25em] uppercase text-[#D4AF37] text-center" data-id="element-6010" data-mp="2q1u56bfbkngt">
                Tap for full lore
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
};
// ---------- Collections Data ----------
const lahoreCourtCollection: CourtCard[] = [{
  img: 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png',
  name: 'Maharaja Ranjit Singh',
  rarity: "Founders' Edition",
  role: 'Sher-e-Punjab',
  type: 'Portrait',
  set: 'Lahore Court',
  oneLineLore: 'The foundational portrait of Lahore Treasury.',
  collectibleDescription: 'Maharaja Ranjit Singh stands at the centre of the entire world: not as a fantasy emperor, but as the one-eyed sovereign who turned Lahore into the heart of Sikh power. This card captures him as calm, weathered, restrained, and impossible to ignore. His authority is not loud. It is gravitational. The pearls, ivory court dress, jewelwork, and Lahore backdrop all point to a ruler whose strength came from coalition-building, diplomacy, military discipline, and symbolic command.',
  historicalMeaning: 'Ranjit Singh unified the Sikh misls and built the Sikh Empire with Lahore as its capital. His court became a space where Sikh, Muslim, Hindu, European, Punjabi, Persianate, and regional traditions intersected. He is remembered as Sher-e-Punjab — the Lion of Punjab — but the best version of this card should emphasize his intelligence as much as his power.',
  whyItIsRare: 'This is a Founders’ Edition because it is the anchor card of the whole system. Without Ranjit Singh, there is no Lahore Treasury. This card should be treated like card #001: the sovereign key to the collection.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/nVFG4hPLbQSgSkuEzm99Kr/Jind_Kaur.png',
  name: 'Maharani Jind Kaur',
  rarity: "Founders' Edition",
  role: 'Regent of Punjab',
  type: 'Portrait',
  set: 'Lahore Court',
  oneLineLore: 'The regent flame of the Khalsa court: composed, elegant, politically aware, and unbroken.',
  collectibleDescription: 'Maharani Jind Kaur appears as the regent flame of the Khalsa court: composed, elegant, politically aware, and unbroken. This card should feel powerful without making her look ornamental. She is not merely royal decoration. She is authority under pressure. The strongest version shows her in refined court dress, surrounded by manuscripts, jewels, and chamber architecture — a woman of strategy, inheritance, and resistance.',
  historicalMeaning: 'Maharani Jind Kaur, the youngest wife of Maharaja Ranjit Singh and mother of Maharaja Duleep Singh, became regent after Ranjit Singh’s death. Her life carries the drama of empire, loss, colonial pressure, exile, and memory. She is one of the most important female figures connected to the fall and afterlife of the Sikh Empire.',
  whyItIsRare: 'Her card deserves Founders’ Edition treatment because she is essential to the story of sovereignty after Ranjit Singh. She represents continuity, resistance, and the cost of imperial collapse. For collectors, this is not just a portrait. It is a card of endurance.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/ekyaLxoHxw9b6NSM5wgnik/Hari_Singh_Nalwa.png',
  name: 'Hari Singh Nalwa',
  rarity: 'Epic',
  role: 'Commander-in-Chief',
  type: 'Portrait',
  set: 'Lahore Court',
  oneLineLore: 'The frontier prestige card. His presence should feel martial, strategic, and severe.',
  collectibleDescription: 'Hari Singh Nalwa is the frontier prestige card. His presence should feel martial, strategic, and severe — the empire’s shield facing west. The portrait uses battlefield depth, dark blue court textiles, gold military detail, and frontier architecture to frame him as both commander and legend. This is one of the most naturally collectible warrior cards in the whole system.',
  historicalMeaning: 'Hari Singh Nalwa was one of the most famous commanders of the Sikh Empire. He is closely associated with campaigns and administration along the northwestern frontier, including Peshawar and Jamrud. His reputation became almost mythic because he represented the empire’s ability to project power into contested frontier zones.',
  whyItIsRare: 'This card should be Epic because Nalwa is not simply a court figure. He is a symbol of military reach. He belongs to the tier of cards that collectors will want for strength, courage, and prestige.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/h1jugirBbdMAbGN9UBoKma/Akali_Phula_Singh.png',
  name: 'Akali Phula Singh',
  rarity: 'Epic',
  role: 'Jathedar · Akal Takht',
  type: 'Portrait',
  set: 'Lahore Court',
  oneLineLore: 'He represents religious conviction, martial discipline, and the fierce independence of the Akali tradition.',
  collectibleDescription: 'Akali Phula Singh’s card should feel sharper and more austere than the other court portraits. He represents religious conviction, martial discipline, and the fierce independence of the Akali tradition. The blue and saffron visual language works well here: deep indigo for discipline, gold for authority, and weaponry as a sign of duty rather than ornament.',
  historicalMeaning: 'Akali Phula Singh was a prominent Nihang/Akali leader and military figure associated with the Sikh Empire. His identity sits at the intersection of faith, martial culture, and sovereign obligation. He was not a polished courtier. He carried the energy of the Panth’s warrior tradition into the imperial world.',
  whyItIsRare: 'This card should be Epic because it represents sacred martial authority, not just court rank. Collectors should feel this is a card of discipline, fearlessness, and spiritual force.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png',
  name: 'Moran Sarkar',
  rarity: 'Treasury Rare',
  role: 'Royal Consort',
  type: 'Portrait',
  set: 'Lahore Court',
  oneLineLore: 'She should feel composed, intelligent, and magnetic.',
  collectibleDescription: 'Moran Sarkar’s card adds beauty, artistry, and cultural sophistication to Lahore Treasury. She should feel composed, intelligent, and magnetic — not generic, not over-sexualized, and not reduced to performance. Her card works best as a chamber-culture collectible: music, poetry, fragrance, candlelight, embroidered textiles, and inner court refinement. It represents the cultural life of Lahore beyond military power.',
  historicalMeaning: 'Moran Sarkar is remembered as a dancer and companion associated with Maharaja Ranjit Singh’s court. Her story opens a different doorway into the empire: not armies and treaties, but music, patronage, intimacy, performance, and courtly culture.',
  whyItIsRare: 'This is Treasury Rare because it captures a softer but very powerful part of the court. Many people will collect warriors and rulers. Moran’s card gives the collection depth, elegance, and emotional contrast.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/4QNmccUw9BGQnbMJ8r6PyR/Fakir_Azzizuddin.png',
  name: 'Fakir Azizuddin',
  rarity: 'Treasury Rare',
  role: 'Foreign Minister',
  type: 'Portrait',
  set: 'Lahore Court',
  oneLineLore: 'The card of diplomacy, counsel, and administrative intelligence.',
  collectibleDescription: 'Fakir Azizuddin is the card of diplomacy, counsel, and administrative intelligence. His portrait should feel learned, composed, and quietly powerful — a man whose influence operated through words, judgment, and trust. The manuscripts, books, lamps, and court setting make this card feel like a piece of political memory rather than a battlefield trophy.',
  historicalMeaning: 'Fakir Azizuddin was one of Maharaja Ranjit Singh’s most trusted ministers and diplomats. He played an important role in the Lahore Darbar’s external relations and helped represent the sophistication of the court’s political machinery.',
  whyItIsRare: 'This is Treasury Rare because his value is not obvious to casual viewers but essential to anyone who understands how empires actually function. Armies win battles; advisers keep states alive.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/5exEPKf1ian4PFKh4zGwAK/Lehna_Singh_Majathia.png',
  name: 'Lehna Singh Majithia',
  rarity: 'Treasury Rare',
  role: 'Governor · Inventor',
  type: 'Portrait',
  set: 'Lahore Court',
  oneLineLore: 'Elite governance, aristocratic service, and strategic continuity.',
  collectibleDescription: 'Lehna Singh Majithia’s card represents elite governance, aristocratic service, and strategic continuity within the Sikh Empire. The card should feel refined, intelligent, and administrative rather than purely martial. This is a court-insider collectible — the kind of card that rewards people who go deeper than the headline figures.',
  historicalMeaning: 'The Majithia family was important within Sikh political and military networks. Lehna Singh Majithia is associated with the aristocratic and administrative world that helped sustain the empire’s institutions.',
  whyItIsRare: 'This is Treasury Rare because it is a knowledge card. It signals that the collector understands the deeper machinery of the Lahore Court, not just the famous names.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/t7XKonR6TRn3gfVAMxWwao/Dhian_Singh_Dhogra.png',
  name: 'Dhian Singh Dogra',
  rarity: 'Treasury Rare',
  role: 'Prime Minister',
  type: 'Portrait',
  set: 'Lahore Court',
  oneLineLore: 'High office, ambition, influence, and the complicated inner politics of the Lahore Darbar.',
  collectibleDescription: 'Dhian Singh Dogra’s card should feel politically charged. He represents high office, ambition, influence, and the complicated inner politics of the Lahore Darbar. Visually, this card should be regal but slightly tense — a court figure whose power came from proximity to the throne.',
  historicalMeaning: 'Dhian Singh Dogra was a major noble and court figure under Maharaja Ranjit Singh. The Dogra brothers played significant roles in the politics of the Sikh Empire, especially during the unstable succession period after Ranjit Singh’s death.',
  whyItIsRare: 'This is Treasury Rare because it belongs to the politics-behind-the-throne layer of the collection. It is not just a portrait. It is a card of influence, court strategy, and risk.'
}];
const foundersSealsCollection: CourtCard[] = [{
  img: 'https://cdn.magicpatterns.com/uploads/ecLy6LTMHkK3K5wmXXnV8A/Royal_Seal_of_Maharaja_Ranjit_Singh.png',
  name: 'Royal Seal of Ranjit Singh',
  rarity: "Founders' Edition",
  type: 'Seal',
  set: "Founders' Seals",
  oneLineLore: 'The sovereign mark of the Lahore Treasury system.',
  collectibleDescription: 'This is the sovereign mark of the Lahore Treasury system. The emerald-like signet, wax impression, and royal document setting make it feel like a direct artifact from the court’s inner archive. The card’s power comes from restraint: one seal, one name, one sovereign presence.',
  historicalMeaning: 'Maharaja Ranjit Singh’s seal is associated with sovereign authority and the phrase “Akal Sahai” — with the help of the Timeless One. This linked rulership to divine assistance rather than personal vanity.',
  whyItIsRare: 'This is Founders’ Edition because it is the signature of sovereignty. It should function as one of the most important non-portrait collectibles in the entire system.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/8bHt2Mjm6ohqpqMMqE5QjE/Seal_of_Lahore_Darbar.png',
  name: 'Seal of the Lahore Darbar',
  rarity: 'Treasury Rare',
  type: 'Seal',
  set: "Founders' Seals",
  oneLineLore: 'This card represents the authority of the court itself.',
  collectibleDescription: 'This card represents the authority of the court itself. Unlike the personal seal of the Maharaja, this is the voice of the Darbar — the institution, the chamber, the administrative machine. The composition of documents, wax, and court instruments makes it feel procedural, official, and intimate.',
  historicalMeaning: 'The Lahore Darbar was not just a royal court. It was the political centre of the Sikh Empire, where petitions, appointments, diplomatic decisions, revenue matters, and military administration flowed.',
  whyItIsRare: 'This is Treasury Rare because it represents institutional authority. Collectors who hold this card are not just collecting a ruler. They are collecting the machinery of rule.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/doWCiDvV7FAzbmKrT4JQRB/Mohr_of_Khalsa_Sarkar.png',
  name: 'Mohr of Khalsa Sarkar',
  rarity: 'Epic',
  type: 'Seal',
  set: "Founders' Seals",
  oneLineLore: 'This is the state-power seal.',
  collectibleDescription: 'This is the state-power seal. It should feel more severe than decorative: metal, wax, paper, dark velvet, and the Khalsa symbol of command. This collectible works because it connects sacred identity with state authority.',
  historicalMeaning: '“Khalsa Sarkar” refers to the Sikh state under the Lahore Darbar. The phrase carries the idea that sovereignty was not merely personal rule, but bound to the Khalsa and the authority of the Panth.',
  whyItIsRare: 'It is Epic because it rises above individual court identity. It represents statehood itself: sovereignty under the Khalsa name.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/4hxNpSJLoi4bC8T9wwuQC5/Akal_Sahai_Signet.png',
  name: 'Akal Sahai Signet',
  rarity: 'Treasury Night Exclusive',
  type: 'Seal / Signet',
  set: "Founders' Seals",
  oneLineLore: 'A night-unlock object: intimate, luminous, and spiritually charged.',
  collectibleDescription: 'The Akal Sahai Signet is a night-unlock object: intimate, luminous, and spiritually charged. The emerald face, red wax, pearl strands, and shadowed court setting make it feel like a relic revealed under candlelight.',
  historicalMeaning: '“Akal Sahai” means “with the help of the Timeless One.” As a phrase of sovereign authority, it links rule, legitimacy, and divine protection.',
  whyItIsRare: 'This should be a Treasury Night Exclusive because it feels ceremonial. It is the kind of object that should only unlock through attendance, participation, or founder-level access.'
}];
const courtTreasuresCollection: CourtCard[] = [{
  img: 'https://cdn.magicpatterns.com/uploads/jyrMYyNM9JskmMzEnBrLTw/Koh-i-noor_Armlet.png',
  name: 'Koh-i-Noor Armlet',
  rarity: "Founders' Edition",
  type: 'Treasure / Regalia',
  set: 'Court Treasures',
  oneLineLore: 'The jewel card of the entire treasury.',
  collectibleDescription: 'The Koh-i-Noor Armlet is the jewel card of the entire treasury. It should feel heavy, luminous, and dangerous — beauty carrying political consequence. The card’s power comes from placing the diamond within a court object, not simply showing the stone. The armlet implies possession, sovereignty, ceremony, and loss.',
  historicalMeaning: 'The Koh-i-Noor became associated with Maharaja Ranjit Singh after it came into his possession from Shah Shuja. It later became one of the most famous symbols of imperial transfer, colonial possession, and contested memory.',
  whyItIsRare: 'This is Founders’ Edition because the Koh-i-Noor is not just a jewel. It is one of the most recognizable symbols of sovereignty connected to Lahore. It should be among the highest-status cards.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/itiARdFbZcdkmdrUEtYH65/Golden_Throne_of_Lahore.png',
  name: 'Golden Throne of Lahore',
  rarity: 'Treasury Night Exclusive',
  type: 'Court Object',
  set: 'Court Treasures',
  oneLineLore: 'The seat of sovereign splendour.',
  collectibleDescription: 'The Golden Throne of Lahore is the seat of sovereign splendour. This card should feel formal, quiet, and powerful — the throne waiting before or after the ruler appears. It is effective because absence creates drama. The empty throne invites the collector to imagine the court around it.',
  historicalMeaning: 'The Sikh Empire’s visual culture included courtly seating, ceremonial display, regalia, and spatial hierarchy. A throne object represents not only the Maharaja but the structure of power around him.',
  whyItIsRare: 'This is Treasury Rare because it represents the physical theatre of sovereignty. As a Treasury Night Exclusive, it can also function as a premium unlock tied to live-event status.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/k7bSK4nqttzFAiN3UnZZs2/Sarpech_of_Soverign_Court.png',
  name: 'Sarpech of the Sovereign Court',
  rarity: 'Treasury Rare',
  type: 'Regalia',
  set: 'Court Treasures',
  oneLineLore: 'Pure prestige. Pearls, feathers, goldwork, emeralds.',
  collectibleDescription: 'The sarpech card is pure prestige. Pearls, feathers, goldwork, emeralds, and a central jewel make it feel ceremonial and immediately collectible. This is not a weapon. It is a mark of presence. It says: the wearer has entered the court with rank.',
  historicalMeaning: 'Turban ornaments and jeweled headpieces were important signs of courtly status across South Asian royal cultures. In the Lahore Court context, the sarpech signals elegance, rank, and refined authority.',
  whyItIsRare: 'This is Treasury Rare because it is symbolic rather than functional. It is for collectors drawn to elegance, court rank, and status.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/cP1VhHHXb1qWwnqErsLGjY/Sword_of_Akal_Sahai.png',
  name: 'Sword of Akal Sahai',
  rarity: 'Epic',
  type: 'Regalia / Weapon',
  set: 'Court Treasures',
  oneLineLore: 'The blade of sovereign resolve.',
  collectibleDescription: 'The Sword of Akal Sahai is the blade of sovereign resolve. It should feel ceremonial but capable — a weapon that belongs equally to court, oath, and command. The best version is not over-bloody or aggressive. It is polished, engraved, devotional, and severe.',
  historicalMeaning: 'Inscribed blades and ceremonial swords were part of South Asian martial and court culture. In a Sikh Empire context, the sword also carries deep symbolic meaning connected to justice, sovereignty, and responsibility.',
  whyItIsRare: 'This is Epic because it fuses spiritual authority with martial power. It is not just a beautiful object. It is a statement of rule under duty.'
}];
const courtScenesCollection: CourtCard[] = [{
  img: 'https://cdn.magicpatterns.com/uploads/i8yqzvFJ6TFd6dTb6YJFfy/Lahor_Darbar.png',
  name: 'The Lahore Darbar',
  rarity: "Founders' Edition",
  type: 'Court Scene',
  set: 'Court Scenes',
  oneLineLore: 'The central scene card: the court as theatre, government, petition chamber, and sovereign stage.',
  collectibleDescription: 'The Lahore Darbar is the central scene card: the court as theatre, government, petition chamber, and sovereign stage. Ranjit Singh sits within a living environment of advisers, petitioners, scribes, guards, and architecture. It feels like the viewer has stepped into the moment before a decision is made.',
  historicalMeaning: 'The Lahore Darbar was the governing centre of the Sikh Empire. It was where royal authority, administration, diplomacy, and public petition intersected.',
  whyItIsRare: 'This is Founders’ Edition because it gives the entire project its core setting. It is the court itself — the place every other collectible returns to.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/ndFukn5EhjaGeCm7W2a9F8/Council_of_Empire.png',
  name: 'Council of Empire',
  rarity: 'Treasury Rare',
  type: 'Court Scene',
  set: 'Court Scenes',
  oneLineLore: 'A quieter, smarter card. It shows maps, manuscripts, advisers, and strategic conversation.',
  collectibleDescription: 'Council of Empire is a quieter, smarter card. It shows maps, manuscripts, advisers, and strategic conversation. This card is collectible because it captures power before action. Not war. Not spectacle. Deliberation.',
  historicalMeaning: 'The Sikh Empire was held together through military force, diplomacy, revenue administration, and elite negotiation. Council scenes represent the planning layer behind imperial stability.',
  whyItIsRare: 'This is Treasury Rare because it is a strategy card. It will appeal to collectors who value intelligence, counsel, and statecraft.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/qXUGTTLzFDWU3yPZqkhw1f/Review_of_the_Khalsa.png',
  name: 'Review of the Khalsa',
  rarity: 'Epic',
  type: 'Court Scene / Martial Scene',
  set: 'Court Scenes',
  oneLineLore: 'A parade of discipline and power.',
  collectibleDescription: 'Review of the Khalsa is a parade of discipline and power. The ruler on horseback, the standards, the soldiers, and the fortress backdrop create a card of imperial confidence. This is one of the most visually accessible cards for general audiences.',
  historicalMeaning: 'Military reviews and ceremonial displays communicated order, readiness, and royal authority. The Khalsa army was a central force in the rise and defence of the Sikh Empire.',
  whyItIsRare: 'This is Epic because it shows collective power. It is not one hero. It is the disciplined force of the empire.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/2NoPLkWP9pP8MsVwhpJBvm/Mehfil_of_Moran_Sarkar.png',
  name: 'Mehfil of Moran Sarkar',
  rarity: 'Live Night Edition',
  type: 'Court Scene / Chamber Culture',
  set: 'Court Scenes',
  oneLineLore: 'This card captures the inner atmosphere of the court.',
  collectibleDescription: 'This card captures the inner atmosphere of the court: music, candlelight, poetry, refined performance, and social presence. It should feel intimate and cinematic — a collectible that could connect directly to a live event, musical moment, or sensory unlock.',
  historicalMeaning: 'Courts were not only military and administrative spaces. They were centres of patronage, music, poetry, conversation, and cultural refinement.',
  whyItIsRare: 'This is a Live Night Edition because it naturally belongs to an event experience. It can unlock during a musical moment, performance sequence, or chamber-culture activation.'
}];
const memoryFragmentsCollection: CourtCard[] = [{
  img: 'https://cdn.magicpatterns.com/uploads/pUc7dhUDTQ8ibLS8igHkeA/Last_Light_of_Lahore.png',
  name: 'Last Light of Lahore',
  rarity: 'Epic',
  type: 'Memory Fragment',
  set: 'Fracture & Memory',
  oneLineLore: 'An empty chamber at sunset. The moment after sound leaves the court.',
  collectibleDescription: 'An empty chamber at sunset. A throne-like chair. Documents left behind. Lahore visible in the distance. This card captures the moment after sound leaves the court.',
  historicalMeaning: 'After Ranjit Singh’s death, the Sikh Empire entered a turbulent period of succession struggles, political instability, British pressure, and eventual annexation. This card symbolizes that fading light.',
  whyItIsRare: 'This is Epic because it carries emotional scale. It is not an object. It is the feeling of an era ending.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/mpWTPqMtxSXCLW7wBLeGe9/Letter_from_Exile.png',
  name: 'Letter from Exile',
  rarity: 'Treasury Rare',
  type: 'Memory Fragment',
  set: 'Fracture & Memory',
  oneLineLore: 'A voice carried across distance.',
  collectibleDescription: 'A sealed letter, pearls, cloth, portrait, and shadow. This is one of the most intimate cards in the set. It should feel like a voice carried across distance.',
  historicalMeaning: 'This card can connect to Maharani Jind Kaur, exile, separation, colonial displacement, and the afterlife of the Sikh Empire in memory and correspondence.',
  whyItIsRare: 'This is Treasury Rare because it is emotionally specific. It is for collectors who care about voice, absence, and survival.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/xqJSFqG4cjYr84cTwzpgB1/Broken_Seal_of_the_Khalsa.png',
  name: 'Broken Seal of the Khalsa',
  rarity: 'Live Night Edition',
  type: 'Memory Fragment / Seal',
  set: 'Fracture & Memory',
  oneLineLore: 'Even when institutions break, memory remains collectible.',
  collectibleDescription: 'A cracked wax seal, torn papers, and scattered authority. This is one of the strongest symbolic cards. It shows that even when institutions break, memory remains collectible.',
  historicalMeaning: 'The broken seal represents the collapse of sovereign authority after the fall of the Sikh Empire and the absorption of Punjab into British rule.',
  whyItIsRare: 'This should be Live Night Edition because it can be unlocked at the emotional turning point of the immersive experience.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/5yTx3Dg8JyVsPEfTEouexU/Desparting_the_Koh-i-Noor.png',
  name: 'The Departing Koh-i-Noor',
  rarity: 'Treasury Night Exclusive',
  type: 'Memory Fragment / Treasure',
  set: 'Fracture & Memory',
  oneLineLore: 'The missing jewel becomes the focal point.',
  collectibleDescription: 'An open jewel box. A missing light. Pearls, papers, and candlelit silence. This card is powerful because the object is absent. The missing jewel becomes the focal point.',
  historicalMeaning: 'The Koh-i-Noor’s removal from Lahore became one of the most famous symbols of imperial loss and contested heritage.',
  whyItIsRare: 'This is Treasury Night Exclusive because it should feel almost sacred to the live experience. It is not just a card. It is a moment of loss.'
}];
const sacredAuthorityCollection: CourtCard[] = [{
  img: 'https://cdn.magicpatterns.com/uploads/vRFN3KMcBvDZegNaWmJsBE/Hukamnama_of_the_Akal_Court.png',
  name: 'Hukamnama of the Akal Court',
  rarity: "Founders' Edition",
  type: 'Sacred Document',
  set: 'Sacred Authority',
  oneLineLore: 'A sacred command, resting under candlelight.',
  collectibleDescription: 'A sacred command, resting under candlelight. The card should feel quiet, elevated, and archival.',
  historicalMeaning: 'A hukamnama is a written order or command, and in Sikh contexts it carries sacred and institutional significance. This card links word, authority, and responsibility.',
  whyItIsRare: 'This is Founders’ Edition because it establishes that Lahore Treasury is not only royal. It is rooted in sacred authority.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/aP3xY1r5KWz2UUGdM4Kfc1/Nishan_Sahib_of_Lahore.png',
  name: 'Nishan Sahib of Lahore',
  rarity: 'Epic',
  type: 'Sacred Banner',
  set: 'Sacred Authority',
  oneLineLore: 'The symbol of timeless allegiance.',
  collectibleDescription: 'The Nishan Sahib card is visually bold: saffron flag, sky, Lahore backdrop, and the symbol of timeless allegiance.',
  historicalMeaning: 'The Nishan Sahib is the Sikh flag. It is a sacred sign of Sikh presence, identity, and collective commitment.',
  whyItIsRare: 'This is Epic because it is not merely an object. It is a banner of identity. It carries collective force.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/fk48rB7Xh8vfhU38qhWpYz/Chaur Sahib of Reverence.png',
  name: 'Chaur Sahib of Reverence',
  rarity: 'Treasury Rare',
  type: 'Sacred Object',
  set: 'Sacred Authority',
  oneLineLore: 'Graceful, tactile, and devotional.',
  collectibleDescription: 'The Chaur Sahib card is graceful, tactile, and devotional. The white hair, jeweled handle, velvet, and soft light create a card of service.',
  historicalMeaning: 'The Chaur Sahib is waved as a mark of reverence before Guru Granth Sahib. It symbolizes honour and humility.',
  whyItIsRare: 'This is Treasury Rare because it represents service before sovereignty. It teaches the hierarchy of values in the world.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/3j4pU1Rts4tctWKYUbVdyS/Palki_of_Soverign_Devotion.png',
  name: 'Palki of Sovereign Devotion',
  rarity: 'Treasury Night Exclusive',
  type: 'Sacred Object / Ceremonial Object',
  set: 'Sacred Authority',
  oneLineLore: 'The canopy of presence and honour.',
  collectibleDescription: 'The Palki card is visually rich: gold, canopy, cushion, embroidered cloth, and sacred presence. This should be treated with the most care.',
  historicalMeaning: 'The Palki Sahib is associated with the respectful placement and procession of Guru Granth Sahib. It represents devotion, honour, and ceremonial reverence.',
  whyItIsRare: 'This is Treasury Night Exclusive because it should be tied to a respectful, carefully designed live unlock — not a casual collectible drop.'
}];
const frontierDefendersCollection: CourtCard[] = [{
  img: 'https://cdn.magicpatterns.com/uploads/aSmUF7b6BWtqGCanyn7aaa/Shield_of_Hari_Singh_Nalwa.png',
  name: 'Shield of Hari Singh Nalwa',
  rarity: 'Epic',
  type: 'Martial Object',
  set: 'Frontier Defenders',
  oneLineLore: 'A shield of the western march.',
  collectibleDescription: 'This is a shield of the western march: dark metal, gold engraving, emerald bosses, and frontier mountains behind it.',
  historicalMeaning: 'Hari Singh Nalwa’s legacy is closely tied to frontier defence and the projection of Sikh power toward the northwest.',
  whyItIsRare: 'This is Epic because it represents defence at the empire’s edge. It is protective, iconic, and commander-linked.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/5ufBpHR9ZV9nnv2ZeAwmvQ/Jamrud_War_Standard.png',
  name: 'Jamrud War Standard',
  rarity: 'Treasury Rare',
  type: 'War Standard',
  set: 'Frontier Defenders',
  oneLineLore: 'A banner of the western gate.',
  collectibleDescription: 'The Jamrud War Standard is a banner of the western gate. It should feel wind-worn, ceremonial, and battle-ready.',
  historicalMeaning: 'Jamrud Fort and its frontier context are tied to the Sikh Empire’s western military story. A war standard turns place into symbol.',
  whyItIsRare: 'This is Treasury Rare because standards are identity objects. They gather soldiers, signal allegiance, and mark territory.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/8onDaZ9cdUq8uHFDtGepEP/Frontier_Matchlock_of_the_Khalsa.png',
  name: 'Frontier Matchlock of the Khalsa',
  rarity: 'Rare',
  type: 'Firearm / Martial Object',
  set: 'Frontier Defenders',
  oneLineLore: 'Rugged and practical.',
  collectibleDescription: 'The matchlock card is rugged and practical. It should look used, decorated, and dangerous without becoming modern or generic.',
  historicalMeaning: 'Firearms were part of early modern and 19th-century military culture in South Asia. This card represents the practical technology of frontier conflict.',
  whyItIsRare: 'This is Rare because it grounds the collection in material military reality. Not every martial object is a jewel. Some are tools of survival.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/grqP9Jkk1cJNAx6sRiboXR/Helm_of_the_Khyber_March.png',
  name: 'Helm of the Khyber March',
  rarity: 'Live Night Edition',
  type: 'Armour',
  set: 'Frontier Defenders',
  oneLineLore: 'A dramatic helmet card.',
  collectibleDescription: 'This is a dramatic helmet card: chainmail, steel, goldwork, dark plume, frontier fort in the distance.',
  historicalMeaning: 'Armour and helmets represent the personal risk of martial leadership and the harshness of frontier defence.',
  whyItIsRare: 'This is Live Night Edition because it has strong costume, photo-op, and immersive-event potential. It can connect to turban, armour, or warrior identity experiences.'
}];
const lahoreCourtInnerLifeCollection: CourtCard[] = [{
  img: 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png',
  name: "The Maharaja's Private Chamber",
  rarity: 'Treasury Rare',
  type: 'Scene',
  set: 'Lahore Court Inner Life',
  oneLineLore: 'Where the Lion of Punjab rested and reflected.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/nVFG4hPLbQSgSkuEzm99Kr/Jind_Kaur.png',
  name: "Maharani's Mirror",
  rarity: 'Epic',
  type: 'Artifact',
  set: 'Lahore Court Inner Life',
  oneLineLore: 'A reflection of power and poise.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/ekyaLxoHxw9b6NSM5wgnik/Hari_Singh_Nalwa.png',
  name: "Commander's Correspondence",
  rarity: 'Mint',
  type: 'Document',
  set: 'Lahore Court Inner Life',
  oneLineLore: 'Letters from the front, written in haste and honour.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png',
  name: "Court Musician's Instrument",
  rarity: 'Mint',
  type: 'Artifact',
  set: 'Lahore Court Inner Life',
  oneLineLore: 'The strings that sang the stories of the Darbar.'
}];
const otherCollections: FutureCollection[] = [{
  name: "Founders' Seals",
  sub: 'Sovereign Marks',
  rarity: "Founders' Edition",
  description: 'The seals and signets that authorized empire.',
  cards: foundersSealsCollection,
  isLive: true
}, {
  name: 'Court Treasures',
  sub: 'Royal Regalia',
  rarity: "Founders' Edition",
  description: 'Crown jewels, thrones, and ceremonial arms of the Lahore Darbar.',
  cards: courtTreasuresCollection,
  isLive: true
}, {
  name: 'Court Scenes',
  sub: 'Living Tableaux',
  rarity: 'Treasury Rare',
  description: 'Painted moments from the inner life of the empire.',
  cards: courtScenesCollection,
  isLive: true
}, {
  name: 'Fracture & Memory',
  sub: 'The Empire After the Light',
  rarity: 'Treasury Rare',
  description: 'Cards of loss, exile, silence, broken seals, and memory that survives collapse.',
  cards: memoryFragmentsCollection,
  isLive: true
}, {
  name: 'Sacred Authority',
  sub: 'Reverence Before Rule',
  rarity: "Founders' Edition",
  description: 'Spiritual objects and symbols that shaped Sikh sovereignty: hukamnamas, banners, and ceremonial regalia.',
  cards: sacredAuthorityCollection,
  isLive: true
}, {
  name: 'Frontier Defenders',
  sub: 'Martial Prestige Objects',
  rarity: 'Epic',
  description: 'Shields, standards, matchlocks, and armour tied to the western edge of the empire.',
  cards: frontierDefendersCollection,
  isLive: true
}, {
  name: 'Lahore Court Inner Life',
  sub: 'Intimate Spaces',
  rarity: 'Treasury Rare',
  description: 'The private chambers, personal effects, and quiet moments of the royal court.',
  cards: lahoreCourtInnerLifeCollection,
  isLive: true
}];
const upcomingCollections = [{
  name: "The Maharaja's Inner Circle",
  sub: 'Closest confidants'
}, {
  name: 'The Khalsa Army',
  sub: 'Regiments & commanders'
}, {
  name: 'The Royal Archive',
  sub: 'Letters & manuscripts'
}, {
  name: 'The Treasury Vault',
  sub: 'Coins & ingots'
}, {
  name: 'Lost Relics of Empire',
  sub: 'Recovered fragments'
}, {
  name: 'The Darbar Ceremonies',
  sub: 'Rituals of court'
}, {
  name: 'The Cities of the Sarkar',
  sub: 'Capitals & strongholds'
}, {
  name: "The Empire's Edge",
  sub: 'Frontier outposts'
}];
// ---------- Mission Section ----------
const MissionSection = () => <section className="px-4 py-24 bg-[#0B0F19] relative overflow-hidden border-t border-[#D4AF37]/10" data-id="element-6011" data-mp="xav3fr38dn1y">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.06),_transparent_70%)]" data-id="element-6012" data-mp="345i7b2k8ctaz" />
    <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-6013" data-mp="1q7co03g61on3">
      <SectionHeader eyebrow="The Mission" title={<>
            REAL HISTORY.{' '}
            <span className="text-[#D4AF37]" data-id="element-6015" data-mp="1v8a8wnsk8wsa">REAL CONNECTION.</span>
          </>} data-id="element-6014" data-mp="1q74f0mn5z3h6" />
      <div className="relative px-4 sm:px-8 pt-12 sm:pt-16 pb-8 sm:pb-10" data-id="element-6016" data-mp="203piv4ng68jf">
        {/* Massive decorative opening quote */}
        <span aria-hidden="true" className="absolute left-0 top-0 font-serif text-[160px] sm:text-[220px] leading-[0.8] text-[#D4AF37]/25 select-none pointer-events-none" data-id="element-6017" data-mp="17h4t28mme4hz">
          &ldquo;
        </span>

        {/* Quote text */}
        <blockquote className="relative font-serif text-2xl sm:text-3xl lg:text-4xl text-[#FDFBF7] leading-[1.5] tracking-tight max-w-3xl mx-auto text-center" data-id="element-6018" data-mp="1gg9mj0jbr7na">
          Treasures of the Sikh Empire is a digital bridge to the physical past.
          Every artifact, every figure, and every location is{' '}
          <span className="text-[#D4AF37] italic" data-id="element-6019" data-mp="spvxzjzy7qxg">real history</span> from the
          Sikh Kingdoms. By collecting and trading, you aren&apos;t just playing
          a game — you are{' '}
          <span className="text-[#D4AF37] italic" data-id="element-6020" data-mp="g7a071kxad3p">
            recovering a lost legacy
          </span>{' '}
          and building a modern Sangat.
        </blockquote>

        {/* Attribution */}
        <div className="mt-10 sm:mt-12 flex flex-col items-center gap-3" data-id="element-6021" data-mp="7asn3st0055b">
          <span className="h-px w-12 bg-[#D4AF37]/50" data-id="element-6022" data-mp="2adax4kgd3ox" />
          <div className="text-center" data-id="element-6023" data-mp="3toj0z69lxr23">
            <p className="font-serif text-base sm:text-lg text-[#D4AF37] tracking-wide" data-id="element-6024" data-mp="3tnhgkb6pbs7p">
              Ajaypal Singh
            </p>
            <p className="text-[#FDFBF7]/50 text-[10px] sm:text-[11px] tracking-[0.35em] uppercase font-light mt-1.5" data-id="element-6025" data-mp="3omtp5oek99cy">
              Sikh Empire Historian
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>;
// ---------- Collections Section ----------
const CollectionsSection = () => {
  const [openCard, setOpenCard] = useState<CourtCard | null>(null);
  const [openCollection, setOpenCollection] = useState<FutureCollection | null>(null);
  return <section className="px-4 py-24 bg-[#0B0F19] relative border-t border-[#D4AF37]/10 overflow-hidden" data-id="element-6026" data-mp="3626ywfaw4212">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.08),_transparent_60%)]" data-id="element-6027" data-mp="2r5pt03lgzflw" />
      <div className="max-w-7xl mx-auto relative z-10" data-id="element-6028" data-mp="3jsaggsytr7k6">
        <SectionHeader eyebrow="The Collections" title={<>
              THE LAHORE COURT{' '}
              <span className="text-[#D4AF37]" data-id="element-6030" data-mp="3ot7xv3c0fxkm">COLLECTION</span>
            </>} description="The flagship collection — eight figures who defined the Sikh Empire. Each card hand-illustrated, each story rooted in real history." data-id="element-6029" data-mp="3jsso0lqxt87e" />

        {/* Sponsor credit — flagship collection partnership */}
        <div className="flex items-center justify-center gap-4 mb-12 -mt-4" data-id="element-6031" data-mp="bc7bww30dz02">
          <span className="h-px w-12 bg-gradient-to-r from-transparent to-[#D4AF37]/40" data-id="element-6032" data-mp="bebpk5qpmcdj" />
          <div className="flex items-center gap-2.5 px-4 py-2 border border-[#D4AF37]/30 bg-[#131B2B]/60" data-id="element-6033" data-mp="1w0t6xciahgq1">
            <span className="text-[#D4AF37]/60 text-[9px] tracking-[0.3em] uppercase" data-id="element-6034" data-mp="1r1ypadw25y7i">
              Presented in partnership with
            </span>
            <span className="font-serif text-base text-[#D4AF37] tracking-wide" data-id="element-6035" data-mp="20unp2bi8xnn0">
              Akal Steel
            </span>
          </div>
          <span className="h-px w-12 bg-gradient-to-l from-transparent to-[#D4AF37]/40" data-id="element-6036" data-mp="1d64hxzaxngwi" />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 mb-20" data-id="element-6037" data-mp="1879yqw91zqbv">
          {lahoreCourtCollection.map((card, i) => <FlipCard key={i} card={card} onOpenModal={() => setOpenCard(card)} data-id="element-6038" data-mp="1i21vxyyq4b64" data-mpi={i} />)}
        </div>

        <div className="text-center mb-12" data-id="element-6039" data-mp="2tia20add64mj">
          <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-3" data-id="element-6040" data-mp="2thi60yfuu4vq">
            The Other 7 Collections
          </p>
          <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7]" data-id="element-6041" data-mp="qtryskv2u8ra">
            Beyond the <span className="text-[#D4AF37]" data-id="element-6042" data-mp="vuzagv0cbs38">Court</span>
          </h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-6043" data-mp="10s7t0wpehy50">
          {otherCollections.map((col, idx) => {
          const peekCards = (col.cards || []).slice(0, 3);
          const glowColor = col.rarity === "Founders' Edition" ? 'rgba(212,175,55,0.45)' : col.rarity === 'Treasury Rare' ? 'rgba(212,175,55,0.28)' : col.rarity === 'Epic' ? 'rgba(139,0,0,0.35)' : 'rgba(212,175,55,0.18)';
          return <motion.button key={col.name} onClick={() => setOpenCollection(col)} initial={{
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.45,
            delay: idx * 0.05,
            ease: 'easeOut'
          }} whileHover={{
            y: -6
          }} className="text-left border border-[#D4AF37]/20 bg-[#131B2B] hover:border-[#D4AF37]/60 transition-all group relative overflow-hidden flex flex-col" data-id="element-6044" data-mp="3lc6lzjw1asb3" data-mpi={idx}>
                {/* === Hero artwork: card stack peek === */}
                <div className="relative aspect-[5/3] bg-[#0B0F19] overflow-hidden border-b border-[#D4AF37]/15" data-id="element-6045" data-mp="2cf87qc2qa8q6">
                  {/* Rarity-tinted glow */}
                  <div aria-hidden="true" className="absolute inset-0 transition-opacity duration-500 opacity-50 group-hover:opacity-100" style={{
                background: `radial-gradient(ellipse at center, ${glowColor}, transparent 70%)`
              }} data-id="element-6046" data-mp="15wjqfuiewafy" />
                  {/* Subtle dust particles */}
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,_rgba(212,175,55,0.08),_transparent_50%)]" data-id="element-6047" data-mp="821utklmqm0b" />

                  {/* Card stack — fanned, animates on hover */}
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-6048" data-mp="i07udvjyrg20">
                    {peekCards.length > 0 ? peekCards.map((card, i) => {
                  const isCenter = i === 1 || peekCards.length === 1;
                  const offsetX = peekCards.length === 1 ? 0 : (i - 1) * 38;
                  const rotate = peekCards.length === 1 ? 0 : (i - 1) * 7;
                  return <motion.div key={i} initial={false} animate={{
                    x: offsetX,
                    rotate,
                    scale: isCenter ? 1 : 0.92
                  }} whileHover={{}} style={{
                    zIndex: isCenter ? 10 : 5 - i
                  }} className="absolute w-[88px] sm:w-[104px] aspect-[4/5] border border-[#D4AF37]/50 shadow-[0_6px_24px_rgba(0,0,0,0.6)] overflow-hidden bg-[#131B2B] transition-all duration-500 group-hover:[transform-origin:center_bottom]" data-id="element-6049" data-mp="2oqnvdfjxojtw" data-mpi={i}>
                            <img src={card.img} alt={card.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" data-id="element-6050" data-mp="1qw4pscq0i8un" />
                            {/* Card frame glint on hover */}
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19]/40 via-transparent to-transparent" data-id="element-6051" data-mp="3es1cr5w88vdy" />
                          </motion.div>;
                }) : <div className="text-[#D4AF37]/30 text-[10px] tracking-[0.3em] uppercase" data-id="element-6052" data-mp="34x15sk5qdlcq">
                        In Production
                      </div>}
                  </div>

                  {/* Spread cards apart on hover */}
                  <style data-id="element-6053" data-mp="3sru3v2bo1dmg">{`
                    .group:hover [data-fan-i="0"] { transform: translateX(-58px) rotate(-12deg) scale(0.94) !important; }
                    .group:hover [data-fan-i="2"] { transform: translateX(58px) rotate(12deg) scale(0.94) !important; }
                  `}</style>

                  {/* Top-right meta badge */}
                  <div className="absolute top-3 right-3 flex items-center gap-2" data-id="element-6054" data-mp="kb8z5xq2h6p5">
                    {col.isLive && <span className="flex items-center gap-1.5 px-2 py-1 bg-[#0B0F19]/85 backdrop-blur-sm border border-emerald-400/40 text-[8px] tracking-widest uppercase text-emerald-400 font-mono" data-id="element-6055" data-mp="wmrvcy4e5pc9">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" data-id="element-6056" data-mp="16m0mzkbsarvg" />
                        Live
                      </span>}
                    <span className="px-2 py-1 bg-[#0B0F19]/85 backdrop-blur-sm border border-[#D4AF37]/30 text-[8px] tracking-widest uppercase text-[#D4AF37] font-mono" data-id="element-6057" data-mp="1fm5urvkw3sa5">
                      {col.cards?.length ?? 0} Cards
                    </span>
                  </div>

                  {/* Bottom-left rarity badge */}
                  <div className="absolute bottom-3 left-3" data-id="element-6058" data-mp="1ublfouxqp3hx">
                    <RarityBadge rarity={col.rarity} data-id="element-6059" data-mp="1u98pxvlqpoub" />
                  </div>
                </div>

                {/* === Body === */}
                <div className="p-5 flex flex-col flex-1" data-id="element-6060" data-mp="2w0qgh1b0qa8c">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/70 mb-1.5" data-id="element-6061" data-mp="2vz6zt9lg9x61">
                    {col.sub}
                  </p>
                  <h4 className="font-serif text-2xl text-[#FDFBF7] mb-2 group-hover:text-[#D4AF37] transition-colors leading-tight" data-id="element-6062" data-mp="34ssdeplvp8dv">
                    {col.name}
                  </h4>
                  <p className="text-xs text-[#FDFBF7]/55 font-light leading-relaxed mb-4 flex-1" data-id="element-6063" data-mp="2d1w1br7n77rn">
                    {col.description}
                  </p>

                  {/* CTA strip */}
                  <div className="flex items-center justify-between pt-3 border-t border-[#D4AF37]/10" data-id="element-6064" data-mp="2m3vm8uls2vzv">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] font-medium flex items-center gap-2 group-hover:gap-3 transition-all" data-id="element-6065" data-mp="2h3zp9fbi04tt">
                      Step Inside
                      <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1.5" data-id="element-6066" data-mp="3sk7lkz0c6jx1" />
                    </span>
                    <span className="text-[9px] tracking-widest uppercase text-[#FDFBF7]/30 font-mono" data-id="element-6067" data-mp="4thig2c2y248">
                      {String(idx + 1).padStart(2, '0')} /{' '}
                      {String(otherCollections.length).padStart(2, '0')}
                    </span>
                  </div>
                </div>
              </motion.button>;
        })}
        </div>

        {/* Glimpse of upcoming */}
        <div className="mt-24" data-id="element-6068" data-mp="nnmyupfoepwe">
          <div className="text-center mb-10" data-id="element-6069" data-mp="skdc7503j7e4">
            <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-3" data-id="element-6070" data-mp="sk3q3si69kdp">
              On the Horizon
            </p>
            <h3 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-6071" data-mp="iutw273rprmy">
              A Glimpse of <span className="text-[#D4AF37]" data-id="element-6072" data-mp="isqwavctck51">What's Coming</span>
            </h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-6073" data-mp="16ibg1ggsjlfd">
            {upcomingCollections.map(c => <div key={c.name} className="p-4 border border-[#D4AF37]/15 bg-[#131B2B]/40 text-center" data-id="element-6074" data-mp="1bcxvpvtwo059">
                <p className="font-serif text-sm text-[#FDFBF7]/80 mb-1 leading-tight" data-id="element-6075" data-mp="1g9bmlxtj3fyf">
                  {c.name}
                </p>
                <p className="text-[9px] tracking-widest uppercase text-[#D4AF37]/50" data-id="element-6076" data-mp="1bb0bzkvet1f5">
                  {c.sub}
                </p>
              </div>)}
          </div>
        </div>
      </div>

      {openCard && <CardModal card={openCard} onClose={() => setOpenCard(null)} data-id="element-6077" data-mp="xhsjzs3f23wx" />}
      {openCollection && <CollectionDetailModal collection={openCollection} onClose={() => setOpenCollection(null)} onOpenCard={card => setOpenCard(card)} data-id="element-6078" data-mp="shwhjxadakjh" />}
    </section>;
};
// ---------- Rarity Section ----------
const RaritySection = () => {
  const tiers = [{
    name: 'Mint',
    sub: 'Common',
    odds: '~60%',
    copy: 'The plentiful tier — still hand-painted, still part of every story. Most packets contain one.',
    borderClass: 'border-[#FDFBF7]/20',
    accentClass: 'text-[#FDFBF7]/70',
    bgGlow: 'from-[#FDFBF7]/5 to-transparent',
    pill: 'bg-[#FDFBF7]/10 text-[#FDFBF7]/70 border-[#FDFBF7]/20',
    isFlagship: false,
    icon: Layers
  }, {
    name: 'Epic',
    sub: 'Strong narrative',
    odds: '~25%',
    copy: 'Battle scenes, frontier locations, lesser figures of court — the connective tissue of the kingdom.',
    borderClass: 'border-[#8B0000]/50',
    accentClass: 'text-[#8B0000]',
    bgGlow: 'from-[#8B0000]/12 to-transparent',
    pill: 'bg-[#8B0000]/15 text-[#8B0000] border-[#8B0000]/40',
    isFlagship: false,
    icon: Sword
  }, {
    name: 'Treasury Rare',
    sub: 'Prized',
    odds: '~10%',
    copy: 'Consorts, sub-court figures, ceremonial objects. The cards collectors quietly hunt for.',
    borderClass: 'border-[#D4AF37]/40',
    accentClass: 'text-[#D4AF37]/90',
    bgGlow: 'from-[#D4AF37]/8 to-transparent',
    pill: 'bg-[#D4AF37]/10 text-[#D4AF37]/90 border-[#D4AF37]/40',
    isFlagship: false,
    icon: Gem
  }, {
    name: "Founders' Edition",
    sub: 'Cornerstone',
    odds: '~5%',
    copy: 'Founding figures and master seals — the cards that define a Treasury. Lifetime-numbered.',
    borderClass: 'border-[#D4AF37]/80',
    accentClass: 'text-[#D4AF37]',
    bgGlow: 'from-[#D4AF37]/15 to-transparent',
    pill: 'bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/70',
    isFlagship: true,
    icon: Crown
  }, {
    name: 'Live Night Only',
    sub: 'Unobtainable from home',
    odds: 'Venue only',
    copy: 'Earned exclusively at Treasury Nights — scan-to-unlock artifacts, sensory keys, hand-stamped tokens.',
    borderClass: 'border-violet-400/50',
    accentClass: 'text-violet-300',
    bgGlow: 'from-violet-500/15 to-transparent',
    pill: 'bg-violet-400/10 text-violet-300 border-violet-400/50',
    isFlagship: false,
    icon: Moon
  }];
  return <section className="px-4 py-24 bg-[#131B2B] relative border-t border-[#D4AF37]/10 overflow-hidden" data-id="element-6079" data-mp="27ez2jcnrq8bi">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.06),_transparent_60%)]" data-id="element-6080" data-mp="1tffwcpeip71u" />
      <div className="max-w-6xl mx-auto relative z-10" data-id="element-6081" data-mp="1xeyg3hulrc33">
        <SectionHeader eyebrow="The Ladder of Rarity" title={<>
              HOW RARE IS <span className="text-[#D4AF37]" data-id="element-6083" data-mp="1og3cmdiy0g3h">RARE</span>
            </>} description="Five tiers. Each with its own scarcity, its own story, and its own standing in the Treasury. We publish the odds — premium collecting deserves transparency." data-id="element-6082" data-mp="1jp4rn0n0ettw" />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-14" data-id="element-6084" data-mp="10q0kxyixxc5e">
          {tiers.map((t, i) => <div key={t.name} className={`relative border ${t.borderClass} bg-[#0B0F19] p-5 flex flex-col min-h-[260px] transition-all hover:-translate-y-1 ${t.isFlagship ? 'shadow-[0_0_30px_rgba(212,175,55,0.15)]' : ''}`} data-id="element-6085" data-mp="15pll2c9ujqjp" data-mpi={i}>
              <div className={`absolute inset-x-0 top-0 h-24 bg-gradient-to-b ${t.bgGlow} pointer-events-none`} data-id="element-6086" data-mp="18t72wahoddv2" />
              {t.isFlagship && <span className="absolute -top-px left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" data-id="element-6087" data-mp="zv513sq4ah6q" />}
              <div className="relative flex items-center justify-between mb-4" data-id="element-6088" data-mp="1rmawjvuw7qpo">
                <span className="text-[#FDFBF7]/30 font-mono text-[10px]" data-id="element-6089" data-mp="1due9i6830705">
                  0{i + 1} / 05
                </span>
                <span className={`px-2 py-0.5 border text-[8px] tracking-[0.25em] uppercase ${t.pill}`} data-id="element-6090" data-mp="1iso2qba8wxj4">
                  {t.odds}
                </span>
              </div>
              <div className="relative flex-1 flex flex-col" data-id="element-6091" data-mp="2batqeqqufsap">
                <p className="text-[#FDFBF7]/40 text-[9px] tracking-[0.3em] uppercase mb-1.5" data-id="element-6092" data-mp="2kokc7inp91mn">
                  {t.sub}
                </p>
                <h4 className={`font-serif text-xl ${t.accentClass} mb-4 leading-tight`} data-id="element-6093" data-mp="2gm7ad5c2pnv5">
                  {t.name}
                </h4>
                <p className="text-[#FDFBF7]/55 text-xs font-light leading-relaxed" data-id="element-6094" data-mp="2pesg9kulw02k">
                  {t.copy}
                </p>
                <div className="mt-auto pt-5 flex items-end justify-between" data-id="element-6095" data-mp="21qhl8yblcum7">
                  <div className={`w-8 h-11 border ${t.borderClass} bg-[#131B2B] flex items-center justify-center`} data-id="element-6096" data-mp="1xowdtshtt8je">
                    <t.icon className={`w-4 h-4 ${t.accentClass}`} data-id="element-6097" data-mp="2bljarv9s3ypb" />
                  </div>
                  <span className="text-[#FDFBF7]/30 text-[8px] tracking-widest uppercase" data-id="element-6098" data-mp="26qvqy9ppzsh2">
                    Tier {i + 1}
                  </span>
                </div>
              </div>
            </div>)}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-[10px] tracking-[0.25em] uppercase" data-id="element-6099" data-mp="3q56z4145wfww">
          <span className="flex items-center gap-2 text-[#FDFBF7]/40" data-id="element-6100" data-mp="3u4o7p3evxt6a">
            <Sparkles className="w-3 h-3 text-[#D4AF37]/60" data-id="element-6101" data-mp="2zocdcmvsa8p" /> Odds published
            per drop
          </span>
          <span className="hidden sm:inline text-[#D4AF37]/20" data-id="element-6102" data-mp="7xpyy7jimrox">·</span>
          <span className="flex items-center gap-2 text-[#FDFBF7]/40" data-id="element-6103" data-mp="36h6ldnee3nap">
            <Shield className="w-3 h-3 text-[#D4AF37]/60" data-id="element-6104" data-mp="36etxyupv4kq9" /> No paid pulls —
            earned only
          </span>
          <span className="hidden sm:inline text-[#D4AF37]/20" data-id="element-6105" data-mp="3gf47o5cova0o">·</span>
          <span className="flex items-center gap-2 text-[#FDFBF7]/40" data-id="element-6106" data-mp="3gcrj39cfe1en">
            <BookOpen className="w-3 h-3 text-[#D4AF37]/60" data-id="element-6107" data-mp="3l75hlkmdwonj" /> Every card
            hand-illustrated
          </span>
        </div>
      </div>
    </section>;
};
// ---------- Trading Section ----------
const TradingSection = () => <section className="px-4 py-24 bg-[#0B0F19] relative border-t border-[#D4AF37]/10" data-id="element-6108" data-mp="2smx5gpka88tc">
    <div className="max-w-4xl mx-auto text-center" data-id="element-6109" data-mp="2l3o8xwwvk4lp">
      <SectionHeader eyebrow="The Court Exchange" title={<>
            TRADE WITH THE <span className="text-[#D4AF37]" data-id="element-6111" data-mp="2b6jktrjgxz29">SANGAT</span>
          </>} description="Duplicate cards become currency. Trade with members of your clan (Misl) or the wider community to complete sets and unlock prestige." data-id="element-6110" data-mp="2g6echxb2isod" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12" data-id="element-6112" data-mp="228zg28medkmw">
        {[{
        icon: Repeat,
        label: 'Direct Trade',
        body: 'Member-to-member swap with verified consent.'
      }, {
        icon: Coins,
        label: 'Open Market',
        body: 'List duplicates. Set your terms. Vetted by the Treasury.'
      }, {
        icon: Gift,
        label: 'Gifting',
        body: 'Send a card to a Sangat member as tribute.'
      }].map(it => <div key={it.label} className="p-6 border border-[#D4AF37]/20 bg-[#131B2B]" data-id="element-6113" data-mp="238zpo54htp8f">
            <it.icon className="w-7 h-7 text-[#D4AF37] mb-4 mx-auto" data-id="element-6114" data-mp="2869mw6y4j56t" />
            <p className="font-serif text-lg text-[#FDFBF7] mb-2" data-id="element-6115" data-mp="1uelcq97fqqsh">{it.label}</p>
            <p className="text-xs text-[#FDFBF7]/60 font-light leading-relaxed" data-id="element-6116" data-mp="1ygq1atj10x89">
              {it.body}
            </p>
          </div>)}
      </div>
    </div>
  </section>;
// ---------- Rank Section ----------
const RankSection = () => {
  const ranks = [{
    name: 'Sevadar',
    xp: '0 XP',
    color: 'text-[#FDFBF7]/60'
  }, {
    name: 'Sardar',
    xp: '500 XP',
    color: 'text-[#FDFBF7]/80'
  }, {
    name: 'Jathedar',
    xp: '1,500 XP',
    color: 'text-[#D4AF37]/90'
  }, {
    name: 'Misldar (Clan Chief)',
    xp: '3,500 XP',
    color: 'text-[#D4AF37]'
  }, {
    name: 'Maharaja',
    xp: '8,000 XP',
    color: 'text-[#8B0000]'
  }];
  return <section className="px-4 py-24 bg-[#131B2B] relative border-t border-[#D4AF37]/10" data-id="element-6117" data-mp="y9kwdywotco0">
      <div className="max-w-5xl mx-auto" data-id="element-6118" data-mp="u8r8h5j8vxv4">
        <SectionHeader eyebrow="The Order of Rank" title={<>
              RISE THROUGH THE <span className="text-[#D4AF37]" data-id="element-6120" data-mp="138eb1unpn9pr">COURT</span>
            </>} description="Every artifact earned, every set completed, every Treasury Night attended — all of it counts toward your standing." data-id="element-6119" data-mp="17yuqtlf077fa" />
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-12" data-id="element-6121" data-mp="1cx611x97njy1">
          {ranks.map((r, i) => <div key={r.name} className="p-5 border border-[#D4AF37]/20 bg-[#0B0F19] text-center" data-id="element-6122" data-mp="1qwfjz4osws5p" data-mpi={i}>
              <p className="text-[#FDFBF7]/30 font-mono text-[10px] mb-2" data-id="element-6123" data-mp="1lwt0ididqfog">
                0{i + 1}
              </p>
              <Crown className={`w-6 h-6 mx-auto mb-3 ${r.color}`} data-id="element-6124" data-mp="3nq8y5ucvwusx" />
              <p className={`font-serif text-lg mb-1 ${r.color}`} data-id="element-6125" data-mp="3ns3eevd7nyqo">{r.name}</p>
              <p className="text-[10px] tracking-widest uppercase text-[#FDFBF7]/40" data-id="element-6126" data-mp="1rof9cb7hhg8f">
                {r.xp}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
};
// ---------- Misl Section ----------
interface MislDetail {
  name: string;
  leader: string;
  members: string;
  xp: string;
  region: string;
  group: string;
  sub: string;
}
const MislDetailModal = ({
  misl,
  onClose,
  onJoin
}: {
  misl: MislDetail;
  onClose: () => void;
  onJoin: () => void;
}) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);
  // Build deterministic mock activity & members from the misl name so it feels
  // consistent across opens but varies between misls.
  const seed = misl.name.length;
  const weeklyXp = Math.round(parseFloat(misl.xp) * 0.07 * 10) / 10;
  const rank = seed * 3 % 17 + 1;
  const newThisWeek = seed * 7 % 18 + 4;
  const cardsClaimedWeek = seed * 11 % 90 + 60;
  const eventsAttended = seed * 5 % 12 + 3;
  const activityFeed = [{
    who: 'Harnoor S.',
    what: 'claimed Royal Seal of Ranjit Singh',
    when: '2h ago',
    rarity: 'Treasury Rare'
  }, {
    who: 'Amrit K.',
    what: 'joined the Clan',
    when: '5h ago',
    rarity: null
  }, {
    who: 'Davinder G.',
    what: "completed Founders' Seals set",
    when: '8h ago',
    rarity: "Founders' Edition"
  }, {
    who: 'Jaspreet S.',
    what: 'attended Treasury Night #04',
    when: '1d ago',
    rarity: 'Live Night Only'
  }, {
    who: 'Manvir K.',
    what: 'traded 3 duplicates',
    when: '1d ago',
    rarity: null
  }, {
    who: 'Gurpreet S.',
    what: 'referred 2 new Sangat members',
    when: '2d ago',
    rarity: null
  }];
  const topMembers = [{
    rank: 1,
    name: misl.leader,
    xp: '42,180',
    tag: 'Misldar'
  }, {
    rank: 2,
    name: '@simran_k',
    xp: '28,940',
    tag: null
  }, {
    rank: 3,
    name: '@arjan_s',
    xp: '24,610',
    tag: null
  }, {
    rank: 4,
    name: '@navjot_g',
    xp: '21,480',
    tag: null
  }, {
    rank: 5,
    name: '@harleen_k',
    xp: '19,320',
    tag: null
  }];
  const perks = [{
    icon: Gift,
    title: 'Bonus Daily Packet',
    body: 'Clan (Misl) members get an extra packet draw on Sundays.'
  }, {
    icon: Crown,
    title: 'Misldar Path (Clan Chief)',
    body: 'Earn the most XP and lead the Clan. Real status.'
  }, {
    icon: Calendar,
    title: 'Member-Only Events',
    body: 'Private Court Sessions for verified Clan members.'
  }, {
    icon: Sparkles,
    title: 'Clan-Exclusive Cards',
    body: "Drops tied to your Clan's history and region."
  }];
  return <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6" data-id="element-6127" data-mp="3epitjlc7wxqe">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="absolute inset-0 bg-[#0B0F19]/92 backdrop-blur-sm" data-id="element-6128" data-mp="3ticlw7cl98ch">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.12),_transparent_55%)]" data-id="element-6129" data-mp="2vy87pilcnb5d" />
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      scale: 0.96,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.96,
      y: 20
    }} transition={{
      duration: 0.4,
      type: 'spring',
      bounce: 0.2
    }} className="relative w-full max-w-5xl bg-[#0B0F19] border-2 border-[#D4AF37]/40 shadow-2xl overflow-y-auto max-h-[90vh] scrollbar-hide" data-id="element-6130" data-mp="35uusk7doe27e">
        <CornerOrnaments data-id="element-6131" data-mp="12xn2qbi28f8h" />
        <button onClick={onClose} className="absolute top-4 right-4 p-2 text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors z-10" aria-label="Close" data-id="element-6132" data-mp="12x50my8pv6sn">
          <X className="w-6 h-6" data-id="element-6133" data-mp="1vkraupcg99oh" />
        </button>

        {/* === Header === */}
        <div className="relative px-6 sm:px-10 pt-10 pb-8 border-b border-[#D4AF37]/15 bg-gradient-to-br from-[#D4AF37]/8 via-[#131B2B]/40 to-transparent" data-id="element-6134" data-mp="1qnr226o7tsto">
          <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-2" data-id="element-6135" data-mp="1goicdq0dv0d4">
            {misl.group} · {misl.sub}
          </p>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4" data-id="element-6136" data-mp="a8zf499u9drr">
            <div data-id="element-6137" data-mp="58bmxk9viqvy">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-2 leading-tight" data-id="element-6138" data-mp="57s29vsu9oe8">
                {misl.name} Misl
              </h2>
              <p className="text-[#FDFBF7]/60 text-sm" data-id="element-6139" data-mp="2lv03d35rrnyw">{misl.region}</p>
            </div>
            <div className="flex items-center gap-3 px-4 py-2.5 border border-[#D4AF37]/30 bg-[#0B0F19]/60" data-id="element-6140" data-mp="2quw9p91xvrhm">
              <div className="w-9 h-9 rounded-full border border-[#D4AF37]/50 bg-[#131B2B] flex items-center justify-center" data-id="element-6141" data-mp="2vlut4hi855p3">
                <Crown className="w-4 h-4 text-[#D4AF37]" data-id="element-6142" data-mp="30jnvezgepfk3" />
              </div>
              <div className="text-left" data-id="element-6143" data-mp="34l9hnbetz66y">
                <p className="text-[#D4AF37]/60 text-[9px] tracking-[0.3em] uppercase" data-id="element-6144" data-mp="34k7uw9ogcv9e">
                  Current Misldar{' '}
                  <span className="text-[#FDFBF7]/40" data-id="element-6145" data-mp="3ek6zxwlny3hi">(Clan Chief)</span>
                </p>
                <p className="font-mono text-[#D4AF37] text-sm" data-id="element-6146" data-mp="3ehujjnvk016k">
                  {misl.leader}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* === KPI strip === */}
        <div className="px-6 sm:px-10 py-6 grid grid-cols-2 sm:grid-cols-4 gap-3 border-b border-[#D4AF37]/10" data-id="element-6147" data-mp="1c586ri123xe1">
          {[{
          label: 'Members',
          value: misl.members,
          icon: Users,
          accent: false
        }, {
          label: 'Total XP',
          value: misl.xp,
          icon: Sparkles,
          accent: true
        }, {
          label: 'Confederacy Rank',
          value: `#${rank}`,
          icon: Crown,
          accent: false
        }, {
          label: 'New This Week',
          value: `+${newThisWeek}`,
          icon: UserPlus,
          accent: false
        }].map(stat => <div key={stat.label} className="border border-[#D4AF37]/15 bg-[#131B2B]/40 p-4 text-center" data-id="element-6148" data-mp="1zxf1kcr4vwbc">
              <stat.icon className={`w-4 h-4 mx-auto mb-2 ${stat.accent ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/40'}`} data-id="element-6149" data-mp="29ubd6rcbwyk4" />
              <p className={`font-serif text-2xl mb-1 ${stat.accent ? 'text-[#D4AF37]' : 'text-[#FDFBF7]'}`} data-id="element-6150" data-mp="2td7hujr5kj7e">
                {stat.value}
              </p>
              <p className="text-[9px] tracking-[0.25em] uppercase text-[#FDFBF7]/50" data-id="element-6151" data-mp="2jiyojtuasm86">
                {stat.label}
              </p>
            </div>)}
        </div>

        {/* === Weekly activity strip === */}
        <div className="px-6 sm:px-10 py-5 border-b border-[#D4AF37]/10 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8 text-[10px] tracking-[0.25em] uppercase" data-id="element-6152" data-mp="2ehj3i1s4efps">
          <span className="text-emerald-400 flex items-center gap-2" data-id="element-6153" data-mp="20o9ysef4aidl">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" data-id="element-6154" data-mp="25q94huybxrdp" />
            Active Now ·{' '}
            {Math.round((parseInt(misl.members.replace(/,/g, '')) || 0) * 0.08)}{' '}
            members
          </span>
          <span className="text-[#FDFBF7]/60 flex items-center gap-2" data-id="element-6155" data-mp="1vt4c3ddw4zog">
            <Sparkles className="w-3 h-3 text-[#D4AF37]" data-id="element-6156" data-mp="2z1nyxmjj4nlm" />+{weeklyXp}k XP this
            week
          </span>
          <span className="text-[#FDFBF7]/60 flex items-center gap-2" data-id="element-6157" data-mp="2u579mp0lg36y">
            <Gift className="w-3 h-3 text-[#D4AF37]" data-id="element-6158" data-mp="2pb3141bojn3r" /> {cardsClaimedWeek} cards
            claimed
          </span>
          <span className="text-[#FDFBF7]/60 flex items-center gap-2" data-id="element-6159" data-mp="2kd16qk8t01cz">
            <Calendar className="w-3 h-3 text-[#D4AF37]" data-id="element-6160" data-mp="2ff9etq85sf8s" /> {eventsAttended}{' '}
            events attended
          </span>
        </div>

        {/* === Two-column: Activity feed + Top members === */}
        <div className="px-6 sm:px-10 py-8 grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-6161" data-mp="26el00taxyauu">
          {/* Activity feed */}
          <div data-id="element-6162" data-mp="21g01cybc7z8i">
            <div className="flex items-center justify-between mb-5" data-id="element-6163" data-mp="21gjjog4wgpn2">
              <h3 className="font-serif text-xl text-[#FDFBF7]" data-id="element-6164" data-mp="4ku5wc0z5kz5">
                Recent Activity
              </h3>
              <span className="text-[9px] tracking-widest uppercase text-[#D4AF37]/60" data-id="element-6165" data-mp="3vt9d6wrcvwtx">
                Live Feed
              </span>
            </div>
            <div className="space-y-2.5" data-id="element-6166" data-mp="c56324bib5zw">
              {activityFeed.map((a, i) => <div key={i} className="flex items-start gap-3 p-3 border border-[#D4AF37]/10 bg-[#131B2B]/40 hover:border-[#D4AF37]/25 transition-colors" data-id="element-6167" data-mp="gwo5yxx3bgni" data-mpi={i}>
                  <div className="w-7 h-7 shrink-0 rounded-full bg-[#0B0F19] border border-[#D4AF37]/25 flex items-center justify-center" data-id="element-6168" data-mp="3kdmrzdk3df4m">
                    <span className="text-[#D4AF37] text-[10px] font-serif" data-id="element-6169" data-mp="3pcpy71bt5re6">
                      {a.who.charAt(0)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-6170" data-mp="359lsnrg59ev">
                    <p className="text-[#FDFBF7]/85 text-xs leading-relaxed" data-id="element-6171" data-mp="32wv9m7mhqq7">
                      <span className="text-[#D4AF37]" data-id="element-6172" data-mp="19kjx7n3w4gcw">{a.who}</span> {a.what}
                    </p>
                    <div className="flex items-center gap-2 mt-1" data-id="element-6173" data-mp="1eilkylc9h3up">
                      <span className="text-[#FDFBF7]/35 text-[9px] tracking-widest uppercase" data-id="element-6174" data-mp="3ami4h1trezq9">
                        {a.when}
                      </span>
                      {a.rarity && <span className="text-[#D4AF37]/70 text-[9px] tracking-widest uppercase" data-id="element-6175" data-mp="3jo9bca01dclp">
                          · {a.rarity}
                        </span>}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Top members leaderboard */}
          <div data-id="element-6176" data-mp="2mvm7lqag5ec5">
            <div className="flex items-center justify-between mb-5" data-id="element-6177" data-mp="30p3pl6nhij02">
              <h3 className="font-serif text-xl text-[#FDFBF7]" data-id="element-6178" data-mp="30qesl493vmq4">Top Members</h3>
              <span className="text-[9px] tracking-widest uppercase text-[#D4AF37]/60" data-id="element-6179" data-mp="2vxlp0jpdikfm">
                XP Ladder
              </span>
            </div>
            <div className="border border-[#D4AF37]/15 bg-[#131B2B]/40" data-id="element-6180" data-mp="247h6kql6c7h1">
              {topMembers.map((m, i) => <div key={m.rank} className={`flex items-center gap-3 px-4 py-3 ${i !== topMembers.length - 1 ? 'border-b border-[#D4AF37]/10' : ''} ${m.rank === 1 ? 'bg-[#D4AF37]/5' : ''}`} data-id="element-6181" data-mp="1u2r3uihy4zbr" data-mpi={i}>
                  <span className={`w-7 text-center font-mono text-sm ${m.rank === 1 ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/40'}`} data-id="element-6182" data-mp="1k6xqiwwel1sx">
                    {String(m.rank).padStart(2, '0')}
                  </span>
                  <div className="flex-1 min-w-0" data-id="element-6183" data-mp="2htekpt9uazag">
                    <p className={`font-mono text-sm truncate ${m.rank === 1 ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/85'}`} data-id="element-6184" data-mp="2hs3g5r8klnia">
                      {m.name}
                    </p>
                    {m.tag && <p className="text-[#D4AF37]/70 text-[9px] tracking-widest uppercase mt-0.5" data-id="element-6185" data-mp="rg5xu9ejqvac">
                        {m.tag}
                      </p>}
                  </div>
                  <span className={`font-mono text-xs ${m.rank === 1 ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/60'}`} data-id="element-6186" data-mp="dp9awy5f0a2g">
                    {m.xp} XP
                  </span>
                </div>)}
            </div>
            <p className="text-center text-[#FDFBF7]/40 text-[10px] tracking-widest uppercase mt-3" data-id="element-6187" data-mp="3smqubkwxn1h">
              + {parseInt(misl.members.replace(/,/g, '')) - 5} more members
            </p>
          </div>
        </div>

        {/* === Why join === */}
        <div className="px-6 sm:px-10 py-8 border-t border-[#D4AF37]/10" data-id="element-6188" data-mp="rhp6jdvzl5tc">
          <div className="text-center mb-6" data-id="element-6189" data-mp="mow44wnzq9kf">
            <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-2" data-id="element-6190" data-mp="mnuosfzxz0ww">
              What you unlock
            </p>
            <h3 className="font-serif text-2xl text-[#FDFBF7]" data-id="element-6191" data-mp="1f5qdwjhfig76">
              Why Join the {misl.name} Clan
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-6192" data-mp="1aegptnbj3kxe">
            {perks.map(p => <div key={p.title} className="p-4 border border-[#D4AF37]/15 bg-[#131B2B]/40" data-id="element-6193" data-mp="2t3um1sg4st37">
                <p.icon className="w-5 h-5 text-[#D4AF37] mb-3" data-id="element-6194" data-mp="322y6p0uy5gjt" />
                <p className="font-serif text-sm text-[#FDFBF7] mb-1" data-id="element-6195" data-mp="324stkkps3iqj">
                  {p.title}
                </p>
                <p className="text-[11px] text-[#FDFBF7]/55 font-light leading-relaxed" data-id="element-6196" data-mp="3c0vvpqkmrhdh">
                  {p.body}
                </p>
              </div>)}
          </div>
        </div>

        {/* === CTA strip === */}
        <div className="sticky bottom-0 px-6 sm:px-10 py-5 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19] to-[#0B0F19]/90 border-t border-[#D4AF37]/20 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3" data-id="element-6197" data-mp="3lqfeyxc99d8v">
          <div data-id="element-6198" data-mp="3uswyh40izrs6">
            <p className="text-[#FDFBF7] text-sm font-medium" data-id="element-6199" data-mp="3up98ufy7bpiq">
              Ready to take your place in the {misl.name} court?
            </p>
            <p className="text-[#FDFBF7]/50 text-xs mt-0.5" data-id="element-6200" data-mp="2qg8aihymsw98">
              Register to lock in your clan and start earning XP today.
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-6201" data-mp="2uiwm2xeqch9w">
            <button onClick={onClose} className="px-5 py-3 border border-[#FDFBF7]/20 text-[#FDFBF7]/70 text-[10px] tracking-[0.25em] uppercase hover:bg-[#FDFBF7]/5 transition-colors" data-id="element-6202" data-mp="2zhg8t015dx3b">
              Browse Other Clans
            </button>
            <button onClick={onJoin} className="px-6 py-3 bg-[#D4AF37] text-[#0B0F19] text-[10px] tracking-[0.3em] uppercase font-bold hover:bg-[#E5C158] transition-colors flex items-center gap-2" data-id="element-6203" data-mp="2bpsox22azmaj">
              Join This Clan
              <ArrowRight className="w-3.5 h-3.5" data-id="element-6204" data-mp="1o0zp2uik8uan" />
            </button>
          </div>
        </div>
      </motion.div>
    </div>;
};
const MislSection = ({
  onRegister
}: {
  onRegister?: () => void;
}) => {
  const [selectedMisl, setSelectedMisl] = useState<MislDetail | null>(null);
  const mislGroups = [{
    group: 'Homeland / Region',
    sub: 'Spiritual and cultural homelands of Punjab',
    misls: [{
      name: 'Lahore',
      leader: '@lahore_s',
      members: '2,100',
      xp: '720k',
      region: 'Punjab · Pakistan'
    }, {
      name: 'Amritsar',
      leader: '@golden_s',
      members: '1,360',
      xp: '510k',
      region: 'Punjab · India'
    }, {
      name: 'Majha',
      leader: '@majha_s',
      members: '960',
      xp: '370k',
      region: 'Central Punjab'
    }, {
      name: 'Doaba',
      leader: '@doaba_k',
      members: '780',
      xp: '300k',
      region: 'Beas–Sutlej'
    }, {
      name: 'Malwa',
      leader: '@malwa_s',
      members: '820',
      xp: '320k',
      region: 'South Punjab'
    }]
  }, {
    group: 'Diaspora / City',
    sub: 'The modern Khalsa beyond Punjab',
    misls: [{
      name: 'Brampton',
      leader: '@brampton_s',
      members: '1,420',
      xp: '460k',
      region: 'Ontario · Canada'
    }, {
      name: 'Surrey',
      leader: '@surrey_s',
      members: '1,180',
      xp: '410k',
      region: 'British Columbia'
    }, {
      name: 'Southall',
      leader: '@southall_s',
      members: '980',
      xp: '360k',
      region: 'London · UK'
    }, {
      name: 'Toronto',
      leader: '@toronto_s',
      members: '1,260',
      xp: '440k',
      region: 'Ontario · Canada'
    }, {
      name: 'Vancouver',
      leader: '@van_s',
      members: '1,040',
      xp: '390k',
      region: 'British Columbia'
    }]
  }, {
    group: 'Family / Clan',
    sub: 'Houses bound by name and lineage',
    misls: [{
      name: 'Sandhu',
      leader: '@sandhu_s',
      members: '1,180',
      xp: '420k',
      region: 'Jat clan'
    }, {
      name: 'Gill',
      leader: '@gill_s',
      members: '1,340',
      xp: '470k',
      region: 'Jat clan'
    }, {
      name: 'Sidhu',
      leader: '@sidhu_s',
      members: '1,220',
      xp: '440k',
      region: 'Jat clan'
    }, {
      name: 'Dhillon',
      leader: '@dhillon_s',
      members: '960',
      xp: '350k',
      region: 'Jat clan'
    }, {
      name: 'Brar',
      leader: '@brar_s',
      members: '880',
      xp: '320k',
      region: 'Jat clan'
    }]
  }, {
    group: 'Historic Prestige',
    sub: 'The sovereign Misls (Clans) of the Khalsa Confederacy',
    misls: [{
      name: 'Sukerchakia',
      leader: '@ranjit_s',
      members: '1,520',
      xp: '480k',
      region: 'Gujranwala'
    }, {
      name: 'Ahluwalia',
      leader: '@jassa_singh',
      members: '1,240',
      xp: '450k',
      region: 'Kapurthala'
    }, {
      name: 'Ramgarhia',
      leader: '@jassa_r',
      members: '1,180',
      xp: '430k',
      region: 'Sri Hargobindpur'
    }, {
      name: 'Kanheya',
      leader: '@jai_singh',
      members: '850',
      xp: '310k',
      region: 'Sohian'
    }, {
      name: 'Nakai',
      leader: '@hira_s',
      members: '620',
      xp: '240k',
      region: 'Chunian'
    }]
  }];
  return <section data-misl-section className="px-4 py-24 bg-[#0B0F19] relative border-t border-[#D4AF37]/10 overflow-hidden scroll-mt-24" data-id="element-6205" data-mp="1to26mhma2gma">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.05),_transparent_70%)]" data-id="element-6206" data-mp="1yjr6km27lzj4" />
      <div className="max-w-6xl mx-auto relative z-10" data-id="element-6207" data-mp="3e5bf46uo5m6s">
        <SectionHeader eyebrow="Community & Sovereignty" title={<>
              JOIN A <span className="text-[#D4AF37]" data-id="element-6209" data-mp="3a5az4hrx92xm">MISL</span>{' '}
              <span className="text-[#FDFBF7]/40" data-id="element-6210" data-mp="355okc3wg3emp">— A CLAN</span>
            </>} description="The Treasury is communal. Join a Misl — your clan in the Khalsa — invite your Sangat, and compete on the global leaderboards. Together, we recover our history." data-id="element-6208" data-mp="3e3qhu9gyzp58" />

        <div className="max-w-3xl mx-auto mb-10 text-center" data-id="element-6211" data-mp="3t1iyj4y96jl0">
          <p className="text-[#FDFBF7]/60 text-sm leading-relaxed italic" data-id="element-6212" data-mp="3o3908lh8f2v6">
            A <span className="text-[#D4AF37]" data-id="element-6213" data-mp="3o14ya92w8dxj">Misl</span> — a clan and
            sovereign state of the Sikh Confederacy — was led by a{' '}
            <span className="text-[#D4AF37]" data-id="element-6214" data-mp="klz78bh57b8l">Misldar</span> (clan chief). Today,
            we honor that legacy — joining historical Misls, royal courts of
            Lahore, our homeland Sangats, and our diaspora hubs around the
            world.
          </p>
        </div>

        {/* How Leadership Works — gamification callout */}
        <div className="max-w-3xl mx-auto mb-12 relative border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/8 via-[#131B2B] to-[#0B0F19] p-6 sm:p-8" data-id="element-6215" data-mp="upxr89bvgg50">
          <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/70 to-transparent" data-id="element-6216" data-mp="krqek2om8k8t" />
          <div className="flex flex-col sm:flex-row items-center gap-5 text-center sm:text-left" data-id="element-6217" data-mp="ps5oek9ov9m1">
            <div className="w-14 h-14 shrink-0 border border-[#D4AF37] bg-[#0B0F19] flex items-center justify-center shadow-[0_0_20px_rgba(212,175,55,0.3)]" data-id="element-6218" data-mp="bswicdf75vvs">
              <Crown className="w-7 h-7 text-[#D4AF37]" data-id="element-6219" data-mp="265vwe23xii0y" />
            </div>
            <div className="flex-1" data-id="element-6220" data-mp="263rt9mdutn1q">
              <p className="text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase font-bold mb-1.5" data-id="element-6221" data-mp="2a7hec1vblmi0">
                How Leadership Works
              </p>
              <p className="text-[#FDFBF7]/80 text-sm leading-relaxed" data-id="element-6222" data-mp="3un5n7n2jiixs">
                The member with the most XP in each clan becomes its{' '}
                <span className="text-[#D4AF37] font-bold" data-id="element-6223" data-mp="89xe5umnsjkj">Misldar</span> (Clan
                Chief). Climb the leaderboard, claim rare cards, refer your
                Sangat — whoever earns the most XP rules the clan.
              </p>
            </div>
          </div>
        </div>

        {/* Grouped Misl categories */}
        <div className="space-y-10 mb-12" data-id="element-6224" data-mp="d88pk9slr24v">
          {mislGroups.map(g => <div key={g.group} data-id="element-6225" data-mp="r7gtffzpmyfe">
              {/* Group header */}
              <div className="flex items-end justify-between gap-4 mb-5 pb-3 border-b border-[#D4AF37]/15" data-id="element-6226" data-mp="m9oqlqxj1rwf">
                <div data-id="element-6227" data-mp="2szd3sma5628l">
                  <h3 className="font-serif text-xl sm:text-2xl text-[#FDFBF7]" data-id="element-6228" data-mp="2t0y0ahg0nv93">
                    {g.group}
                  </h3>
                  <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.3em] uppercase mt-1.5" data-id="element-6229" data-mp="2o2wg1t6utpw1">
                    {g.sub}
                  </p>
                </div>
                <span className="text-[#FDFBF7]/30 text-[10px] tracking-widest uppercase shrink-0" data-id="element-6230" data-mp="275eg5krue2vr">
                  {g.misls.length} {g.misls.length === 1 ? 'Misl' : 'Misls'}
                </span>
              </div>

              {/* 5-up grid so each category fits on one row, no orphans */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-id="element-6231" data-mp="1k7kz6yy17jru">
                {g.misls.map(m => <div key={m.name} className="group relative p-4 border border-[#D4AF37]/20 bg-[#131B2B]/60 hover:border-[#D4AF37]/55 hover:bg-[#131B2B] transition-all flex flex-col" data-id="element-6232" data-mp="1o84stsjec598">
                    {/* Name + region */}
                    <div className="mb-3" data-id="element-6233" data-mp="34ll7pdxaoqe8">
                      <h4 className="font-serif text-[#FDFBF7] text-base leading-tight truncate" data-id="element-6234" data-mp="39kxw433te5ix">
                        {m.name}
                      </h4>
                      <p className="text-[#FDFBF7]/40 text-[9px] tracking-[0.2em] uppercase mt-1 truncate" data-id="element-6235" data-mp="xx2ssbjamxfm">
                        {m.region}
                      </p>
                    </div>

                    {/* Misldar row */}
                    <div className="flex items-center gap-1.5 pb-3 mb-3 border-b border-[#D4AF37]/10" data-id="element-6236" data-mp="j8y6yyoy25sm">
                      <Crown className="w-3 h-3 text-[#D4AF37] shrink-0" data-id="element-6237" data-mp="f738e1yee6ij" />
                      <p className="text-[#D4AF37]/80 text-[9px] tracking-widest uppercase font-mono truncate" data-id="element-6238" data-mp="f59zsh485c7w">
                        {m.leader}
                      </p>
                    </div>

                    {/* Stats — 2 stacked rows for clarity */}
                    <div className="flex items-center justify-between text-[10px] mb-4" data-id="element-6239" data-mp="bdu6pzgexod">
                      <span className="flex items-center gap-1.5 text-[#FDFBF7]/60" data-id="element-6240" data-mp="1zm56dqffc266">
                        <Users className="w-3 h-3" data-id="element-6241" data-mp="1unlg5dtss480" />
                        <span className="font-mono" data-id="element-6242" data-mp="1um0njtr1mvcs">{m.members}</span>
                      </span>
                      <span className="flex items-center gap-1.5 text-[#D4AF37]" data-id="element-6243" data-mp="2mcg00w662my4">
                        <Sparkles className="w-3 h-3" data-id="element-6244" data-mp="2hewm5xddsc6p" />
                        <span className="font-mono" data-id="element-6245" data-mp="2w4vj5wt79hkl">{m.xp}</span>
                      </span>
                    </div>

                    {/* Join CTA — opens detailed Misl view with stats & activity */}
                    <button type="button" onClick={() => setSelectedMisl({
                ...m,
                group: g.group,
                sub: g.sub
              })} className="mt-auto w-full py-2 border border-[#D4AF37]/30 text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase hover:bg-[#D4AF37] hover:text-[#0B0F19] transition-all" data-id="element-6246" data-mp="23f551nrkifoh">
                      Join Clan
                    </button>
                  </div>)}
              </div>
            </div>)}
        </div>

        <div className="p-8 sm:p-10 border border-[#D4AF37]/30 bg-[#131B2B] relative flex flex-col md:flex-row items-center justify-between gap-8" data-id="element-6247" data-mp="6uz5expfmmj">
          <CornerOrnaments data-id="element-6248" data-mp="3iec19e1wvkx5" />
          <div className="text-center md:text-left" data-id="element-6249" data-mp="3if3x8pzf7kny">
            <h3 className="font-serif text-2xl text-[#FDFBF7] mb-2" data-id="element-6250" data-mp="3mfe1ny7ke30p">
              Invite your Sangat
            </h3>
            <p className="text-[#FDFBF7]/60 text-sm" data-id="element-6251" data-mp="dz2s0vlvatfy">
              Bring friends into your Clan (Misl) and earn bonus packets for
              every verified member.
            </p>
          </div>
          <button className="flex items-center gap-3 px-8 py-4 bg-[#25D366] text-white hover:bg-[#128C7E] transition-all font-medium text-sm tracking-wider" data-id="element-6252" data-mp="55ppd1y04dgo">
            <MessageCircle className="w-5 h-5" data-id="element-6253" data-mp="wwvj90n4pexi" />
            SHARE ON WHATSAPP
          </button>
        </div>
      </div>

      {/* Detail modal mounted OUTSIDE inner z-10 stacking context so it always overlays correctly */}
      {selectedMisl && <MislDetailModal misl={selectedMisl} onClose={() => setSelectedMisl(null)} onJoin={() => {
      setSelectedMisl(null);
      if (onRegister) onRegister();
    }} data-id="element-6254" data-mp="1bjazryur6l7p" />}
    </section>;
};
// ---------- Create Your Own Misl Section ----------
const CreateMislSection = ({
  onRegister,
  embedded = false,
  onClose
}: {
  onRegister?: () => void;
  embedded?: boolean;
  onClose?: () => void;
}) => {
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    region: '',
    type: 'family',
    crest: 'lion'
  });
  const [formingMembers, setFormingMembers] = useState(2);
  const inviteLink = `lahoretreasury.com/misl/${(form.name || 'your-misl').toLowerCase().replace(/\s+/g, '-')}`;
  const handleCopyLink = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleWhatsAppShare = () => {
    const msg = encodeURIComponent(`I'm forming a Misl inside Lahore Treasury — ${form.name || 'a new house'}. Join us and help activate our house in the Lahore Court.\n\n${inviteLink}`);
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };
  const entryCards = [{
    icon: Users,
    tag: 'Join an existing house',
    title: 'Join a Misl',
    body: 'Join an existing Misl and contribute to its collection, rank, and standing inside the Lahore Court.',
    cta: 'Browse Misls',
    action: () => {
      const el = document.querySelector('[data-misl-section]');
      if (el) el.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, {
    icon: Plus,
    tag: 'Build your own',
    title: 'Create a Misl',
    body: 'Start your own Misl, invite your people through WhatsApp, and activate your house once enough members join.',
    cta: 'Create Your Misl',
    action: () => {
      const el = document.getElementById('create-misl-form');
      if (el) el.scrollIntoView({
        behavior: 'smooth'
      });
    },
    highlight: true
  }];
  const steps = [{
    n: '01',
    title: 'Create Your Misl',
    body: 'Choose a name, region, and identity. Your house takes shape inside the Lahore Court.'
  }, {
    n: '02',
    title: 'Share Through WhatsApp',
    body: 'Bring your people in — family, friends, school, community. One link, one Misl.'
  }, {
    n: '03',
    title: 'Reach the Threshold',
    body: 'A Misl awakens when its members arrive. Five voices are enough to activate a house.'
  }, {
    n: '04',
    title: 'Unlock Active Status',
    body: 'Enter the leaderboard, earn collective XP, and step into group rewards together.'
  }];
  const statusLadder = [{
    tag: 'Stage 01',
    name: 'Forming Misl',
    body: 'Created, but still gathering its first members. Your house is being assembled.',
    dot: 'bg-[#FDFBF7]/40',
    border: 'border-[#FDFBF7]/20',
    titleColor: 'text-[#FDFBF7]/80'
  }, {
    tag: 'Stage 02',
    name: 'Active Misl',
    body: 'Minimum threshold reached. Your Misl is officially recognised inside the Court.',
    dot: 'bg-[#D4AF37]',
    border: 'border-[#D4AF37]/50',
    titleColor: 'text-[#D4AF37]'
  }, {
    tag: 'Stage 03',
    name: 'Ranked Misl',
    body: 'A growing house that earns XP, climbs the leaderboard, and unlocks stronger benefits.',
    dot: 'bg-[#8B0000]',
    border: 'border-[#8B0000]/50',
    titleColor: 'text-[#8B0000]'
  }];
  const thresholds = [{
    count: '5',
    label: 'Active Misl',
    body: 'Your house is officially recognised. Identity, badge, and listing inside the Court.',
    progress: 100
  }, {
    count: '10+',
    label: 'Stronger Standing',
    body: 'Better leaderboard placement, group XP multipliers, and broader recognition.',
    progress: 65
  }, {
    count: '25+',
    label: 'Prestige Misl',
    body: 'Featured placement, spotlight in Court communications, and prestige drops.',
    progress: 30
  }];
  const mislTypes = [{
    id: 'family',
    label: 'Family'
  }, {
    id: 'friends',
    label: 'Friends'
  }, {
    id: 'school',
    label: 'School'
  }, {
    id: 'community',
    label: 'Community'
  }, {
    id: 'collectors',
    label: 'Collectors'
  }];
  const crestOptions = [{
    id: 'lion',
    label: 'Lion',
    icon: Shield
  }, {
    id: 'sword',
    label: 'Sword',
    icon: Sword
  }, {
    id: 'crown',
    label: 'Crown',
    icon: Crown
  }, {
    id: 'gem',
    label: 'Gem',
    icon: Gem
  }, {
    id: 'star',
    label: 'Star',
    icon: Star
  }];
  const benefits = [{
    icon: Trophy,
    title: 'Misl Leaderboard',
    body: 'Active Misls appear on the global leaderboard and compete for standing.'
  }, {
    icon: Shield,
    title: 'House Identity',
    body: 'Unlock an exclusive Misl badge, crest, and identity marker across the Court.'
  }, {
    icon: Sparkles,
    title: 'Court Visibility',
    body: 'Your Misl becomes visible to the wider Lahore Treasury community.'
  }, {
    icon: Gift,
    title: 'Group Rewards',
    body: 'Qualify for group-based rewards earned by your Misl as a whole.'
  }, {
    icon: Crown,
    title: 'Rising Status',
    body: 'As your Misl grows, your house unlocks stronger status and recognition.'
  }, {
    icon: Gem,
    title: 'Misl-Only Drops',
    body: 'Access future Misl-only drops, prestige spotlights, and Court honours.'
  }];
  const Wrapper: React.FC<{
    children: React.ReactNode;
  }> = ({
    children
  }) => embedded ? <div className="relative" data-id="element-6255" data-mp="301rewakkaw37">{children}</div> : <section className="px-4 py-24 bg-[#0B0F19] relative border-t border-[#D4AF37]/10 overflow-hidden" data-id="element-6256" data-mp="2axkrtb1f0bvi">
        {/* Ambient gold glow */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.06),_transparent_60%)] pointer-events-none" data-id="element-6257" data-mp="25yhl7m5sdxlf" />
        <div className="absolute -left-32 top-1/3 w-96 h-96 bg-[#D4AF37]/5 blur-[120px] rounded-full pointer-events-none" data-id="element-6258" data-mp="2fx749i9f5o39" />
        <div className="absolute -right-32 bottom-1/3 w-96 h-96 bg-[#8B0000]/8 blur-[120px] rounded-full pointer-events-none" data-id="element-6259" data-mp="1s6tdrw6hfxas" />
        {children}
      </section>;
  return <Wrapper data-id="element-6260" data-mp="20zo8vx28kcmu">
      <div className={`${embedded ? '' : 'max-w-6xl mx-auto relative z-10'}`} data-id="element-6261" data-mp="210g1r04g7w9f">
        {embedded ? <div className="flex flex-col gap-6 mb-10" data-id="element-6262" data-mp="1w8y2cgi2q3qk">
            <div className="flex items-center justify-between border-b border-[#D4AF37]/20 pb-4" data-id="element-6263" data-mp="1w73kjb23mrqp">
              <div data-id="element-6264" data-mp="18hizmmmdxkes">
                <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-1.5" data-id="element-6265" data-mp="14ia3jb3vhag2">
                  Build Your House
                </p>
                <h3 className="font-serif text-2xl sm:text-3xl text-[#FDFBF7] leading-tight" data-id="element-6266" data-mp="ucinerw5p3pw">
                  Create Your Own <span className="text-[#D4AF37]" data-id="element-6267" data-mp="z64pjq4p1p7g">Misl</span>
                </h3>
              </div>
              {onClose && <button onClick={onClose} className="flex items-center gap-2 text-[#D4AF37] hover:text-[#E5C158] text-[10px] uppercase tracking-widest border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 px-3 py-2 transition-colors" data-id="element-6268" data-mp="pf0day6ekigb">
                  <ChevronLeft className="w-3 h-3" data-id="element-6269" data-mp="1i1bmuybzltp9" />
                  Back to My Clan
                </button>}
            </div>
            <p className="text-[#FDFBF7]/60 text-sm leading-relaxed max-w-3xl" data-id="element-6270" data-mp="184p1m8fr10mp">
              Build your own house inside Lahore Treasury. Invite your people
              through WhatsApp, grow your rank, and unlock rewards together.
              Your Misl begins as a forming house and activates once enough
              members join.
            </p>
          </div> : <>
            <SectionHeader eyebrow="Build Your House" title={<>
                  CREATE YOUR OWN <span className="text-[#D4AF37]" data-id="element-6272" data-mp="21oanw07clcr5">MISL</span>
                </>} description="Build your own house inside Lahore Treasury. Invite your people, grow your rank, and unlock rewards together." data-id="element-6271" data-mp="26oov5g14kio4" />

            <div className="max-w-3xl mx-auto mb-16 text-center" data-id="element-6273" data-mp="1n17m3hbzshy0">
              <p className="text-[#FDFBF7]/60 text-sm leading-relaxed" data-id="element-6274" data-mp="1myuvygw2z19v">
                Lahore Treasury is not only about individual collecting. You can
                also create your own{' '}
                <span className="text-[#D4AF37]" data-id="element-6275" data-mp="1dy6hjl2ryywg">Misl</span>, bring your people
                in through WhatsApp, and grow together inside the Lahore Court.
              </p>
            </div>
          </>}

        {/* ---------- 1. Two Entry Cards ---------- */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-20" data-id="element-6276" data-mp="txd9jy9efc00">
          {entryCards.map(c => <motion.div key={c.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className={`relative p-8 sm:p-10 border ${c.highlight ? 'border-[#D4AF37]/50 bg-gradient-to-br from-[#131B2B] to-[#0B0F19]' : 'border-[#D4AF37]/20 bg-[#131B2B]'} group hover:border-[#D4AF37]/70 transition-all`} data-id="element-6277" data-mp="yvojsa3lvoir">
              <CornerOrnaments data-id="element-6278" data-mp="pye9os2iy5ff" />
              {c.highlight && <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" data-id="element-6279" data-mp="3ols4pzmcoa6u" />}
              <div className="flex items-start justify-between mb-6" data-id="element-6280" data-mp="3lkp38f0mpqr5">
                <div className="w-12 h-12 border border-[#D4AF37]/40 bg-[#0B0F19] flex items-center justify-center" data-id="element-6281" data-mp="3livumu6ggwgi">
                  <c.icon className="w-5 h-5 text-[#D4AF37]" data-id="element-6282" data-mp="3vas4t3ltgd13" />
                </div>
                <span className="text-[#D4AF37]/60 text-[9px] tracking-[0.3em] uppercase" data-id="element-6283" data-mp="3vcuqjbfxrk00">
                  {c.tag}
                </span>
              </div>
              <h3 className="font-serif text-3xl text-[#FDFBF7] mb-3 leading-tight" data-id="element-6284" data-mp="32n5piy387jye">
                {c.title}
              </h3>
              <p className="text-[#FDFBF7]/60 text-sm leading-relaxed mb-8" data-id="element-6285" data-mp="2xoujyzjyrvlz">
                {c.body}
              </p>
              <button onClick={c.action} className={`w-full py-3.5 text-[10px] tracking-[0.25em] uppercase font-bold flex items-center justify-center gap-2 transition-all ${c.highlight ? 'bg-[#D4AF37] text-[#0B0F19] hover:bg-[#E5C158] shadow-[0_0_30px_rgba(212,175,55,0.2)]' : 'border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10'}`} data-id="element-6286" data-mp="3chxree3ucsyz">
                {c.cta}
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" data-id="element-6287" data-mp="zdapv1uvhbzn" />
              </button>
            </motion.div>)}
        </div>

        {/* ---------- 2. How Misls Work ---------- */}
        <div className="mb-20" data-id="element-6288" data-mp="pg64334xvijd">
          <div className="text-center mb-10" data-id="element-6289" data-mp="phh8942aqsb0">
            <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-3 font-light" data-id="element-6290" data-mp="1d4pate8cbn9c">
              The Path
            </p>
            <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] leading-tight mb-3" data-id="element-6291" data-mp="1i3ac7gzbwczd">
              How Misls Work
            </h3>
            <p className="text-[#FDFBF7]/55 text-sm max-w-2xl mx-auto leading-relaxed" data-id="element-6292" data-mp="18e0mg7ryj6ef">
              Misls begin as forming houses. Once enough members join, they
              become active and can start appearing in rankings, earning status,
              and unlocking collective benefits.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-6293" data-mp="d79n5wggnzvd">
            {steps.map((s, i) => <div key={s.n} className="relative p-6 border border-[#D4AF37]/20 bg-[#131B2B]/60 hover:border-[#D4AF37]/40 transition-colors" data-id="element-6294" data-mp="85kcwj12bq8c" data-mpi={i}>
                <CornerOrnaments data-id="element-6295" data-mp="3vd66f37pu3ny" />
                <div className="flex items-center justify-between mb-5" data-id="element-6296" data-mp="3ve7jtec7f28u">
                  <span className="font-mono text-[#D4AF37] text-xs tracking-[0.2em]" data-id="element-6297" data-mp="vw800bjjumfh">
                    {s.n}
                  </span>
                  {i < steps.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-[#D4AF37]/40 hidden lg:block" data-id="element-6298" data-mp="m5lniq74qc10" />}
                </div>
                <h4 className="font-serif text-lg text-[#FDFBF7] mb-2 leading-tight" data-id="element-6299" data-mp="i38iy544ck5t">
                  {s.title}
                </h4>
                <p className="text-[#FDFBF7]/55 text-xs leading-relaxed" data-id="element-6300" data-mp="19vz7rujh36l1">
                  {s.body}
                </p>
              </div>)}
          </div>
        </div>

        {/* ---------- 3. Status Ladder ---------- */}
        <div className="mb-20" data-id="element-6301" data-mp="2xivh8294lg4n">
          <div className="text-center mb-10" data-id="element-6302" data-mp="2xk6j1wj0gdt4">
            <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-3 font-light" data-id="element-6303" data-mp="31ivtphcnzh92">
              Misl Status
            </p>
            <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] leading-tight" data-id="element-6304" data-mp="36gxgac9atypa">
              From Forming to Ranked
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-6305" data-mp="3gfwoxuu2duca">
            {statusLadder.map(s => <div key={s.name} className={`relative p-7 border ${s.border} bg-[#131B2B] hover:bg-[#131B2B]/80 transition-colors`} data-id="element-6306" data-mp="3l97p5d4dfcv8">
                <CornerOrnaments data-id="element-6307" data-mp="3u9nnk869btt8" />
                <div className="flex items-center gap-3 mb-5" data-id="element-6308" data-mp="3uafjjk3rntk1">
                  <span className={`w-2 h-2 rounded-full ${s.dot} shadow-[0_0_12px_currentColor]`} data-id="element-6309" data-mp="33csqtrubpac" />
                  <span className="text-[#FDFBF7]/40 text-[9px] tracking-[0.3em] uppercase" data-id="element-6310" data-mp="1z3d70mjibzhk">
                    {s.tag}
                  </span>
                </div>
                <h4 className={`font-serif text-2xl ${s.titleColor} mb-3 leading-tight`} data-id="element-6311" data-mp="28war2x45dwia">
                  {s.name}
                </h4>
                <p className="text-[#FDFBF7]/60 text-sm leading-relaxed" data-id="element-6312" data-mp="2hveda9ym2s10">
                  {s.body}
                </p>
              </div>)}
          </div>
        </div>

        {/* ---------- 4. Member Threshold Logic ---------- */}
        <div className="mb-20 border border-[#D4AF37]/20 bg-[#131B2B]/60 p-8 sm:p-10 relative" data-id="element-6313" data-mp="2rtc1wykduqu5">
          <CornerOrnaments data-id="element-6314" data-mp="2mvk1th9l3yev" />
          <div className="text-center mb-10" data-id="element-6315" data-mp="2mt7fkrwsn1w0">
            <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-3 font-light" data-id="element-6316" data-mp="xh5covunm9iu">
              Activation Thresholds
            </p>
            <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] leading-tight mb-3" data-id="element-6317" data-mp="344cqy7aibauk">
              When a Misl Awakens
            </h3>
            <p className="text-[#FDFBF7]/55 text-sm max-w-2xl mx-auto leading-relaxed" data-id="element-6318" data-mp="392ecqzzbho9q">
              A Misl is not active by default. It is awakened by its people.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-6319" data-mp="2zcl0rxv9j74y">
            {thresholds.map((t, i) => <div key={t.label} className="relative border border-[#D4AF37]/15 bg-[#0B0F19] p-6" data-id="element-6320" data-mp="2ld25i5njvqa2" data-mpi={i}>
                <div className="flex items-baseline gap-2 mb-3" data-id="element-6321" data-mp="2bfp2y3ydi7db">
                  <span className="font-serif text-5xl text-[#D4AF37] leading-none" data-id="element-6322" data-mp="2bhiadlgt8vmd">
                    {t.count}
                  </span>
                  <span className="text-[#FDFBF7]/50 text-[10px] tracking-[0.25em] uppercase" data-id="element-6323" data-mp="9okbp90cr8ti">
                    Members
                  </span>
                </div>
                <p className="font-serif text-lg text-[#FDFBF7] mb-2" data-id="element-6324" data-mp="elb1io1ylis4">
                  {t.label}
                </p>
                <p className="text-[#FDFBF7]/55 text-xs leading-relaxed mb-5" data-id="element-6325" data-mp="2tkd09sgw424v">
                  {t.body}
                </p>
                <div className="h-1 bg-[#D4AF37]/10 relative overflow-hidden" data-id="element-6326" data-mp="2osuzb4eva1jw">
                  <motion.div initial={{
                width: 0
              }} whileInView={{
                width: `${t.progress}%`
              }} viewport={{
                once: true
              }} transition={{
                duration: 1.2,
                delay: i * 0.2
              }} className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#D4AF37]/60 to-[#D4AF37]" data-id="element-6327" data-mp="2jutgmklcs48y" />
                </div>
              </div>)}
          </div>
        </div>

        {/* ---------- 5. Create A Misl Form ---------- */}
        <div id="create-misl-form" className="mb-20 border border-[#D4AF37]/30 bg-gradient-to-br from-[#131B2B] to-[#0B0F19] p-8 sm:p-12 relative scroll-mt-24" data-id="element-6328" data-mp="6odcq58t0p7f">
          <CornerOrnaments data-id="element-6329" data-mp="gdwxxhjzov5g" />
          <span className="absolute -top-px left-12 right-12 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" data-id="element-6330" data-mp="geoizyfynahh" />

          <div className="text-center mb-10" data-id="element-6331" data-mp="lbyik6x2d2j1">
            <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-3 font-light" data-id="element-6332" data-mp="3juvirtv98nkz">
              The Founding
            </p>
            <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] leading-tight mb-3" data-id="element-6333" data-mp="3otp0xzlhl2rh">
              Found Your Misl
            </h3>
            <p className="text-[#FDFBF7]/55 text-sm max-w-2xl mx-auto leading-relaxed" data-id="element-6334" data-mp="2lywlswis3ju">
              Shape the identity of your house. You can refine these details
              after your Misl is created.
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-6" data-id="element-6335" data-mp="1e02kchcikvgg">
            {/* Name */}
            <div data-id="element-6336" data-mp="22oxf0s9oo20u">
              <label className="block text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase mb-2" data-id="element-6337" data-mp="22qi9yizwtmz8">
                Misl Name
              </label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({
              ...f,
              name: e.target.value
            }))} placeholder="The Singh House, Lahore Circle, Anand Misl…" className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3.5 text-[#FDFBF7] text-sm placeholder:text-[#FDFBF7]/25 focus:outline-none focus:border-[#D4AF37]/60 transition-colors" data-id="element-6338" data-mp="1tx564m0b50yd" />
            </div>

            {/* Description */}
            <div data-id="element-6339" data-mp="35cbptwshelhr">
              <label className="block text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase mb-2" data-id="element-6340" data-mp="35bsj7c8a7jj1">
                Short Description
              </label>
              <textarea rows={3} value={form.description} onChange={e => setForm(f => ({
              ...f,
              description: e.target.value
            }))} placeholder="A few lines about your Misl, who it's for, and what you stand for." className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3.5 text-[#FDFBF7] text-sm placeholder:text-[#FDFBF7]/25 focus:outline-none focus:border-[#D4AF37]/60 transition-colors resize-none" data-id="element-6341" data-mp="39bb3c5sa3qkt" />
            </div>

            {/* City / Region */}
            <div data-id="element-6342" data-mp="1qdwiyzl56hqx">
              <label className="block text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase mb-2" data-id="element-6343" data-mp="1qefvu1vxqfy3">
                City or Region
              </label>
              <div className="relative" data-id="element-6344" data-mp="3s5sxmi2lw7px">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]/40" data-id="element-6345" data-mp="3s6uhnc1lo4js" />
                <input type="text" value={form.region} onChange={e => setForm(f => ({
                ...f,
                region: e.target.value
              }))} placeholder="Toronto, Amritsar, London, Punjab…" className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 pl-11 pr-4 py-3.5 text-[#FDFBF7] text-sm placeholder:text-[#FDFBF7]/25 focus:outline-none focus:border-[#D4AF37]/60 transition-colors" data-id="element-6346" data-mp="3n92hjuqsxc4i" />
              </div>
            </div>

            {/* Type / Inspiration */}
            <div data-id="element-6347" data-mp="2js8t1wert8ug">
              <label className="block text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase mb-3" data-id="element-6348" data-mp="2jttnzn4zytsu">
                Type of Misl
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-id="element-6349" data-mp="2stgom6pwjvku">
                {mislTypes.map(t => {
                const active = form.type === t.id;
                return <button key={t.id} type="button" onClick={() => setForm(f => ({
                  ...f,
                  type: t.id
                }))} className={`py-3 text-[10px] tracking-[0.2em] uppercase border transition-all ${active ? 'border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]' : 'border-[#D4AF37]/15 bg-[#0B0F19] text-[#FDFBF7]/50 hover:border-[#D4AF37]/40 hover:text-[#FDFBF7]/80'}`} data-id="element-6350" data-mp="2xq70siyl670z">
                      {t.label}
                    </button>;
              })}
              </div>
            </div>

            {/* Crest / Badge */}
            <div data-id="element-6351" data-mp="r3hv93ml1ce2">
              <label className="block text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase mb-3" data-id="element-6352" data-mp="r411vo6s8ecs">
                Crest{' '}
                <span className="text-[#FDFBF7]/30 normal-case tracking-normal" data-id="element-6353" data-mp="1jsyjrq730950">
                  (optional)
                </span>
              </label>
              <div className="grid grid-cols-5 gap-2" data-id="element-6354" data-mp="13bw48gf1cseb">
                {crestOptions.map(c => {
                const active = form.crest === c.id;
                return <button key={c.id} type="button" onClick={() => setForm(f => ({
                  ...f,
                  crest: c.id
                }))} className={`py-4 flex flex-col items-center gap-2 border transition-all ${active ? 'border-[#D4AF37] bg-[#D4AF37]/15' : 'border-[#D4AF37]/15 bg-[#0B0F19] hover:border-[#D4AF37]/40'}`} data-id="element-6355" data-mp="udst8e1ofe0r">
                      <c.icon className={`w-4 h-4 ${active ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/50'}`} data-id="element-6356" data-mp="1i8vl6uvs1xlg" />
                      <span className={`text-[9px] tracking-[0.2em] uppercase ${active ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/40'}`} data-id="element-6357" data-mp="2arj2dz7nksh9">
                        {c.label}
                      </span>
                    </button>;
              })}
              </div>
            </div>

            {/* Primary action */}
            <div className="pt-4" data-id="element-6358" data-mp="2owj9xgmxskzt">
              <button onClick={() => {
              setFormingMembers(1);
              const el = document.getElementById('misl-invite-block');
              if (el) el.scrollIntoView({
                behavior: 'smooth'
              });
            }} disabled={!form.name.trim()} className={`w-full py-4 text-[10px] tracking-[0.25em] uppercase font-bold flex items-center justify-center gap-2 transition-all ${form.name.trim() ? 'bg-[#D4AF37] text-[#0B0F19] hover:bg-[#E5C158] shadow-[0_0_30px_rgba(212,175,55,0.25)]' : 'bg-[#D4AF37]/20 text-[#0B0F19]/40 cursor-not-allowed'}`} data-id="element-6359" data-mp="2oxkm5ofovdj4">
                Found This Misl
                <ArrowRight className="w-3.5 h-3.5" data-id="element-6360" data-mp="3pld4uq4oog59" />
              </button>
              <p className="text-[#FDFBF7]/35 text-[10px] tracking-[0.2em] uppercase text-center mt-3" data-id="element-6361" data-mp="3tmgj5873244d">
                Your Misl begins as a forming house · Activate it by inviting
                your people
              </p>
            </div>
          </div>
        </div>

        {/* ---------- 6. WhatsApp Invite Block ---------- */}
        <div id="misl-invite-block" className="mb-20 relative border border-[#D4AF37]/30 bg-[#131B2B] p-8 sm:p-12 scroll-mt-24" data-id="element-6362" data-mp="35vu9xdlcedss">
          <CornerOrnaments data-id="element-6363" data-mp="3fv1vup657gri" />

          {/* Forming Misl status */}
          <div className="flex items-center justify-center gap-3 mb-8" data-id="element-6364" data-mp="3kmjngmma04zt">
            <span className="w-2 h-2 rounded-full bg-[#FDFBF7]/50 animate-pulse" data-id="element-6365" data-mp="3kowedpa0hpp0" />
            <span className="text-[#FDFBF7]/50 text-[9px] tracking-[0.3em] uppercase" data-id="element-6366" data-mp="2n75qkw2icgj4">
              Forming Misl · {formingMembers} of 5 members
            </span>
          </div>

          {/* Progress to active */}
          <div className="max-w-md mx-auto mb-10" data-id="element-6367" data-mp="2kkn8kmb9azus">
            <div className="h-1 bg-[#D4AF37]/10 relative overflow-hidden" data-id="element-6368" data-mp="2foy547w48yt7">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${formingMembers / 5 * 100}%`
            }} transition={{
              duration: 0.8
            }} className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#D4AF37]/60 to-[#D4AF37]" data-id="element-6369" data-mp="2aoagfsvd0u25" />
            </div>
            <p className="text-center text-[#FDFBF7]/40 text-[9px] tracking-[0.25em] uppercase mt-2" data-id="element-6370" data-mp="1rzdlrnw1vzje">
              {5 - formingMembers} more to activate
            </p>
          </div>

          <div className="text-center mb-8" data-id="element-6371" data-mp="1i0e9zwfznns6">
            <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-3 font-light" data-id="element-6372" data-mp="1hzcw7k7l8n6r">
              The Activation
            </p>
            <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] leading-tight mb-4" data-id="element-6373" data-mp="31upjrv91mduf">
              Invite Your People
            </h3>
            <p className="text-[#FDFBF7]/60 text-sm max-w-2xl mx-auto leading-relaxed" data-id="element-6374" data-mp="36tkb5ll2taq5">
              Every Misl grows through its people. Share your Misl through
              WhatsApp, bring your group into Lahore Treasury, and activate your
              house together.
            </p>
          </div>

          {/* Invite link display */}
          <div className="max-w-2xl mx-auto mb-6" data-id="element-6375" data-mp="2nv7qb2ksyceq">
            <div className="flex items-center gap-2 border border-[#D4AF37]/20 bg-[#0B0F19] px-4 py-3" data-id="element-6376" data-mp="2nx0zaoiw18pw">
              <span className="font-mono text-[#D4AF37]/80 text-xs truncate flex-1" data-id="element-6377" data-mp="298egudxys0ug">
                {inviteLink}
              </span>
              <button onClick={handleCopyLink} className="shrink-0 flex items-center gap-2 px-3 py-1.5 border border-[#D4AF37]/30 text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase hover:bg-[#D4AF37]/10 transition-colors" data-id="element-6378" data-mp="20658nuq64jk8">
                {copied ? <>
                    <Check className="w-3 h-3" data-id="element-6379" data-mp="1gkv6lmt6zam8" />
                    Copied
                  </> : <>
                    <Copy className="w-3 h-3" data-id="element-6380" data-mp="1uhry7qh4ww46" />
                    Copy
                  </>}
              </button>
            </div>
          </div>

          {/* CTAs */}
          <div className="max-w-2xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-6381" data-mp="24g7qvxvt8dfs">
            <button onClick={handleWhatsAppShare} className="flex items-center justify-center gap-3 px-6 py-4 bg-[#25D366] text-white hover:bg-[#128C7E] transition-all text-[10px] tracking-[0.25em] uppercase font-bold" data-id="element-6382" data-mp="1zi69dhe18m6f">
              <MessageCircle className="w-4 h-4" data-id="element-6383" data-mp="2n8a93do3o1s5" />
              Share on WhatsApp
            </button>
            <button onClick={handleCopyLink} className="flex items-center justify-center gap-3 px-6 py-4 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all text-[10px] tracking-[0.25em] uppercase font-bold" data-id="element-6384" data-mp="2i9z1l9la23d3">
              <Share2 className="w-4 h-4" data-id="element-6385" data-mp="34qcyga37crfz" />
              Copy Invite Link
            </button>
          </div>

          <p className="text-[#FDFBF7]/40 text-xs text-center mt-6 italic max-w-xl mx-auto leading-relaxed" data-id="element-6386" data-mp="2zrjlqjvqp4gv">
            Build your Misl through family, friends, classmates, and community
            groups. Five members is all it takes to step into the Court.
          </p>
        </div>

        {/* ---------- 7. Misl Benefits ---------- */}
        <div className="mb-16" data-id="element-6387" data-mp="2lsadq7hotgnz">
          <div className="text-center mb-10" data-id="element-6388" data-mp="2ltbtgp9nerc1">
            <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-3 font-light" data-id="element-6389" data-mp="2gzr3nki7l3nx">
              Why It Matters
            </p>
            <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] leading-tight mb-3" data-id="element-6390" data-mp="1t93jo0v0qnl0">
              The Power of an Active Misl
            </h3>
            <p className="text-[#FDFBF7]/55 text-sm max-w-2xl mx-auto leading-relaxed" data-id="element-6391" data-mp="3t1697leozby">
              Activating your Misl unlocks identity, recognition, and rewards
              that no individual member can earn alone.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-6392" data-mp="3pzg06vmf9sji">
            {benefits.map(b => <div key={b.title} className="relative p-6 border border-[#D4AF37]/15 bg-[#131B2B]/60 hover:border-[#D4AF37]/40 hover:bg-[#131B2B] transition-all group" data-id="element-6393" data-mp="mktrgfu8hg94">
                <CornerOrnaments data-id="element-6394" data-mp="dknecv2g6iah" />
                <div className="w-10 h-10 border border-[#D4AF37]/30 bg-[#0B0F19] flex items-center justify-center mb-4 group-hover:border-[#D4AF37]/60 transition-colors" data-id="element-6395" data-mp="djm42st99ztt">
                  <b.icon className="w-4 h-4 text-[#D4AF37]" data-id="element-6396" data-mp="2y91n31m8gk8w" />
                </div>
                <h4 className="font-serif text-lg text-[#FDFBF7] mb-2 leading-tight" data-id="element-6397" data-mp="32bydvflxfmnx">
                  {b.title}
                </h4>
                <p className="text-[#FDFBF7]/55 text-xs leading-relaxed" data-id="element-6398" data-mp="18sfz5cpav9vu">
                  {b.body}
                </p>
              </div>)}
          </div>
        </div>

        {/* ---------- 8. Leaderboard / Status Connection ---------- */}
        <div className="relative border border-[#D4AF37]/30 bg-gradient-to-br from-[#0B0F19] via-[#131B2B] to-[#0B0F19] p-8 sm:p-12 overflow-hidden" data-id="element-6399" data-mp="u4mj5qway5cq">
          <CornerOrnaments data-id="element-6400" data-mp="q3sugvb1cmis" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.08),_transparent_60%)] pointer-events-none" data-id="element-6401" data-mp="l3ekh7pvj2i4" />
          <div className="relative z-10 flex flex-col lg:flex-row items-center gap-8" data-id="element-6402" data-mp="g5392sjxkjxs">
            <div className="shrink-0 w-16 h-16 border border-[#D4AF37]/50 bg-[#0B0F19] flex items-center justify-center" data-id="element-6403" data-mp="b6a7nyhx4jdz">
              <Trophy className="w-7 h-7 text-[#D4AF37]" data-id="element-6404" data-mp="2aht5bgxru58c" />
            </div>
            <div className="flex-1 text-center lg:text-left" data-id="element-6405" data-mp="25kb46cpzwuaw">
              <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-3 font-light" data-id="element-6406" data-mp="2e08p8cd6sh2e">
                Status & Recognition
              </p>
              <h3 className="font-serif text-2xl sm:text-3xl text-[#FDFBF7] mb-3 leading-tight" data-id="element-6407" data-mp="2i0auevi4rsmf">
                Misls Rise in the Court
              </h3>
              <p className="text-[#FDFBF7]/65 text-sm leading-relaxed max-w-2xl" data-id="element-6408" data-mp="2rs6sw7o4pkr6">
                Misls are not just social groups. Once active, they rise through
                ranking, XP, and participation, giving members another path to
                status and recognition inside Lahore Treasury.
              </p>
            </div>
            {!embedded && <button onClick={onRegister} className="shrink-0 flex items-center gap-2 px-6 py-3.5 bg-[#D4AF37] text-[#0B0F19] hover:bg-[#E5C158] transition-all text-[10px] tracking-[0.25em] uppercase font-bold shadow-[0_0_30px_rgba(212,175,55,0.2)]" data-id="element-6409" data-mp="244fff0qur1ob">
                Enter the Court
                <ArrowRight className="w-3.5 h-3.5" data-id="element-6410" data-mp="x7dc1s80549o" />
              </button>}
          </div>
        </div>
      </div>
    </Wrapper>;
};
// ---------- Treasury Nights Section ----------
const TreasuryNightsSection = () => {
  const rewards = [{
    n: '01',
    title: 'Scan & Unlock',
    body: 'Hidden codes reveal Live Night Edition artifacts exclusive to the venue.',
    icon: Sparkles
  }, {
    n: '02',
    title: 'Sensory Rituals',
    body: 'Scent blending, taste pairings, turban ceremonies keyed to your rank.',
    icon: Crown
  }, {
    n: '03',
    title: 'Commemorative Tokens',
    body: 'Hand-stamped physical coins for completed sets. Numbered. Engraved.',
    icon: Award
  }, {
    n: '04',
    title: 'Step Inside the Painting',
    body: 'Walk through the chambers your artifacts depict. The Darbar — full size.',
    icon: BookOpen
  }];
  return <section className="px-4 py-24 bg-[#131B2B] relative border-t border-[#D4AF37]/10" data-id="element-6411" data-mp="1tzz0c9l75olk">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(139,0,0,0.12),_transparent_60%)]" data-id="element-6412" data-mp="2dkh3awpdahoj" />
      <div className="max-w-6xl mx-auto relative z-10" data-id="element-6413" data-mp="2igy2cly3ugge">
        <div className="text-center max-w-3xl mx-auto" data-id="element-6414" data-mp="23kq9yycjm8q1">
          <SectionHeader eyebrow="The Real World · In Person" title={<>
                TREASURY <span className="text-[#D4AF37]" data-id="element-6416" data-mp="1uswocxi5q7tl">NIGHTS</span>
              </>} data-id="element-6415" data-mp="28kuquphd55gs" />
          <p className="text-[#FDFBF7]/80 text-base font-light leading-relaxed" data-id="element-6417" data-mp="1zkoe55tho9io">
            Monthly preview evenings at{' '}
            <span className="text-[#D4AF37]" data-id="element-6418" data-mp="1kwl7rvbjavtc">Illuminarium Toronto</span> — where
            your digital Treasury comes alive in the physical world.
          </p>
        </div>
        <div className="mt-16" data-id="element-6419" data-mp="1bubzlc3qnej4">
          <p className="text-center text-[#D4AF37]/60 text-[10px] tracking-[0.35em] uppercase mb-8" data-id="element-6420" data-mp="1bv3x4sgwbmc1">
            What You Can Win — In Person Only
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-6421" data-mp="3ijcjq0mdqw1s">
            {rewards.map(r => {
            const Icon = r.icon;
            return <div key={r.n} className="p-6 border border-[#D4AF37]/20 bg-[#0B0F19]" data-id="element-6422" data-mp="38t18ru4a3mle">
                  <p className="text-[#D4AF37]/40 font-mono text-[10px] mb-3" data-id="element-6423" data-mp="33v9g2xvt7wg5">
                    {r.n}
                  </p>
                  <Icon className="w-7 h-7 text-[#D4AF37] mb-4" data-id="element-6424" data-mp="5bj949yntlw2" />
                  <p className="font-serif text-lg text-[#FDFBF7] mb-2" data-id="element-6425" data-mp="e9g6kc2as3h">
                    {r.title}
                  </p>
                  <p className="text-xs text-[#FDFBF7]/55 font-light leading-relaxed" data-id="element-6426" data-mp="3rhpom1nwn1qb">
                    {r.body}
                  </p>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
};
// ---------- Rewards Section ----------
const RewardsSection = () => <section className="px-4 py-24 bg-[#0B0F19] relative border-t border-[#D4AF37]/10" data-id="element-6427" data-mp="2c4v97lm7rkdz">
    <div className="max-w-4xl mx-auto text-center" data-id="element-6428" data-mp="rmknpmd8xi94">
      <SectionHeader eyebrow="The Returns" title={<>
            WHAT YOU <span className="text-[#D4AF37]" data-id="element-6430" data-mp="10kwb7fitfzxc">EARN</span>
          </>} description="Collecting is the path. These are the privileges that come with it." data-id="element-6429" data-mp="vltf4kktd41i" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12" data-id="element-6431" data-mp="15dz5xqzbxff4">
        {[{
        icon: Trophy,
        label: 'Prestige',
        body: 'Verified rank, public profile, leaderboard standing.'
      }, {
        icon: Gift,
        label: 'Physical Rewards',
        body: 'Hand-stamped coins, printed cards, physical regalia.'
      }, {
        icon: Calendar,
        label: 'Event Access',
        body: 'Priority entry to Treasury Nights and private sessions.'
      }].map(it => <div key={it.label} className="p-6 border border-[#D4AF37]/20 bg-[#131B2B]" data-id="element-6432" data-mp="3q4ga4x31e2pn">
            <it.icon className="w-7 h-7 text-[#D4AF37] mb-4 mx-auto" data-id="element-6433" data-mp="1rt69iwy788dr" />
            <p className="font-serif text-lg text-[#FDFBF7] mb-2" data-id="element-6434" data-mp="1rqttipc0483c">{it.label}</p>
            <p className="text-xs text-[#FDFBF7]/60 font-light leading-relaxed" data-id="element-6435" data-mp="25jtakvrfmad0">
              {it.body}
            </p>
          </div>)}
      </div>
    </div>
  </section>;
// ---------- Final CTA ----------
const FinalCTASection = ({
  onJoin
}: {
  onJoin: () => void;
}) => <section className="px-4 py-32 bg-[#131B2B] relative border-t border-[#D4AF37]/20 overflow-hidden" data-id="element-6436" data-mp="18v3qqzw8v7oe">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.15),_transparent_60%)]" data-id="element-6437" data-mp="1mummsubs6skc" />
    <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-6438" data-mp="1htfe8t1tdpcm">
      <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-6" data-id="element-6439" data-mp="2y875v3r4yskv">
        The Treasury Awaits
      </p>
      <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#FDFBF7] mb-6 leading-tight" data-id="element-6440" data-mp="2t9dt5djob5lr">
        ENTER THE <span className="text-[#D4AF37]" data-id="element-6441" data-mp="f0tf7hcxirb3">LAHORE COURT</span>
      </h2>
      <OrnateDivider className="mb-8" data-id="element-6442" data-mp="k2abezkgxplc" />
      <p className="text-[#FDFBF7]/70 text-base font-light leading-relaxed mb-10 max-w-xl mx-auto" data-id="element-6443" data-mp="jz4rdyqp5dwh">
        Become a founding member. Open your first packet. Take your place among
        the Sangat recovering our shared heritage.
      </p>
      <button type="button" onClick={onJoin} className="px-10 py-4 bg-[#D4AF37] text-[#0B0F19] font-medium text-sm tracking-[0.25em] uppercase hover:bg-[#E5C158] transition-all shadow-[0_0_30px_rgba(212,175,55,0.3)] inline-flex items-center gap-3 group" data-id="element-6444" data-mp="ypvg2xwzrwvd">
        Claim Your First Packet
        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-6445" data-mp="ty3zng7jv7iy" />
      </button>
    </div>
  </section>;
// ---------- Page Footer ----------
const PageFooter = () => <footer className="px-4 py-16 bg-[#0B0F19] border-t border-[#D4AF37]/20 text-center" data-id="element-6446" data-mp="1mgrg2iblpydp">
    <div className="max-w-4xl mx-auto" data-id="element-6447" data-mp="2ogicmx7lfvav">
      <OrnateDivider className="mb-10" data-id="element-6448" data-mp="2ofgp3t9e5gc9" />

      <div className="flex flex-wrap justify-center gap-x-8 gap-y-4 mb-12" data-id="element-6449" data-mp="2tejwvlgra0nx">
        <Link to="/treasure" className="text-[#FDFBF7]/60 hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase transition-colors" data-id="element-6450" data-mp="2tcgue1yf2f2b">
          Treasury Home
        </Link>
        <Link to="/treasure" className="text-[#FDFBF7]/60 hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase transition-colors" data-id="element-6451" data-mp="328y739mfil06">
          Collections
        </Link>
        <Link to="/treasure" className="text-[#FDFBF7]/60 hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase transition-colors" data-id="element-6452" data-mp="2ahsdlc17rljv">
          Court Packet
        </Link>
        <Link to="/support-the-court" className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase transition-colors" data-id="element-6453" data-mp="2je9jo0uq0t8r">
          Support the Court
        </Link>
      </div>

      <p className="text-[#D4AF37] font-serif text-lg tracking-[0.3em] uppercase mb-2" data-id="element-6454" data-mp="8ubvv239uyl0">
        Treasures of the Sikh Empire
      </p>
      <p className="text-[#FDFBF7]/40 text-[10px] tracking-[0.25em] uppercase font-light" data-id="element-6455" data-mp="3uz1lwa2mp8e">
        An archive of the Lahore Darbar ·{' '}
        <Link to="/treasure/blueprint" aria-label="Sealed Chamber" title="Sealed Chamber" className="hover:text-[#D4AF37] transition-colors duration-500" data-id="element-6456" data-mp="3v83bjf3crzwz">
          1799 — 1839
        </Link>
      </p>

      {/* Claude Design — handoff note (packet-opening animation + design tokens) */}
      <details className="mt-12 text-left border border-[#D4AF37]/20 bg-[#131B2B]/40 rounded-sm" data-id="element-6457" data-mp="3casdtyxddejn">
        <summary className="cursor-pointer list-none select-none px-5 py-4 flex items-center justify-between text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase" data-id="element-6458" data-mp="389qeli3gd2ho">
          <span data-id="element-6459" data-mp="1ahl0uw58b90x">Claude Design</span>
          <span className="text-[#FDFBF7]/40 tracking-normal normal-case text-[11px]" data-id="element-6460" data-mp="16hkd2kw8plh7">
            Handoff note ▾
          </span>
        </summary>
        <div className="px-5 pb-6 pt-1 border-t border-[#D4AF37]/10 text-[#FDFBF7]/70 text-[11px] leading-relaxed font-light space-y-5" data-id="element-6461" data-mp="2zzhy10edmjch">
          <p className="text-[#FDFBF7]/50" data-id="element-6462" data-mp="2r04jvufb56lf">
            Reference bundle for Claude Design onboarding. Download the files
            below to attach them directly.
          </p>

          {/* Download buttons */}
          <div className="flex flex-wrap gap-3" data-id="element-6463" data-mp="3iqso5fca2s3b">
            <button type="button" onClick={() => downloadText('claude-design.md', CLAUDE_DESIGN_MD, 'text/markdown;charset=utf-8')} className="inline-flex items-center gap-2 border border-[#D4AF37]/40 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 text-[#D4AF37] px-4 py-2.5 text-[10px] tracking-[0.2em] uppercase font-medium transition-colors" data-id="element-6464" data-mp="3is3n91us3bo3">
              <ArrowDown className="w-3.5 h-3.5" data-id="element-6465" data-mp="238h1y200x5va" />
              claude-design.md
            </button>
            <button type="button" onClick={() => downloadText('PacketOpeningAnimation.tsx', PACKET_OPENING_TSX, 'text/plain;charset=utf-8')} className="inline-flex items-center gap-2 border border-[#D4AF37]/40 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 text-[#D4AF37] px-4 py-2.5 text-[10px] tracking-[0.2em] uppercase font-medium transition-colors" data-id="element-6466" data-mp="1uvmdtko2477v">
              <ArrowDown className="w-3.5 h-3.5" data-id="element-6467" data-mp="1724bqg5bsiep" />
              Packet Animation (.tsx)
            </button>
          </div>
          <p className="text-[#FDFBF7]/35 text-[10px]" data-id="element-6468" data-mp="1h1mrsli47k1h">
            The packet download is the canonical DailyPacketSection, extracted
            standalone. It is the shared implementation behind all three packet
            surfaces listed below.
          </p>

          <div data-id="element-6469" data-mp="obd0ta5owjy7">
            <p className="text-[#D4AF37]/80 text-[10px] tracking-[0.25em] uppercase mb-2" data-id="element-6470" data-mp="3nrgnnxbadxfh">
              Packet-Opening Animation — files
            </p>
            <ul className="space-y-1 text-[#FDFBF7]/60" data-id="element-6471" data-mp="3sr2xdwrwovjj">
              <li data-id="element-6472" data-mp="m8tvza9dkdr">
                <span className="text-[#FDFBF7]" data-id="element-6473" data-mp="lpm3bebocdg">
                  pages/LahoreTreasuryPage.tsx
                </span>{' '}
                — DailyPacketSection (canonical) + dashboard "Daily Packet" tab
              </li>
              <li data-id="element-6474" data-mp="ac22crf3tjds">
                <span className="text-[#FDFBF7]" data-id="element-6475" data-mp="aeob2udqe4xl">
                  components/TreasuryOnboarding.tsx
                </span>{' '}
                — "Claim Your First Packet" step
              </li>
              <li data-id="element-6476" data-mp="k8n9biaop4k9">
                <span className="text-[#FDFBF7]" data-id="element-6477" data-mp="occudxs5h40j">
                  pages/SponsorDeckPage.tsx
                </span>{' '}
                — sponsor-deck demo slide
              </li>
              <li data-id="element-6478" data-mp="2m47ctmmnesrn">Assets: envelope.png + revealed card PNG (CDN-hosted)</li>
            </ul>
          </div>

          <div data-id="element-6479" data-mp="2q5j5k10tju42">
            <p className="text-[#D4AF37]/80 text-[10px] tracking-[0.25em] uppercase mb-2" data-id="element-6480" data-mp="2ijx9jhbm0wvm">
              Process
            </p>
            <p className="text-[#FDFBF7]/60" data-id="element-6481" data-mp="2dsffzebx1yko">
              State machine (React useState + framer-motion AnimatePresence):{' '}
              <span className="text-[#D4AF37]" data-id="element-6482" data-mp="28uneptpdt03t">
                sealed → opening → revealed → added
              </span>
              . Sealed floats the envelope; "Break the Seal" starts opening
              (gold burst, shake &amp; shrink, sparkle particles, ~1.9s);
              revealed flips the card up with the "Treasury Rare" badge and a
              register CTA; added flies the card into the archive and hands off
              to registration (~1.8s).
            </p>
          </div>

          <div data-id="element-6483" data-mp="2nj0fr53bt956">
            <p className="text-[#D4AF37]/80 text-[10px] tracking-[0.25em] uppercase mb-2" data-id="element-6484" data-mp="2nlmimrf9v0h2">
              Design tokens
            </p>
            <ul className="space-y-1 text-[#FDFBF7]/60" data-id="element-6485" data-mp="1c3v95q02qqga">
              <li data-id="element-6486" data-mp="1c6hgboj1z3xz">Fonts: Playfair Display (serif) + Inter (sans)</li>
              <li data-id="element-6487" data-mp="1dltl7e8zjy3w">
                Palette: gold <span className="text-[#D4AF37]" data-id="element-6488" data-mp="1djh75c6cm7w4">#D4AF37</span> ·
                indigo #0B0F19 / #131B2B · ivory #FDFBF7 · lacquer red #8B0000
              </li>
              <li data-id="element-6489" data-mp="14ju64rhj743l">
                Stack: React + TS, Tailwind v3, framer-motion, lucide-react
              </li>
              <li data-id="element-6490" data-mp="18ke0jnapztm1">
                Best reference files: components/, pages/LahoreTreasuryPage.tsx,
                tailwind.config.js, index.css
              </li>
            </ul>
          </div>

          <p className="text-[#FDFBF7]/40 text-[10px]" data-id="element-6491" data-mp="zmtwy7pdxl89">
            Life in the Sikh Kingdoms — a premium, cinematic immersive
            experience and digital collectible platform built around Maharaja
            Ranjit Singh's Lahore Court. Skip the .fig upload; the code is the
            source of truth.
          </p>
        </div>
      </details>
    </div>
  </footer>;
// ---------- Top Bar ----------
const TopBar = () => <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0B0F19]/90 border-b border-[#D4AF37]/20" data-id="element-6492" data-mp="3tem06y2e8q1j">
    <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between" data-id="element-6493" data-mp="3ofivjeqbslu3">
      <Link to="/" className="text-[#D4AF37]/70 hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase flex items-center gap-1 transition-colors" data-id="element-6494" data-mp="3ohvkwcyexyh6">
        <ChevronLeft className="w-4 h-4" data-id="element-6495" data-mp="26rdswwveo1k" /> Kingdoms
      </Link>
      <div className="font-serif text-xs sm:text-sm text-[#D4AF37] tracking-[0.25em] uppercase text-center" data-id="element-6496" data-mp="g7bvfpr1udth">
        Treasures of the Sikh Empire
      </div>
      <div className="w-16" data-id="element-6497" data-mp="b769mq7gnljb" />
    </div>
  </header>;
export const LahoreTreasuryPage = () => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  const [invite, setInvite] = useState<{
    from?: string;
    misl?: string;
    code?: string;
  }>({});
  // Detect ?ref=...&from=...&misl=... on mount — opens onboarding pre-filled
  // when the user arrives via someone else's invite link.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('ref') || undefined;
    const from = params.get('from') || undefined;
    const misl = params.get('misl') || undefined;
    if (code || from || misl) {
      setInvite({
        from: from || 'a friend',
        misl,
        code
      });
      setIsOnboardingOpen(true);
    }
  }, []);
  // Handle onboarding completion
  const handleOnboardingComplete = () => {
    setIsOnboardingOpen(false);
    const dashboard = document.getElementById('member-dashboard');
    if (dashboard) {
      dashboard.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen w-full bg-[#0B0F19] text-[#FDFBF7] font-sans selection:bg-[#D4AF37] selection:text-[#0B0F19]" data-id="element-6498" data-mp="1we5vnw6iblvg">
      <style data-id="element-6499" data-mp="13wk1zvsu35y9">{`
        html { scroll-behavior: smooth; }
        .perspective-1000 { perspective: 1000px; }
        .preserve-3d { transform-style: preserve-3d; }
        .backface-hidden { backface-visibility: hidden; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* Only mount Onboarding once the user actually opens it — keeps its bundle
                                                        off the initial render path entirely. */}
      {isOnboardingOpen && <Suspense fallback={null} data-id="element-6500" data-mp="dfzx8t1tgxz1">
          <TreasuryOnboarding isOpen={isOnboardingOpen} onClose={() => setIsOnboardingOpen(false)} onComplete={handleOnboardingComplete} invitedBy={invite.from} invitedMisl={invite.misl} inviteCode={invite.code} data-id="element-6501" data-mp="8n6o7szbf3h5" />
        </Suspense>}

      <TopBar data-id="element-6502" data-mp="3qrrwaeusox8l" />
      <HeroSection onJoin={() => setIsOnboardingOpen(true)} data-id="element-6503" data-mp="3qs9x7osejzmu" />

      {/* ---------- 3. Mission Section ---------- */}
      <MissionSection data-id="element-6504" data-mp="3lwcifmwsf1c6" />

      {/* ---------- 4. Daily Court Packet Section ---------- */}
      <DailyPacketSection onRegister={() => setIsOnboardingOpen(true)} data-id="element-6505" data-mp="1innuov02avtm" />

      {/* ---------- 4.5 Member Dashboard Preview Section ---------- */}
      <MemberDashboardPreviewSection data-id="element-6506" data-mp="19sg73n0xcnq9" />

      {/* ---------- 5. Collections Section ---------- */}
      <CollectionsSection data-id="element-6507" data-mp="14uxunmnp7qdi" />

      {/* ---------- 6. Rarity Section ---------- */}
      <RaritySection data-id="element-6508" data-mp="21k5gyww563ih" />

      {/* ---------- 7. Trading Section ---------- */}
      <TradingSection data-id="element-6509" data-mp="21i2tajigomgx" />

      {/* ---------- 8. Rank Section ---------- */}
      <RankSection data-id="element-6510" data-mp="1xjlj1r3quvva" />

      {/* ---------- 9. Misl Section ---------- */}
      <MislSection onRegister={() => setIsOnboardingOpen(true)} data-id="element-6511" data-mp="1sifqpudzxama" />

      {/* ---------- 10. Treasury Nights Section ---------- */}
      <TreasuryNightsSection data-id="element-6512" data-mp="1nmqofjaldfma" />

      {/* ---------- 11. Rewards Section ---------- */}
      <RewardsSection data-id="element-6513" data-mp="h5vfqmkg8bmp" />

      {/* ---------- 12. Final CTA Section ---------- */}
      <FinalCTASection onJoin={() => setIsOnboardingOpen(true)} data-id="element-6514" data-mp="c4pm8miyskc4" />

      {/* ---------- 13. Page Footer ---------- */}
      <PageFooter data-id="element-6515" data-mp="23dgf75mst6w" />
    </div>;
};